// Download ID card PDF for a member from the members list
export async function handleMemberIdCardDownload(member) {
  // Import normalizeMinioUrl dynamically since this is a top-level export
  const { normalizeMinioUrl } = await import('./utils/imageUtils.js');

  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.left = '-9999px';
  container.style.top = '0';
  document.body.appendChild(container);

  const normalizedPhotoUrl = normalizeMinioUrl(member.photoUrl) || '';

  container.innerHTML = `
    <div id="idcard-front" style="width: 609px; height: 383px;">
      <img src="${idCardFront}" style="width:100%;height:100%;position:absolute;z-index:0;"/>
      <div style="position:absolute;z-index:1;top:60px;left:40px;color:#222;font-size:32px;font-weight:bold;">${member.name}</div>
      <div style="position:absolute;z-index:1;top:120px;left:40px;color:#222;font-size:20px;">Voter ID: ${member.voterId || ''}</div>
      <div style="position:absolute;z-index:1;top:160px;left:40px;color:#222;font-size:20px;">District: ${member.district || ''}</div>
      <div style="position:absolute;z-index:1;top:200px;left:40px;color:#222;font-size:20px;">Phone: ${member.phone || ''}</div>
      <img src="${normalizedPhotoUrl}" style="position:absolute;top:60px;right:40px;width:100px;height:120px;border-radius:12px;border:2px solid #fff;object-fit:cover;" onerror="this.style.display='none'"/>
    </div>
    <div id="idcard-back" style="width: 609px; height: 383px;margin-top:20px;">
      <img src="${idCardBack}" style="width:100%;height:100%;position:absolute;z-index:0;"/>
      <div style="position:absolute;z-index:1;top:60px;left:40px;color:#222;font-size:20px;">Member ID: ${member.id}</div>
      <div style="position:absolute;z-index:1;top:100px;left:40px;color:#222;font-size:20px;">${member.address || ''}</div>
    </div>
  `;

  try {
    const frontCanvas = await html2canvas(container.querySelector('#idcard-front'), { scale: 2, useCORS: true });
    const backCanvas = await html2canvas(container.querySelector('#idcard-back'), { scale: 2, useCORS: true });
    const frontUrl = frontCanvas.toDataURL('image/png');
    const backUrl = backCanvas.toDataURL('image/png');

    const pdfDoc = await PDFDocument.create();
    const page1 = pdfDoc.addPage([152.25, 95.8]);
    const frontImage = await pdfDoc.embedPng(frontUrl);
    page1.drawImage(frontImage, { x: 0, y: 0, width: 152.25, height: 95.8 });
    const page2 = pdfDoc.addPage([152.25, 95.8]);
    const backImage = await pdfDoc.embedPng(backUrl);
    page2.drawImage(backImage, { x: 0, y: 0, width: 152.25, height: 95.8 });
    const pdfBytes = await pdfDoc.save();
    const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
    const pdfLink = document.createElement('a');
    pdfLink.href = URL.createObjectURL(pdfBlob);
    pdfLink.download = `${member.id}-id-card.pdf`;
    document.body.appendChild(pdfLink);
    pdfLink.click();
    document.body.removeChild(pdfLink);
    URL.revokeObjectURL(pdfLink.href);
  } catch (e) {
    alert('ID card PDF generation failed: ' + (e.message || 'Unknown error'));
  } finally {
    document.body.removeChild(container);
  }
}
import React, { useEffect, useState, Suspense } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown, ChevronUp, Send, UploadCloud,
  Users, UserPlus, IdCard as IdCardIcon, BadgeCheck, Megaphone, CalendarDays, Landmark, Scale, BookOpen, Leaf, Zap, Shield, Recycle, Gavel, HeartPulse, Globe,
  Settings as SettingsIcon, CheckCircle2, XCircle, ChevronRight, Phone, Mail, MapPin, HelpCircle, LogOut, Users as UsersIcon, FileText, FilePlus, Share2,
  Twitter, Facebook, MessageCircle, Clock, Instagram, Sun, Moon, BellOff, Languages, LifeBuoy, Key,
  Home, ClipboardList, MessageSquareText, User as UserIcon, Search, Download, RotateCw, X, PlayCircle, Bell, Check as CheckIcon, Activity,
  ShieldCheck, Edit3, Loader2, AlertCircle, Eye, EyeOff, ServerCrash, Heart, IndianRupee
} from "lucide-react";
import { useTranslation } from 'react-i18next';
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate, Navigate } from "react-router-dom";
import partyLogo from './assests/logo.png';
import leaderPhoto from './assests/148.png';
import signaturePhoto from './assests/signature.png';
import thalaivarPhoto from './assests/Thalaivar.png';
import kolgaiThalaivarPhoto from './assests/Thamizharasan.png'; // For 'kolgai' card left
import kolgaiThalaivarCenterPhoto from './assests/Kolgai_Thalaivar.png'; // For center image
import sidebarBg from './assests/Imge_Sidebar.jpg';
import tvktext from './assests/tvktext.png';
import thiruvalluvarImage from './assests/tvkthiru1.png';
import kuralBg from './assests/AdobeStock_397734461(1).png';
import wavylines from './assests/wavylines.png';
import loginGif from './assests/ori1.gif';
import loginBg from './assests/IMGBGLOGIN_copy.jpg';
import idCardFront from './assests/IDCARDFRONT1.jpg';
import idCardBack from './assests/IDCARDBACK.jpg';
import { PDFDocument, PDFPage } from 'pdf-lib';
import { toPng, toBlob } from 'html-to-image';
import html2canvas from 'html2canvas';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import * as XLSX from 'xlsx';
import { apiFetch, initializeApi } from './api.js';
import { normalizeMinioUrl } from './utils/imageUtils.js';
import thirukkuralData from './assests/thirukkural.json';
import './custom-scrollbar.css';
import tamilNaduData from './assests/states-districts-ta.json';
import allStatesData from './assests/states-and-districts.json';
import { QueryClient, QueryClientProvider, useQuery, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { ThemeProvider, useTheme } from './contexts/ThemeContext.jsx';
import ProfileDropdown from './components/ProfileDropdown.jsx';
import './i18n.js';
import Card from './Card.jsx';
import ConfirmationModal from './ConfirmationModal.jsx';
import ThemeToggle from './ThemeToggle.jsx';
import Banner from './components/Banner.jsx';
// Firebase OTP removed - using backend SMS API instead

// Environment variables
const WEB_URL = import.meta.env.VITE_WEB_URL || 'http://localhost:5173';

/** RBAC */
const RBAC = {
  admin: { tabs: ["home", "members", "new_user", "approvals", "messages", "idcard", "events", "admin", "profile", "help"], canApprove: true, canAccessAdmin: true },
  state: { tabs: ["home", "members", "new_user", "approvals", "messages", "idcard", "events", "profile", "help"], canApprove: true, canAccessAdmin: false },
  district: { tabs: ["home", "members", "new_user", "approvals", "messages", "idcard", "events", "profile", "help"], canApprove: true, canAccessAdmin: false },
  member: { tabs: ["home", "new_user", "messages", "idcard", "events", "profile", "help"], canApprove: false, canAccessAdmin: false },
};

/** Utils */
function parseJwtExp(token) {
  try { const [, payload] = token.split('.'); const { exp } = JSON.parse(atob(payload)); return exp ? exp * 1000 : null; } catch { return null; }
}

import { AuthProvider, useAuth } from './AuthContext.jsx';
import { ToastProvider, useToasts } from './ToastContext.jsx';

/** Utils */
function sleep(ms) { return new Promise(r => setTimeout(r, ms)); }

// Common spelling aliases for OCR variations
const districtAliases = {
  'kanniyakumari': 'Kanyakumari',
  'kanyakumari': 'Kanyakumari',
  'kanya kumari': 'Kanyakumari',
  'thirunelveli': 'Tirunelveli',
  'thiruvannamalai': 'Tiruvannamalai',
  'thiruvallur': 'Tiruvallur',
  'thiruvarur': 'Tiruvarur',
  'thiruchirapalli': 'Tiruchirappalli',
  'trichy': 'Tiruchirappalli',
  'thoothukudi': 'Thoothukudi (Tuticorin)',
  'tuticorin': 'Thoothukudi (Tuticorin)',
  'madurai': 'Madurai',
  'chennai': 'Chennai',
  'coimbatore': 'Coimbatore',
  'salem': 'Salem',
};

const stateAliases = {
  'tamil nadu': 'Tamil Nadu',
  'tamilnadu': 'Tamil Nadu',
  'tn': 'Tamil Nadu',
};

// Helper function to normalize text for comparison
function normalizeText(text) {
  if (!text) return '';
  return text.toLowerCase()
    .replace(/[^a-z0-9\s]/g, '') // Remove special characters
    .replace(/\s+/g, ' ')
    .trim();
}

// Helper function to find matching state name (case-insensitive with aliases)
function findMatchingState(extractedState) {
  if (!extractedState) return null;
  const normalizedInput = normalizeText(extractedState);

  // Check aliases first
  if (stateAliases[normalizedInput]) {
    return stateAliases[normalizedInput];
  }

  // Exact match (case-insensitive)
  const found = allStatesData.states.find(s =>
    normalizeText(s.state) === normalizedInput
  );
  return found ? found.state : null;
}

// Helper function to find matching district name (case-insensitive with aliases and fuzzy matching)
function findMatchingDistrict(extractedDistrict, stateName) {
  if (!extractedDistrict) return null;
  const normalizedInput = normalizeText(extractedDistrict);

  // Check aliases first
  if (districtAliases[normalizedInput]) {
    return districtAliases[normalizedInput];
  }

  // Get districts to search
  let districtsToSearch = [];
  if (stateName) {
    const stateData = allStatesData.states.find(s => s.state === stateName);
    if (stateData) {
      districtsToSearch = stateData.districts;
    }
  }
  if (districtsToSearch.length === 0) {
    // Search all states
    districtsToSearch = allStatesData.states.flatMap(s => s.districts);
  }

  // Exact match
  let found = districtsToSearch.find(d => normalizeText(d) === normalizedInput);
  if (found) return found;

  // Fuzzy match - check if input contains or is contained by district name
  found = districtsToSearch.find(d => {
    const normalizedDistrict = normalizeText(d);
    return normalizedDistrict.includes(normalizedInput) || normalizedInput.includes(normalizedDistrict);
  });
  if (found) return found;

  // Try removing common suffixes/prefixes and match again
  const cleanedInput = normalizedInput.replace(/^(the|district|dt)\s*/i, '').replace(/\s*(district|dt)$/i, '');
  found = districtsToSearch.find(d => normalizeText(d) === cleanedInput);

  return found || null;
}



/** UI Atoms */
const Section = ({ title, action, children }) => (
  <section className="mt-6">
    <div className="flex items-center justify-between mb-3 px-1 text-gray-800 dark:text-gray-200">
      <h3 className="text-base font-semibold">{title}</h3>
      {action && (
        <div className="text-sm text-gray-600 flex items-center gap-1">
          {action}
        </div>
      )}
    </div>
    {children}
  </section>
);
const NotificationCard = ({ title, time, onClick }) => (
  <Card onClick={onClick} className="p-3 flex items-center justify-between cursor-pointer active:bg-gray-50 dark:active:bg-gray-700 transition-colors">
    <div>
      <div className="text-sm font-medium text-gray-800 dark:text-gray-100">{title}</div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{time}</div>
    </div>
    <ChevronRight className="w-5 h-5 text-gray-400 dark:text-gray-500" />
  </Card>
);
const StatCard = ({ icon: Icon, label, value, sub }) => {
  const isReferralCard = label === 'என் பரிந்துரைகள்';

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="relative dark:text-white bg-white dark:bg-gray-800 rounded-2xl shadow-sm p-4 flex items-center justify-between border border-gray-100 dark:border-gray-700">
      <div className="flex items-center gap-3">
        <div className="p-2.5 rounded-xl bg-gray-100 dark:bg-gray-700">
          <Icon className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        </div>
        <div>
          <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
          <div className="text-xl font-semibold text-gray-900 dark:text-gray-50">{value}</div>
        </div>
      </div>
      {isReferralCard && (
        <div className="absolute -top-2 -right-2 flex items-center gap-1 bg-gradient-to-br from-yellow-400 to-amber-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-md transform rotate-6">
          <BadgeCheck className="w-3.5 h-3.5" />
          <span>சிறப்பு</span>
        </div>
      )}
      {sub && !isReferralCard ? <div className="text-xs text-gray-500 dark:text-gray-400">{sub}</div> : null}
    </motion.div>
  );
};

const ContactCard = ({ title, name, phone, email, area, photo }) => {
  return (
    <Card className="p-4 flex flex-col items-center gap-4 text-center h-full transition-all duration-300 hover:shadow-lg hover:border-green-200 dark:hover:border-green-700 hover:-translate-y-1">
      <div className="relative">
        <div className="absolute -inset-1 bg-gradient-to-r from-green-400 to-blue-500 rounded-full blur opacity-25 group-hover:opacity-50 transition-opacity duration-300"></div>
        <img
          src={photo || `https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(name)}`}
          alt={name}
          className="relative w-24 h-24 rounded-full object-cover border-4 border-white dark:border-gray-800 shadow-md"
        />
      </div>
      <div className="flex-1">
        <div className="text-sm font-semibold text-green-700 dark:text-green-400 uppercase tracking-wider">{title}</div>
        <div className="text-lg font-bold text-gray-800 dark:text-gray-100 mt-1">{name}</div>
        <div className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{area}</div>
        <div className="mt-3 text-xs space-y-2">
          <a href={`tel:${phone}`} className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400 transition-colors"><Phone className="w-3.5 h-3.5" /> {phone}</a>
          <a href={`mailto:${email}`} className="flex items-center justify-center gap-2 text-gray-600 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400 transition-colors"><Mail className="w-3.5 h-3.5" /> {email}</a>
        </div>
      </div>
    </Card>
  );
};
const TextInput = ({ label, required, className, type, ...props }) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === 'password';

  return (
    <label className={`block text-sm text-gray-700 dark:text-gray-300 ${className || ''}`}>
      <span>{label}{required && <span className="text-red-500"> *</span>}</span>
      <div className="relative">
        <input {...props} type={isPassword && !showPassword ? 'password' : 'text'} className={`mt-1 w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-600 ${props.disabled ? 'bg-gray-100 dark:bg-gray-800' : ''} ${isPassword ? 'pr-10' : ''}`} />
        {isPassword && <button type="button" onClick={() => setShowPassword(s => !s)} className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">{showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}</button>}
      </div>
    </label>
  );
};

const OtpInput = ({ length = 6, value, onChange }) => {
  const inputRefs = React.useRef([]);

  // Function to handle input changes
  const handleChange = (element, index) => {
    if (isNaN(element.value)) return;

    const newValue = value.split('');
    newValue[index] = element.value;
    onChange(newValue.join(''));

    // Focus next input if there is a value
    if (element.nextSibling && element.value) {
      element.nextSibling.focus();
    }
  };

  // Function to handle backspace for easy correction
  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !e.target.value && e.target.previousSibling) {
      e.target.previousSibling.focus();
    }
  };

  // Function to handle pasting the OTP
  const handlePaste = (e) => {
    const paste = e.clipboardData.getData('text');
    if (paste.length === length && /^\d+$/.test(paste)) {
      onChange(paste);
      // Focus the last input after paste
      inputRefs.current[length - 1]?.focus();
    }
  };

  return (
    <div className="flex justify-center gap-2 sm:gap-3" onPaste={handlePaste}>
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={el => (inputRefs.current[index] = el)}
          type="text"
          maxLength="1"
          value={value[index] || ''}
          onChange={(e) => handleChange(e.target, index)}
          onKeyDown={e => handleKeyDown(e, index)}
          onFocus={e => e.target.select()}
          className="w-12 h-12 sm:w-14 sm:h-14 text-center text-lg sm:text-2xl font-semibold border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 rounded-xl focus:ring-2 focus:ring-red-600 focus:outline-none"
        />
      ))}
    </div>
  );
};

const VoterIdInput = ({ value, onChange }) => {
  const inputRefs = React.useRef([]);
  const length = 10;

  const handleChange = (e, index) => {
    const { value: val } = e.target;
    const currentVal = value || '';
    let newValue = [...currentVal.padEnd(length, ' ')];

    // Allow only letters for first 3 chars, and numbers for the rest
    if (index < 3 && !/^[A-Z]*$/i.test(val)) return;
    if (index >= 3 && !/^[0-9]*$/.test(val)) return;

    newValue[index] = val.toUpperCase();
    onChange(newValue.join('').trimEnd());

    if (val && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData('text').toUpperCase();
    if (paste.length === length && /^[A-Z]{3}[0-9]{7}$/i.test(paste)) {
      onChange(paste);
      inputRefs.current[length - 1]?.focus();
    }
  };

  return (
    <div className="flex flex-wrap justify-center gap-1.5" onPaste={handlePaste}>
      {Array.from({ length }).map((_, index) => (
        <input
          key={index}
          ref={el => (inputRefs.current[index] = el)}
          type="text"
          maxLength="1"
          value={value[index] || ''}
          onChange={e => handleChange(e, index)}
          onKeyDown={e => handleKeyDown(e, index)}
          onFocus={(e) => e.target.select()}
          className={`w-8 h-10 sm:w-9 sm:h-12 text-center text-sm sm:text-base font-semibold border rounded-md focus:ring-2 focus:ring-red-600 focus:outline-none ${index < 3 ? 'border-blue-300' : 'border-gray-300'}`}
        />
      ))}
    </div>
  );
};

const AadhaarInput = ({ value, onChange }) => {
  const inputRefs = React.useRef([]);
  const length = 12;

  const handleChange = (e, index) => {
    const { value: val } = e.target;
    if (!/^[0-9]*$/.test(val)) return;

    const currentVal = value || '';
    let newValue = [...currentVal.padEnd(length, ' ')];
    newValue[index] = val;
    onChange(newValue.join('').trimEnd());

    if (val && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !e.target.value && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    const paste = e.clipboardData.getData('text').replace(/\s/g, '');
    if (paste.length === length && /^[0-9]{12}$/.test(paste)) {
      onChange(paste);
      inputRefs.current[length - 1]?.focus();
    }
  };

  return (
    <div className="flex flex-wrap justify-center gap-1.5" onPaste={handlePaste}>
      {Array.from({ length }).map((_, index) => (
        <React.Fragment key={index}>
          <input
            ref={el => (inputRefs.current[index] = el)} type="text" maxLength="1" value={value[index] || ''}
            onChange={e => handleChange(e, index)} onKeyDown={e => handleKeyDown(e, index)} onFocus={(e) => e.target.select()}
            className="w-8 h-10 sm:w-9 sm:h-12 text-center text-sm sm:text-base font-semibold border border-gray-300 rounded-md focus:ring-2 focus:ring-green-600 focus:outline-none"
          />
          {(index === 3 || index === 7) && <div className="w-2" />}
        </React.Fragment>
      ))}
    </div>
  );
};

const Skeleton = ({ className }) => <div className={`bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse ${className}`} />;

const EmptyState = ({ icon: Icon, title, message }) => (
  <Card className="p-6 text-center border-dashed border-gray-300 dark:border-gray-600"><Icon className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" /><h3 className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3><p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{message}</p></Card>
);
const Sidebar = () => {
  const location = useLocation();
  const { role, logout } = useAuth();

  const sidebarItems = [
    { k: "home", label: "முகப்பு", icon: Home, path: "/" },
    { k: "members", label: "உறுப்பினர்கள்", icon: UsersIcon, path: "/members" },
    { k: "new_user", label: "புதிய உறுப்பினர்", icon: UserPlus, path: "/members/new" },
    { k: "messages", label: "அறிவிப்புகள்", icon: MessageSquareText, path: "/messages" },
    { k: "idcard", label: "ID அட்டை", icon: IdCardIcon, path: "/idcard" },
    { k: "events", label: "நிகழ்வுகள்", icon: CalendarDays, path: "/events" },
    { k: "profile", label: "சுயவிவரம்", icon: UserIcon, path: "/profile" },
    { k: "help", label: "உதவி", icon: HelpCircle, path: "/help" },
  ].filter(i => RBAC[role]?.tabs.includes(i.k));

  const socialLinks = [
    { name: 'Facebook', icon: Facebook, url: 'https://www.facebook.com/tvkpartyofficial', color: 'text-blue-300' },
    { name: 'X', icon: Twitter, url: 'https://x.com/tvkpartyoff', color: 'text-sky-300' },
    { name: 'Instagram', icon: Instagram, url: 'https://www.instagram.com/tvkpartyofficial', color: 'text-pink-300' },
    { name: 'YouTube', icon: Send, url: 'https://www.youtube.com/@TVKITWING', color: 'text-red-300' },
  ];

  return (
    <aside className="hidden sm:flex sm:flex-col w-64 border-r border-green-900 shrink-0 relative bg-gradient-to-b from-green-800 to-green-900 h-screen">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-20"
        style={{ backgroundImage: `url(${sidebarBg})` }}
        aria-hidden="true"
      />
      <div className="relative z-10 flex items-center justify-center h-24 border-b border-white/20 px-4 bg-black/10">
        <img src={partyLogo} alt="Party Logo" className="h-20 w-auto drop-shadow-[0_8px_12px_rgba(250,204,21,0.6)]" />
      </div>
      <div className="relative z-10 flex-1 flex flex-col overflow-y-auto">
        <nav className="flex-1 px-4 py-4 flex flex-col">
          <div className="flex-grow space-y-2 overflow-y-auto">
            {sidebarItems.map(item => {
              const active = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link
                  key={item.k}
                  to={item.path}
                  className={`flex items-center px-3 py-2.5 text-sm font-medium rounded-lg transition-colors ${active
                    ? 'bg-white/20 text-white font-semibold'
                    : 'text-green-100 hover:bg-white/10 hover:text-white'
                    }`}
                >
                  <Icon className="mr-3 h-5 w-5" />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </div>
          <div className="mt-4 pt-4 border-t border-white/20">
            <h3 className="px-3 text-xs font-semibold text-green-200 uppercase tracking-wider mb-2">சமூக ஊடகம்</h3>
            <div className="flex items-center justify-around px-3">
              {socialLinks.map(social => (
                <a key={social.name} href={social.url} target="_blank" rel="noopener noreferrer" className="p-2 rounded-full hover:bg-white/10 transition-colors">
                  <social.icon className={`w-5 h-5 ${social.color}`} />
                </a>
              ))}
            </div>
          </div>
          <button
            onClick={logout}
            className="flex items-center w-full px-3 py-2.5 text-sm font-medium rounded-lg text-green-100 hover:bg-white/10 hover:text-white transition-colors mt-auto"
          >
            <LogOut className="mr-3 h-5 w-5" />
            <span>வெளியேறு</span>
          </button>
        </nav>
        <div className="relative z-10 px-4 py-3 border-t border-white/20 bg-black/20 mt-auto">
          <div className="text-xs text-green-200 text-center">Version 1.0.0</div>
          <div className="text-xs text-green-200 mt-1">தமிழக வாழ்வுரிமைக் கட்சி - தகவல் தொழில்நுட்பப்பிரிவு</div>
        </div>
      </div>
    </aside>
  );
};

const SelectInput = ({ label, required, children, className, ...props }) => (
  <label className={`block text-sm text-gray-700 dark:text-gray-300 ${className || ''}`}>
    <span>{label}{required && <span className="text-red-500"> *</span>}</span>
    <select {...props} className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-600">
      {children}
    </select>
  </label>
);

const FullPageLoader = () => (
  <div className="w-full h-screen flex flex-col font-anek bg-gray-50 dark:bg-gray-900">
    <header className="sticky top-0 z-40 bg-white/90 dark:bg-gray-800/90 backdrop-blur border-b border-gray-100 dark:border-gray-700">
      <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-md animate-pulse sm:hidden" />
          <div>
            <div className="h-3 w-16 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
            <div className="h-5 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mt-1.5" />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
          <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
          <div className="w-10 h-10 bg-gray-200 dark:bg-gray-700 rounded-full animate-pulse" />
        </div>
      </div>
    </header>
    <main className="flex-1 overflow-y-auto">
      <div className="max-w-5xl mx-auto px-4 pt-4">
        <div className="h-24 bg-gray-200 dark:bg-gray-700 rounded-2xl animate-pulse mb-6" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded-2xl animate-pulse" />
          <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded-2xl animate-pulse" />
          <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded-2xl animate-pulse" />
          <div className="h-20 bg-gray-200 dark:bg-gray-700 rounded-2xl animate-pulse" />
        </div>
      </div>
    </main>
  </div>
);

const Progress = ({ step, total }) => (
  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 mb-6">
    <div className="bg-green-600 h-2.5 rounded-full transition-all duration-500" style={{ width: `${(step / total) * 100}%` }}></div>
  </div>
);

/** Layout */
const DonationModal = ({ onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
    onClick={onClose}
  >
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      className="relative z-10 w-full max-w-sm"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="relative bg-gradient-to-br from-yellow-300 via-orange-400 to-red-400 p-[2px] rounded-2xl overflow-hidden shadow-2xl">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(255,255,255,0.4),transparent_70%)]"></div>
        <div className="relative bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-6 text-center">
          {/* Floating coins animation */}
          <div className="absolute inset-0 overflow-hidden">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="absolute animate-float"
                style={{
                  left: `${Math.random() * 100}%`,
                  animationDelay: `${i * 0.5}s`,
                  top: '100%'
                }}
              >
                <IndianRupee className="h-6 w-6 text-yellow-500 opacity-30" />
              </div>
            ))}
          </div>

          {/* Icon with glowing effect */}
          <div className="relative mx-auto">
            <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 rounded-full blur-lg animate-pulse opacity-70"></div>
            <div className="relative flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400">
              <div className="absolute inset-[2px] rounded-full bg-white"></div>
              <Heart className="h-10 w-10 text-red-500 relative z-10 fill-current animate-[pulse_2s_ease-in-out_infinite]" />
            </div>
          </div>

          <h3 className="mt-6 text-3xl font-bold bg-gradient-to-br from-yellow-600 via-orange-600 to-red-600 text-transparent bg-clip-text">
            நன்கொடை அம்சம்
          </h3>
          <p className="mt-3 text-lg text-orange-800 font-medium">விரைவில் வருகிறது</p>
          <p className="mt-4 text-sm text-orange-700">
            எங்கள் கட்சியின் நோக்கத்திற்காக நீங்கள் தடையின்றி மற்றும் பாதுகாப்பான முறையில் பங்களிக்க நாங்கள் கடுமையாக உழைத்து வருகிறோம். காத்திருங்கள்!
          </p>

          <button
            onClick={onClose}
            type="button"
            className="mt-6 w-full px-4 py-3 text-sm font-medium text-white bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 rounded-xl hover:shadow-lg hover:scale-[1.02] transition-all relative overflow-hidden group"
          >
            <span className="absolute inset-0 bg-gradient-to-r from-yellow-400 via-orange-400 to-red-400 animate-[shimmer_2s_linear_infinite] opacity-70"></span>
            <span className="relative z-10">சரி!</span>
          </button>
        </div>
      </div>
    </motion.div>
  </motion.div>
);

const ChatComingSoonModal = ({ onClose }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
    onClick={onClose}
  >
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      className="relative z-10 w-full max-w-sm"
      onClick={(e) => e.stopPropagation()}
    >
      <Card className="p-6 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-100 to-blue-200 dark:from-blue-800 dark:to-blue-900">
          <MessageSquareText className="h-8 w-8 text-blue-600 dark:text-blue-300" />
        </div>
        <h3 className="mt-5 text-xl font-bold text-gray-900 dark:text-gray-100">அரட்டை அம்சம்</h3>
        <p className="mt-2 text-base text-gray-600 dark:text-gray-300">விரைவில் வருகிறது</p>
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
          உறுப்பினர்கள் ஒருவருக்கொருவர் பாதுகாப்பாக உரையாடுவதற்கும், கட்சி தொடர்பான கலந்துரையாடல்களில் ஈடுபடுவதற்கும் ஒரு புதிய அரட்டை தளத்தை நாங்கள் உருவாக்கி வருகிறோம்.
        </p>
        <div className="mt-6">
          <button
            onClick={onClose}
            type="button"
            className="w-full px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            சரி
          </button>
        </div>
      </Card>
    </motion.div>
  </motion.div>
);

const TopBar = () => {
  const location = useLocation();

  const pageNames = {
    "/": "முகப்பு",
    "/members": "உறுப்பினர்கள்",
    "/members/new": "புதிய உறுப்பினர்",
    "/messages": "அறிவிப்புகள்",
    "/idcard": "ID அட்டை",
    "/events": "நிகழ்வுகள்",
    "/profile": "சுயவிவரம்",
    "/help": "உதவி",
    "/admin": "நிர்வாகம்",
    "/approvals": "அனுமதிகள்",
    "/settings": "அமைப்புகள்",
    "/registration-success": "பதிவு வெற்றி",
  };

  const currentPageName = pageNames[location.pathname] || "தமிழக வாழ்வுரிமைக் கட்சி";

  return (
    <>
      <header className="sticky top-0 z-40 bg-gradient-to-b from-green-800 to-green-900 border-b border-white/20">
        <div className="relative z-10 flex items-center justify-center h-20 px-4 bg-black/10">
          <img src={partyLogo} alt="Party Logo" className="h-16 w-auto drop-shadow-[0_8px_12px_rgba(250,204,21,0.6)]" />
        </div>
      </header>
    </>
  );
};


function BottomNav() {
  const { role } = useAuth();
  const location = useLocation();
  const items = [
    { k: "home", label: "முகப்பு", icon: Home, path: "/" },
    { k: "members", label: "உறுப்பினர்கள்", icon: Users, path: "/members" },
    { k: "messages", label: "அறிவிப்புகள்", icon: MessageSquareText, path: "/messages" },
    { k: "idcard", label: "ID அட்டை", icon: IdCardIcon, path: "/idcard" },
    { k: "events", label: "நிகழ்வுகள்", icon: CalendarDays, path: "/events" },
    { k: "profile", label: "சுயவிவரம்", icon: UserIcon, path: "/profile" },
  ].filter(i => RBAC[role]?.tabs.includes(i.k));

  const middleIndex = Math.floor(items.length / 2);

  return (
    <nav className="fixed bottom-0 left-0 right-0 h-16 bg-white/95 dark:bg-gray-800/95 backdrop-blur border-t border-gray-200 dark:border-gray-700 z-50 sm:hidden">
      <div className="flex justify-around items-center h-full">
        {items.slice(0, middleIndex).map(item => <BottomNavItem key={item.k} {...item} />)}

        <motion.div whileTap={{ scale: 0.9 }}>
          <Link to="/members/new" className="relative -top-5 flex flex-col items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full shadow-lg text-white">
            <UserPlus className="w-7 h-7" />
          </Link>
        </motion.div>

        {items.slice(middleIndex).map(item => <BottomNavItem key={item.k} {...item} />)}
      </div>
    </nav>
  );
}

const BottomNavItem = ({ k, path, icon: Icon }) => {
  const location = useLocation();
  const active = location.pathname === path;
  return (
    <Link key={k} to={path} className={`flex flex-col items-center py-2 text-[11px] ${active ? "text-green-700" : "text-gray-500"}`}>
      <Icon className="w-6 h-6" />
    </Link>
  );
};

const Shell = ({ children }) => {
  const [isChatOpen, setIsChatOpen] = useState(false);

  return (
    <div className="flex h-screen font-anek">
      <MotionBackground />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopBar />
        <main className="flex-1 overflow-y-auto pb-24 sm:pb-4 scrollbar-hide">
          <div className="max-w-5xl mx-auto px-4 pt-4">{children}</div>
        </main>
      </div>
      <BottomNav />

      <button onClick={() => setIsChatOpen(true)} className="fixed bottom-24 sm:bottom-6 right-6 bg-green-600 text-white p-4 rounded-full shadow-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 z-50 hidden sm:flex">
        <MessageSquareText className="w-6 h-6" />
      </button>

      <AnimatePresence>{isChatOpen && <LiveChatWindow onClose={() => setIsChatOpen(false)} />}</AnimatePresence>
    </div>
  );
};

const PrincipleCard = ({ index, title, icon: Icon, children }) => {
  const gradients = [
    'from-red-500 to-orange-500', 'from-amber-500 to-yellow-500', 'from-lime-500 to-green-500',
    'from-emerald-500 to-teal-500', 'from-cyan-500 to-sky-500', 'from-blue-500 to-indigo-500',
    'from-violet-500 to-purple-500', 'from-fuchsia-500 to-pink-500', 'from-rose-500 to-red-500',
    'from-slate-500 to-gray-500'
  ];
  const gradient = gradients[index % gradients.length];

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: index * 0.05 }}>
      <Card className="overflow-hidden h-full flex flex-col">
        <div className={`p-4 bg-gradient-to-br ${gradient} text-white flex items-center gap-4`}>
          <div className="p-3 bg-white/20 rounded-xl"><Icon className="w-7 h-7" /></div>
          <div>
            <div className="text-xs font-bold opacity-80">கொள்கை {index + 1}</div>
            <h3 className="text-lg font-bold">{title}</h3>
          </div>
        </div>
        <div className="p-4 flex-grow">{children}</div>
      </Card>
    </motion.div>
  );
};

const PrinciplesPage = () => {
  const { push } = useToasts();
  const shareTitle = "தமிழக வாழ்வுரிமை கட்சியின் கோட்பாடுகள்";
  const shareUrl = window.location.origin + "/principles"; // Dynamic URL based on environment
  const shareText = `${shareTitle} - ${shareUrl}`;

  const handleNativeShare = () => {
    if (navigator.share) {
      navigator.share({
        title: shareTitle,
        text: `தமிழக வாழ்வுரிமை கட்சியின் கோட்பாடுகளைப் பற்றி தெரிந்து கொள்ளுங்கள்! (Principles of TVK)`,
        url: shareUrl,
      }).catch((error) => console.log('Error sharing', error));
    } else {
      // Fallback for browsers that don't support Web Share API
      navigator.clipboard.writeText(shareText)
        .then(() => push('Link copied to clipboard!', 'success'))
        .catch(() => push('Failed to copy link.', 'error'));
    }
  };

  const socialLinks = [
    { name: 'Facebook', icon: Facebook, url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, color: 'text-blue-600 bg-blue-100 hover:bg-blue-200' },
    { name: 'Twitter', icon: Twitter, url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`, color: 'text-sky-500 bg-sky-100 hover:bg-sky-200' },
    { name: 'WhatsApp', icon: MessageCircle, url: `https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`, color: 'text-green-600 bg-green-100 hover:bg-green-200' },
  ];

  const principles = [
    { title: "தமிழர் அடையாளம் மற்றும் தன்னாட்சி", icon: Landmark, content: "தமிழரின் அடையாளம், பண்பாடு மற்றும் மொழியை காக்கும் தன்னார்வம் கொண்ட இயக்கமாகும். தமிழர் வரலாறு, கலாச்சாரம், கலை மற்றும் மொழி ஆகியவை தமிழகத்தின் ஆன்மா என்பதை நாங்கள் நம்புகிறோம். அரசியல், கல்வி, நிர்வாகம் மற்றும் நீதித்துறை துறைகளில் தமிழ் மொழி முழுமையாகப் பயன்பட வேண்டும். தமிழ்நாட்டின் பொருளாதார மற்றும் சமூக தன்னாட்சி உறுதி செய்ய, மத்திய அரசின் தலையீடுகளிலிருந்து விடுபட்ட ஒரு வலிமையான மாநில தன்னாட்சி அமைப்பை உருவாக்கும் நோக்கம்." },
    { title: "சமூக நீதி மற்றும் சமத்துவம்", icon: Scale, content: "சமூகத்தில் எவரும் புறக்கணிக்கப்படக் கூடாது என்று நம்புகிறது. ஜாதி, மதம், பாலினம், மொழி, நிலைமைகள் என்பவற்றின் அடிப்படையில் ஏற்படும் பாகுபாடுகளை ஒழிக்க முயற்சி செய்கிறது. ஒவ்வொரு குடிமகனுக்கும் சம வாய்ப்பும் சம உரிமையும் உறுதி செய்யப்படும். புறக்கணிக்கப்பட்ட சமூகங்கள், விவசாயிகள், தொழிலாளர்கள் மற்றும் பெண்கள் முன்னேற கல்வி, வேலைவாய்ப்பு, தொழில் முனைவு ஆதரவு வழங்கப்படும்." },
    { title: "கல்வி மற்றும் அறிவு உரிமை", icon: BookOpen, content: "அறிவே சமூக மாற்றத்தின் தூண். அனைவருக்கும் தரமான, இலவச கல்வி கிடைக்க வேண்டும். தாய்மொழியில் ஆரம்பக் கல்வி வழங்கப்பட வேண்டும். தொழில்நுட்பம், தொழில் பயிற்சி மற்றும் நவீன அறிவியல் கல்வி வழியே இளைஞர்கள் உலகளாவிய போட்டியில் முன்னேற வழிவகை செய்யப்படும். பள்ளி விட்டு விலகும் மாணவர்களுக்கு மறுகல்வி மற்றும் திறன் வளர்ப்பு மையங்கள் உருவாக்கப்படும்." },
    { title: "பொருளாதார தன்னிறைவு மற்றும் விவசாய முன்னேற்றம்", icon: Leaf, content: "தமிழகத்தின் நிலம் மற்றும் உழவர் நம் உயிர் நரம்புகள். விவசாயம் மாநிலத்தின் முதன்மை துறையாக மீண்டும் உயர்த்தப்படும். நீர்ப்பாசனம், விதை தரம், மண் பாதுகாப்பு மற்றும் சந்தை விலை உறுதிகாப்பு திட்டங்கள் முன்னெடுக்கப்படும். உள்ளூர் தொழில், சிறு மற்றும் நடுத்தர தொழில்கள் வளர்ச்சிக்கு ஊக்குவிப்பு வழங்கப்படும். வெளிநாட்டு முதலீடுகளுக்கு மாற்றாக தாய்நாட்டு தொழில்நுட்பங்கள் மற்றும் உள்ளூர் உற்பத்தி மையங்களை முன்னேற்றம் செய்யப்படும்." },
    { title: "இளைஞர் வலிமை மற்றும் வேலைவாய்ப்பு", icon: Zap, content: "இளைஞர்களை நாட்டின் எதிர்காலமாக அல்ல, நிகழ்கால முன்னேற்ற சக்தியாகக் காண்கிறது. ஒவ்வொரு மாவட்டத்திலும் “இளைஞர் முன்னேற்ற மையங்கள்” அமைக்கப்படும். ஸ்டார்ட்அப் ஊக்குவிப்பு, தொழில் பயிற்சி, அரசு மற்றும் தனியார் வேலை வாய்ப்பு மையங்கள் நிறுவப்படும். விளையாட்டு, கலாச்சாரம் மற்றும் ஆராய்ச்சி துறைகளில் இளைஞர்கள் பங்கெடுக்க ஊக்குவிக்கப்படும்." },
    { title: "பெண்களின் பாதுகாப்பு மற்றும் முன்னேற்றம்", icon: Shield, content: "பெண்கள் சமுதாயத்தின் அஸ்திவாரம். கல்வி, வேலைவாய்ப்பு, அரசியல் மற்றும் சமூக துறைகளில் பெண்களுக்கு சம உரிமை வழங்கப்படும். குடும்ப வன்முறை, துஷ்பிரயோகம் மற்றும் பாலியல் தொந்தரவுகளைத் தடுக்க கடுமையான சட்ட அமுல். பெண்கள் தொழில் முனைவோருக்கான கடன் மற்றும் நிதி உதவி திட்டங்கள் நடைமுறைப்படுத்தப்படும்." },
    { title: "சுற்றுச்சூழல் மற்றும் இயற்கை பாதுகாப்பு", icon: Recycle, content: "இயற்கை மற்றும் மனிதன் இணைந்த வாழ்வை ஆதரிக்கிறது. நீர், நிலம், காடு, கடல் ஆகியவை மாநிலத்தின் பொதுச் சொத்து என்று கருதப்படும். தொழில் வளர்ச்சி சுற்றுச்சூழல் சமநிலையை குலைக்கக் கூடாது. பசுமை தமிழகம் திட்டத்தின் மூலம் மரநடவு, மழைநீர் சேகரிப்பு, கழிவுநீர் சுத்திகரிப்பு நடவடிக்கைகள் மேற்கொள்ளப்படும்." },
    { title: "நல்லாட்சியும் வெளிப்படைத்தன்மையும்", icon: Gavel, content: "அரசு மக்களுக்காக — மக்கள் அரசுக்காக அல்ல. ஊழல், அதிகார துஷ்பிரயோகம், சீர்கேடு ஆகியவற்றை ஒழிக்க மின்னணு நிர்வாகம் (e-Governance) மூலம் வெளிப்படையான நடைமுறை. அரசு செலவுகள் மற்றும் திட்டங்கள் பொதுமக்களுக்கு வெளிப்படையாக வெளியிடப்படும். மக்கள் புகார் தீர்க்கும் மையங்கள் மற்றும் நேரடி நிர்வாக இணைய மையங்கள் அமைக்கப்படும்." },
    { title: "மருத்துவம் மற்றும் நலத்திட்டங்கள்", icon: HeartPulse, content: "மக்களின் உடல் நலமே நாட்டின் செல்வம். அனைத்து குடிமக்களுக்கும் இலவச அடிப்படை சுகாதார சேவை உறுதி செய்யப்படும். அரசு மருத்துவமனைகள் மேம்படுத்தப்பட்டு, கிராமப்புறங்களிலும் நவீன மருத்துவ வசதிகள் வழங்கப்படும். மனநலம், பெண்கள் மற்றும் குழந்தைகள் நலத்திட்டங்கள் வலுப்படுத்தப்படும்." },
    { title: "தமிழக ஒருமைப்பாடு மற்றும் தேசிய ஒற்றுமை", icon: Globe, content: "தமிழ்நாட்டின் பெருமை மற்றும் இந்தியாவின் ஒற்றுமை ஆகிய இரண்டையும் மதிக்கிறது. தமிழ்நாடு மாநிலத்தின் தனித்துவம், உரிமைகள் மற்றும் கலாச்சாரம் பாதுகாக்கப்படும். இந்திய ஒன்றியத்தின் ஒரு அங்கமாக, மாநிலங்களுக்கு சம உரிமை மற்றும் சுயநிர்ணய அதிகாரம் வலியுறுத்தப்படும். உலகம் முழுவதும் வாழும் தமிழர்களுடன் கலாச்சார, தொழில் மற்றும் கல்வி இணைப்புகள் மேம்படுத்தப்படும்." },
  ];

  return (
    <Shell>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <div className="space-y-6">
          <Card className="p-4 sm:p-6 text-center bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-800 dark:to-gray-900">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-gradient-to-br from-purple-100 to-fuchsia-200 dark:from-purple-800 dark:to-fuchsia-900 rounded-xl inline-block">
                <FileText className="w-8 h-8 text-purple-700 dark:text-purple-300" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">தமிழக வாழ்வுரிமை கட்சியின் கோட்பாடுகள்</h1>
            <p className="text-md text-gray-500 dark:text-gray-400 mt-2 font-sans">(Principles of Thamizhaga Vaazhvurimai Katchi - TVK)</p>
            <p className="max-w-3xl mx-auto mt-4 text-gray-600 dark:text-gray-300">
              ஒவ்வொரு தமிழனின் அடிப்படை உரிமை, மரியாதை மற்றும் முன்னேற்றத்தை உறுதி செய்ய உருவான ஒரு மக்கள் இயக்கம். சமூக நீதியும், பொருளாதார சமத்துவமும், கல்வி வளர்ச்சியும், சுற்றுச்சூழல் பாதுகாப்பும் நிறைந்த ஒரு தன்னிறைவு கொண்ட தமிழகம் நோக்கி பயணிக்கிறோம்.
            </p>
          </Card>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {principles.map((p, i) => (
              <PrincipleCard key={i} index={i} title={p.title} icon={p.icon}>
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{p.content}</p>
              </PrincipleCard>
            ))}
          </div>

          <Card className="p-6">
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-800 dark:text-gray-100">முடிவுரை</h3>
              <p className="mt-2 text-gray-600 dark:text-gray-300">
                தமிழக வாழ்வுரிமை கட்சி என்பது ஒரு அரசியல் கட்சி மட்டுமல்ல — அது ஒரு தமிழர் மறுமலர்ச்சி இயக்கம்.
              </p>
              <blockquote className="mt-4 border-r-4 border-amber-500 pr-4 italic text-right">
                <strong className="text-lg text-gray-700 dark:text-gray-200">“தமிழரின் ஒவ்வொரு உரிமையும் வாழ்வுரிமை; வாழ்வுரிமையைப் பாதுகாப்பதே அரசியலின் நோக்கம்.”</strong>
              </blockquote>
            </div>

            <div className="mt-8 pt-6 border-t dark:border-gray-700 flex flex-col sm:flex-row items-center justify-between gap-4">
              <h4 className="text-base font-semibold text-gray-800 dark:text-gray-200">இந்தக் கொள்கைகளைப் பகிரவும்:</h4>
              <div className="flex items-center gap-3">
                {socialLinks.map(social => (
                  <a key={social.name} href={social.url} target="_blank" rel="noopener noreferrer" className={`p-3 rounded-full transition-all duration-300 hover:scale-110 ${social.color}`} title={`Share on ${social.name}`}>
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
                <button
                  onClick={handleNativeShare}
                  className="p-3 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 transition-all duration-300 hover:scale-110" title="More sharing options"
                >
                  <Share2 className="w-5 h-5 text-gray-700 dark:text-gray-200" />
                </button>
              </div>
            </div>
          </Card>
        </div>
      </motion.div>
    </Shell>
  );
};

/** Pages */
function HomePage() {
  const { push } = useToasts();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [selectedNote, setSelectedNote] = useState(null); // This is fine for local UI state
  const [isChatComingSoonModalOpen, setIsChatComingSoonModalOpen] = useState(false);

  const { data: stats = [], isLoading: isLoadingStats } = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: () => apiFetch('/dashboard/stats'),
    enabled: !!user,
    initialData: [],
  });

  const { data: notes = [], isLoading: isLoadingNotes } = useQuery({
    queryKey: ['dashboardNotifications'],
    queryFn: () => apiFetch('/dashboard/notifications'),
    enabled: !!user,
    initialData: [],
  });

  const { data: contacts = [], isLoading: isLoadingContacts } = useQuery({
    queryKey: ['dashboardContacts'], // Changed from contacts to dashboardContacts for consistency
    queryFn: () => apiFetch('/dashboard/contacts'),
    enabled: !!user,
    initialData: [],
  });

  const actions = [
    { icon: UserPlus, label: "உறுப்பினர் சேர்", key: 'add', onClick: () => navigate('/members/new') },
    { icon: ClipboardList, label: "அனுமதிகள்", key: 'approve', onClick: () => navigate('/approvals') },
    { icon: IdCardIcon, label: "அடையாள அட்டை", key: 'id', onClick: () => navigate('/idcard') },
    { icon: Megaphone, label: "அறிவிப்புகள்", key: 'ann', onClick: () => navigate('/messages') },
    { icon: CalendarDays, label: "நிகழ்வுகள்", key: 'events', onClick: () => navigate('/events') },
  ];

  const districtData = [
    { name: 'சென்னை', members: 450 },
    { name: 'கோவை', members: 320 },
    { name: 'மதுரை', members: 280 },
    { name: 'திருச்சி', members: 250 },
    { name: 'சேலம்', members: 220 },
  ];

  const assemblyData = [
    { name: 'சேப்பாக்கம்', members: 120 },
    { name: 'வேளச்சேரி', members: 95 },
    { name: 'சிங்காநல்லூர்', members: 80 },
  ];

  const getDailyKural = () => {
    const today = new Date();
    const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
    const kuralIndex = dayOfYear % thirukkuralData.kural.length;
    return thirukkuralData.kural[kuralIndex];
  };
  const dailyKural = getDailyKural();

  const greetingMessages = [
    "உங்கள் அணியின் வளர்ச்சி சிறப்பாக உள்ளது! தொடர்ந்து பணியாற்றுங்கள், மாற்றத்தை உருவாக்குவோம். 🚀",
    "இன்றைய உங்கள் உழைப்பு, நாளைய தமிழகத்தின் வாழ்வுரிமை! 💪",
    "ஒவ்வொரு புதிய உறுப்பினரும் நமது கட்சியின் வலிமை. தொடரட்டும் உங்கள் பணி! ✨",
    "உங்கள் பகுதியில் கட்சியின் குரலை ஒலிக்கச் செய்யுங்கள். உங்கள் பணி சிறக்கட்டும்! 📢",
    "தடைகளைத் தாண்டி, இலக்கை நோக்கிப் பயணிப்போம். உங்கள் முயற்சிக்கு வாழ்த்துகள்! 🎯",
    "மாற்றத்திற்கான விதையை நீங்கள் விதைக்கிறீர்கள். அதன் பலனை தமிழகம் அறுவடை செய்யும். 🌱",
    "உங்கள் ஆற்றல், நமது கட்சியின் உந்துசக்தி. இன்று ஒரு சிறந்த நாளாக அமையட்டும்! 🔥",
    "சோர்வடைய வேண்டாம், சிகரம் தொடுவோம். உங்கள் பணி மென்மேலும் வளரட்டும்! ⛰️",
    "மக்களுடன் இணைந்திருங்கள், மகத்தான மாற்றங்களை உருவாக்குங்கள். 🤝",
    "உங்கள் ஒவ்வொரு செயலும் வரலாற்றில் பதியப்படுகிறது. பெருமையுடன் பணியாற்றுங்கள்! 📜",
    "புதிய உறுப்பினர்களை இணைத்து, நம் குடும்பத்தைப் பெரிதாக்குங்கள். 👨‍👩‍👧‍👦",
    "இன்று நீங்கள் சந்திக்கும் ஒவ்வொருவரும் நமது கொள்கையின் தூதுவராக மாறலாம். 🗣️",
    "உங்கள் பகுதியில் கட்சியின் வளர்ச்சி அபாரம். இந்த வேகம் தொடரட்டும்! ⚡",
    "சவால்களைக் கண்டு அஞ்சாதே, சரித்திரம் படைப்போம் வா! ✊",
    "உங்கள் உழைப்பு வீண் போகாது. தமிழகம் உங்களை நம்பியுள்ளது. 🙏",
    "புதிய சிந்தனைகளுடன், புதிய பாதையில் பயணிப்போம். உங்கள் பங்களிப்பு முக்கியம்! 💡",
    "ஒற்றுமையே நமது பலம். அணியாகச் செயல்பட்டு வெற்றிகளைக் குவிப்போம். 🏆",
    "இன்றைய சிறு துளி, நாளைய பெருவெள்ளம். உங்கள் பணி தொடரட்டும்! 🌊",
    "உங்கள் குரல், மக்களின் குரல். ஓங்கி ஒலிக்கட்டும்! 🎤",
    "வாழ்வுரிமை வென்றெடுக்க, உங்கள் பங்களிப்பு இன்றியமையாதது. வாழ்த்துகள்! 🌟"
  ];
  const [greeting, setGreeting] = useState(greetingMessages[Math.floor(Math.random() * greetingMessages.length)]);

  useEffect(() => {
    const interval = setInterval(() => {
      setGreeting(greetingMessages[Math.floor(Math.random() * greetingMessages.length)]);
    }, 8788);
    return () => clearInterval(interval);
  }, []);

  const kuralShareText = `இன்றைய திருக்குறள் (குறள் ${dailyKural.Number}):\n"${dailyKural.Line1}\n${dailyKural.Line2}"\n\n- மு.வ உரை: ${dailyKural.mv}`;
  const shareUrl = `https://join.tvkparty.org/`;

  return (
    <Shell>
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative mb-4 -mt-4 sm:mt-0 -mx-4 sm:mx-0"
      >
        <Card
          onClick={() => navigate('/principles')}
          className="cursor-pointer group relative p-4 flex items-center gap-4 overflow-hidden bg-gradient-to-br from-purple-500 to-fuchsia-600 dark:from-purple-700 dark:to-fuchsia-800 shadow-lg hover:shadow-xl transition-all duration-300 rounded-none sm:rounded-2xl"
        >
          <div className="relative z-10 flex items-center gap-4 w-full">
            <div className="relative p-3 bg-white/20 rounded-full border-2 border-white/50 backdrop-blur-sm">
              <FileText className="w-6 h-6 text-white drop-shadow-lg" />
            </div>
            <h4 className="font-bold text-lg text-white flex-1">கட்சியின் கொள்கைகளை படிக்க</h4>
            <ChevronRight className="w-6 h-6 text-white/70 ml-auto transform group-hover:translate-x-1 transition-transform" />
          </div>
        </Card>
      </motion.div>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="relative text-white rounded-2xl p-4 overflow-hidden group flex items-start justify-between gap-4">
        <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-green-700"></div>
        <img src={wavylines} alt="background" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="relative z-10">
          <div className="text-sm">வணக்கம், {user?.name || 'தலைவர்'}!</div>
          <div className="text-lg font-semibold">{greeting}</div>
          <div className="text-xs mt-1 opacity-90">நிலுவையில் உள்ள உறுப்பினர் அனுமதிகள் மற்றும் புதிய சேர்க்கைகளை நிர்வகிக்கவும்.</div>
          <div className="absolute top-0 -left-full h-full w-1/2 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 animate-[glare_5s_ease-in-out_infinite]"></div>
        </div>
        <div className="relative z-10 flex-shrink-0 text-center flex flex-col items-center gap-3">
          <div className="relative px-4 py-2 bg-white/25 rounded-full border border-white/30 backdrop-blur-sm">
            <div className="text-xs font-bold uppercase tracking-wider">{user?.badge || 'தங்கம்'}</div>
            <div className="text-[9px] opacity-80 -mt-0.5">பயனர் பேட்ஜ்</div>
          </div>
          <div className="relative sm:hidden">
            <div className="absolute -inset-1.5 rounded-full border-2 border-dashed border-white/30 animate-spin-slow drop-shadow-[0_0_3px_rgba(255,255,255,0.5)]"></div>
            <div className="absolute -inset-2.5 rounded-full border border-dashed border-white/20 animate-spin-slow-reverse drop-shadow-[0_0_5px_rgba(255,255,255,0.3)]"></div>
            <img src={normalizeMinioUrl(user?.photo) || `https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(user?.name || 'User')}`} alt="User" className="relative w-20 h-20 rounded-full object-cover border-2 border-white/50 shadow-lg" />
          </div>
        </div>
      </motion.div>

      <div className="mt-4 sm:mt-6 px-2 sm:px-4">
        <div onClick={() => setIsChatComingSoonModalOpen(true)} className="cursor-pointer group">
          <Card className="relative p-4 sm:p-5 flex items-center gap-4 overflow-hidden bg-gradient-to-br from-blue-500 to-indigo-600 dark:from-blue-700 dark:to-indigo-800 shadow-lg hover:shadow-xl transition-all duration-300">
            {/* Animated background shapes */}
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-white/10 rounded-full animate-pulse"></div>
            <div className="absolute -bottom-8 -right-4 w-32 h-32 bg-white/10 rounded-full animate-[pulse_3s_ease-in-out_infinite]"></div>

            <div className="relative z-10 flex items-center gap-4 w-full">
              {/* Icon with glowing effect */}
              <div className="relative">
                <div className="absolute -inset-2 bg-white/30 rounded-full blur-lg animate-pulse opacity-70 group-hover:opacity-100 transition-opacity"></div>
                <div className="relative p-3 bg-white/20 rounded-full border-2 border-white/50 backdrop-blur-sm">
                  <MessageSquareText className="w-7 h-7 sm:w-8 sm:h-8 text-white drop-shadow-lg" />
                </div>
              </div>

              <div className="text-white">
                <h4 className="font-bold text-lg sm:text-xl">அரட்டை</h4>
                <p className="text-sm sm:text-base opacity-90">விரைவில் வருகிறது...</p>
              </div>
              <ChevronRight className="w-6 h-6 text-white/70 ml-auto transform group-hover:translate-x-1 transition-transform" />
            </div>
          </Card>
        </div>
      </div>

      <AnimatePresence>
        {isChatComingSoonModalOpen && <ChatComingSoonModal onClose={() => setIsChatComingSoonModalOpen(false)} />}
      </AnimatePresence>

      <Section title="இன்றைய திருக்குறள்">
        <div className="flex flex-col gap-3">
          <Card className="relative p-4 text-white shadow-lg font-anek flex items-center gap-4 overflow-hidden">
            <img src={kuralBg} alt="Background" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-br from-yellow-900 to-amber-950 opacity-75"></div>
            <div className="relative z-10 flex items-center gap-4 w-full">
              <img src={thiruvalluvarImage} alt="Thiruvalluvar" className="w-24 h-auto sm:w-28 object-contain flex-shrink-0" />
              <div className="text-center sm:text-left flex-1 min-w-0">
                <h4 className="font-semibold text-xs sm:text-base leading-normal">{dailyKural.Line1}<br />{dailyKural.Line2}</h4>
                <p className="text-xs sm:text-sm mt-2 opacity-80">{dailyKural.mv}</p>
                <p className="text-xs mt-2 opacity-70">- குறள் {dailyKural.Number}</p>
              </div>
            </div>
            {/* Share icon for desktop/tablet view */}
            <button onClick={() => navigator.share ? navigator.share({ title: 'இன்றைய திருக்குறள்', text: kuralShareText, url: shareUrl }) : navigator.clipboard.writeText(kuralShareText).then(() => push('திருக்குறள் நகலெடுக்கப்பட்டது!', 'success'))} className="absolute top-3 right-3 z-20 hidden sm:flex items-center justify-center p-2 bg-white/20 text-white rounded-full shadow-sm border border-white/30 hover:bg-white/30 transition-colors backdrop-blur-sm">
              <Share2 className="w-5 h-5" />
            </button>
          </Card>
          {/* Share button for mobile view */}
          <button onClick={() => navigator.share ? navigator.share({ title: 'இன்றைய திருக்குறள்', text: kuralShareText, url: shareUrl }) : navigator.clipboard.writeText(kuralShareText).then(() => push('திருக்குறள் நகலெடுக்கப்பட்டது!', 'success'))} className="sm:hidden w-full flex items-center justify-center gap-2 p-3 bg-white text-gray-700 rounded-xl shadow-sm border hover:bg-gray-50 transition-colors">
            <Share2 className="w-5 h-5" /> <span className="font-semibold text-sm">பகிர்</span>
          </button>
        </div>
      </Section>

      <Section title="தலைவர் உங்கள் ஊரில்">
        <Card className="relative p-4 flex items-center gap-4 sm:gap-6 text-white shadow-lg overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-500 to-blue-500"></div>
          <img src={wavylines} alt="background" className="absolute inset-0 w-full h-full object-cover opacity-20" />

          <div className="relative z-10 flex items-center gap-4 sm:gap-6 w-full">
            <img src={leaderPhoto} alt="தலைவர் வேல்முருகன்" className="w-28 h-auto flex-shrink-0" />
            <div className="text-center sm:text-left">
              <h3 className="text-lg sm:text-xl font-bold">உங்கள் ஊரில் தலைவர் வேல்முருகன்</h3>
              <p className="text-sm mt-1 opacity-90">வரவிருக்கும் நிகழ்வுகள் மற்றும் சந்திப்புகளுக்கு, இங்கே கிளிக் செய்யவும்.</p>
              <button onClick={() => navigate('/messages')} className="mt-3 px-4 py-2 bg-white text-amber-800 rounded-lg text-sm font-semibold hover:bg-gray-100 transition-colors">மேலும் அறிக</button>
            </div>
          </div>
        </Card>
      </Section>

      <Section title="இன்றைய நிலவரம்">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          {isLoadingStats ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24" />)
          ) : (
            stats.map((s, i) => {
              // Use a more specific icon for referrals
              const icon = s.label === 'என் பரிந்துரைகள்' ? Share2 : Users;
              return <StatCard key={i} icon={icon} label={s.label} value={s.value} sub={s.sub} />
            }
            )
          )}
        </div>
      </Section>

      <Section title="விரைவு நடவடிக்கைகள்">
        <div className="grid grid-cols-3 gap-3 sm:grid-cols-5">
          {actions.map((a) => (
            <button key={a.key} onClick={a.onClick} className="flex flex-col items-center gap-2 bg-white dark:bg-gray-800 rounded-2xl p-2 sm:p-4 shadow-sm border border-gray-100 dark:border-gray-700 active:scale-[0.98] hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
              <div className="p-3 rounded-xl bg-gray-100"><a.icon className="w-6 h-6" /></div>
              <span className="text-sm font-medium text-gray-800 text-center leading-tight">{a.label}</span>
            </button>
          ))}
          <button onClick={() => navigate('/settings')} className="flex flex-col items-center gap-2 bg-white dark:bg-gray-800 rounded-2xl p-2 sm:p-4 shadow-sm border border-gray-100 dark:border-gray-700 active:scale-[0.98] hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
            <div className="p-3 rounded-xl bg-gray-100"><SettingsIcon className="w-6 h-6" /></div>
            <span className="text-sm font-medium text-gray-800 text-center leading-tight">அமைப்புகள்</span>
          </button>
        </div>
      </Section>

      <Section title="மாவட்ட வாரியாக பகுப்பாய்வு">
        <div className="overflow-x-auto">
          <Card className="p-4 min-w-[600px] md:min-w-full">
            <h4 className="font-semibold mb-4">உறுப்பினர்கள் எண்ணிக்கை</h4>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={districtData} margin={{ top: 5, right: 20, left: -10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} interval={0} angle={-30} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip cursor={{ fill: 'rgba(236, 253, 245, 0.5)' }} contentStyle={{ fontSize: 12, borderRadius: '0.75rem' }} />
                <Bar dataKey="members" name="உறுப்பினர்கள்" unit="" fill="#16A34A">
                  {districtData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={['#10B981', '#059669', '#047857', '#065F46', '#064E3B'][index % 5]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
      </Section>

      <Section title="சட்டமன்றத் தொகுதி வாரியாக பகுப்பாய்வு">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {assemblyData.map(d => (
            <Card key={d.name} className="p-4 text-center">
              <div className="text-lg font-bold">{d.members}</div>
              <div className="text-sm text-gray-600">{d.name}</div>
            </Card>
          ))}
        </div>
      </Section>

      <Section title="நேரலை & சமூக ஊடகம்">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <iframe className="w-full aspect-video rounded-2xl shadow-lg" src="https://www.youtube.com/embed/videoseries?list=UULFO5lP_Iro_5j2q9VwG3fA" title="YouTube video player" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
          <Card className="p-4 flex flex-col justify-center gap-3">
            <h4 className="font-bold text-lg mb-3">சமூக ஊடகங்களில் இணையுங்கள்</h4>
            <div className="grid grid-cols-2 gap-3">
              <a href="https://www.facebook.com/tvkpartyofficial" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 p-3 bg-blue-50 text-blue-800 rounded-xl hover:bg-blue-100 transition-colors">
                <Facebook className="w-5 h-5" /> <span className="font-semibold text-sm">Facebook</span>
              </a>
              <a href="https://x.com/tvkpartyoff" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 p-3 bg-sky-50 text-sky-800 rounded-xl hover:bg-sky-100 transition-colors">
                <Twitter className="w-5 h-5" /> <span className="font-semibold text-sm">X (Twitter)</span>
              </a>
              <a href="https://www.instagram.com/tvkpartyofficial" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 p-3 bg-pink-50 text-pink-800 rounded-xl hover:bg-pink-100 transition-colors">
                <Instagram className="w-5 h-5" /> <span className="font-semibold text-sm">Instagram</span>
              </a>
              <a href="https://www.youtube.com/@TVKITWING" target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 p-3 bg-red-50 text-red-800 rounded-xl hover:bg-red-100 transition-colors">
                <Send className="w-5 h-5" /> <span className="font-semibold text-sm">YouTube</span>
              </a>
            </div>
            {/* You can add more links here if needed */}
          </Card>
        </div>
      </Section>

      <Section title="ஒருங்கிணைப்பாளர் தொடர்புகள்">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {isLoadingContacts && Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-36" />)}
          {!isLoadingContacts && Array.isArray(contacts) && contacts.length > 0 ? ( // Added Array.isArray check for safety
            contacts.map((c, i) => (<ContactCard key={i} {...c} />))
          ) : !isLoadingContacts && <EmptyState icon={UsersIcon} title="தொடர்புகள் இல்லை" message="ஒருங்கிணைப்பாளர் தொடர்புகள் இங்கே தோன்றும்." />}
        </div>
      </Section>
    </Shell>
  );
}

// EditMemberModal Component
function EditMemberModal({ member, isOpen, onClose, onSave }) {
  const { push } = useToasts();
  const [formData, setFormData] = useState(member || {});
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(member?.photo || null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setFormData(member || {});
    setPhotoPreview(member?.photo || null);
    setPhotoFile(null);
  }, [member, isOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setPhotoFile(file);
      const reader = new FileReader();
      reader.onload = (event) => {
        setPhotoPreview(event.target?.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.phone) {
      push('பெயர் மற்றும் தொலைபேசி எண் தேவை', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const body = new FormData();

      // Add all form fields
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== null && value !== undefined && key !== 'photo') {
          body.append(key, value);
        }
      });

      // Add photo file if new one selected
      if (photoFile) {
        body.append('photo', photoFile);
      }

      const response = await apiFetch(`/members/${member.id}`, {
        method: 'PUT',
        body: body
      });

      if (response.success) {
        push('உறுப்பினர் வெற்றிகரமாக புதுப்பிக்கப்பட்டார்', 'success');
        onSave(response.member);
        onClose();
      } else {
        push(response.message || 'புதுப்பிக்க முடியவில்லை', 'error');
      }
    } catch (error) {
      console.error('Edit error:', error);
      push('புதுப்பிக்க முடியவில்லை: ' + error.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white dark:bg-gray-800 flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">உறுப்பினரைத் திருத்தவும்</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Photo Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">சுயவிவரப் படம்</label>
            <div className="flex items-center gap-4">
              <div className="w-24 h-24 rounded-xl overflow-hidden bg-gray-100 dark:bg-gray-700 flex items-center justify-center border-2 border-gray-300 dark:border-gray-600">
                {photoPreview ? (
                  <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                ) : (
                  <div className="text-center">
                    <UploadCloud className="w-8 h-8 text-gray-400 mx-auto" />
                    <span className="text-xs text-gray-400 mt-1">படம் இல்லை</span>
                  </div>
                )}
              </div>
              <label className="flex-1 flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-green-500 hover:bg-green-50 dark:hover:bg-gray-700 transition-colors">
                <div className="text-center">
                  <UploadCloud className="w-5 h-5 text-gray-400 mx-auto mb-1" />
                  <span className="text-sm text-gray-600 dark:text-gray-400">படம் தேர்ந்தெடுக்கவும்</span>
                </div>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoChange}
                  className="hidden"
                />
              </label>
            </div>
          </div>

          {/* Row 1: Name & Father/Husband Name */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">பெயர் *</label>
              <input
                type="text"
                name="name"
                value={formData.name || ''}
                onChange={handleChange}
                placeholder="உறுப்பினரின் பெயர்"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">தந்தை/கணவர் பெயர்</label>
              <input
                type="text"
                name="fatherOrHusbandName"
                value={formData.fatherOrHusbandName || ''}
                onChange={handleChange}
                placeholder="தந்தை/கணவர் பெயர்"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-green-500"
              />
            </div>
          </div>

          {/* Row 2: Phone & Email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">தொலைபேசி *</label>
              <input
                type="text"
                name="phone"
                value={formData.phone || ''}
                onChange={handleChange}
                placeholder="9999999999"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">மின்னஞ்சல்</label>
              <input
                type="email"
                name="email"
                value={formData.email || ''}
                onChange={handleChange}
                placeholder="example@email.com"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-green-500"
              />
            </div>
          </div>

          {/* Row 3: Gender & Blood Group */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">பாலினம்</label>
              <select
                name="gender"
                value={formData.gender || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-green-500"
              >
                <option value="ஆண்">ஆண்</option>
                <option value="பெண்">பெண்</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">இரத்த வகை</label>
              <select
                name="bloodGroup"
                value={formData.bloodGroup || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-green-500"
              >
                <option value="">தேர்ந்தெடுக்கவும்</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
              </select>
            </div>
          </div>

          {/* Row 4: Date of Birth & Aadhaar */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">பிறந்த தேதி</label>
              <input
                type="date"
                name="dob"
                value={formData.dob || ''}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">ஆதார்</label>
              <input
                type="text"
                name="aadhaar"
                value={formData.aadhaar || ''}
                onChange={handleChange}
                placeholder="12 இலக்க ஆதார் எண்"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-green-500"
              />
            </div>
          </div>

          {/* Row 5: Voter ID & State */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">வாக்காளர் ID</label>
              <input
                type="text"
                name="voterId"
                value={formData.voterId || ''}
                onChange={handleChange}
                placeholder="வாக்காளர் ID"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">மாநிலம்</label>
              <input
                type="text"
                name="state"
                value={formData.state || ''}
                onChange={handleChange}
                placeholder="மாநிலம்"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-green-500"
              />
            </div>
          </div>

          {/* Row 6: District & Assembly */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">மாவட்டம்</label>
              <input
                type="text"
                name="district"
                value={formData.district || ''}
                onChange={handleChange}
                placeholder="மாவட்டம்"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">சட்டமன்ற</label>
              <input
                type="text"
                name="assembly"
                value={formData.assembly || ''}
                onChange={handleChange}
                placeholder="சட்டமன்ற"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-green-500"
              />
            </div>
          </div>

          {/* Row 7: City & Pincode */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">நகரம்</label>
              <input
                type="text"
                name="city"
                value={formData.city || ''}
                onChange={handleChange}
                placeholder="நகரம்"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">பின்கோடு</label>
              <input
                type="text"
                name="pincode"
                value={formData.pincode || ''}
                onChange={handleChange}
                placeholder="பின்கோடு"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-green-500"
              />
            </div>
          </div>

          {/* Row 8: Address 1 & Address 2 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">முகவரி 1</label>
              <textarea
                name="address1"
                value={formData.address1 || ''}
                onChange={handleChange}
                placeholder="முகவரி 1"
                rows="2"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">முகவரி 2</label>
              <textarea
                name="address2"
                value={formData.address2 || ''}
                onChange={handleChange}
                placeholder="முகவரி 2"
                rows="2"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-green-500"
              />
            </div>
          </div>

          {/* Row 9: Education & Profession */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">கல்வி</label>
              <input
                type="text"
                name="education"
                value={formData.education || ''}
                onChange={handleChange}
                placeholder="கல்வி தகுதி"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">தொழில்</label>
              <input
                type="text"
                name="profession"
                value={formData.profession || ''}
                onChange={handleChange}
                placeholder="தொழில்"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-green-500"
              />
            </div>
          </div>

          {/* Row 10: Contribution */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">பங்களிப்பு</label>
            <input
              type="text"
              name="contribution"
              value={formData.contribution || ''}
              onChange={handleChange}
              placeholder="பங்களிப்பு வகை"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:border-green-500"
            />
          </div>

          {/* Buttons */}
          <div className="pt-4 flex gap-2 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            >
              ரத்துசெய்யவும்
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving}
              className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {isSaving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>சேமிக்கிறது...</span>
                </>
              ) : (
                'சேமிக்கவும்'
              )}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function MembersPage() {
  const { push } = useToasts();
  const { user, role } = useAuth();
  const navigate = useNavigate();

  // Admin-only access check
  useEffect(() => {
    if (user && role !== 'admin') {
      push('இந்த பக்கம் நிர்வாகிகளுக்கு மட்டுமே கிடைக்கும்.', 'error');
      navigate('/dashboard');
    }
  }, [user, role, navigate, push]);

  // If not admin, don't render anything
  if (role !== 'admin') {
    return null;
  }

  const queryClient = useQueryClient();
  const [q, setQ] = useState("");
  const [activeTab, setActiveTab] = useState('all'); // 'all' or 'referrals'
  const [selectedDistrict, setSelectedDistrict] = useState('all');
  const [selectedReferrer, setSelectedReferrer] = useState('all');
  const [editingMember, setEditingMember] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [sendingCredentialsId, setSendingCredentialsId] = useState(null);
  const [showCredentialModal, setShowCredentialModal] = useState(false);
  const [credentialModalData, setCredentialModalData] = useState({ memberId: null, email: '' });

  // Use useInfiniteQuery for pagination
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status,
  } = useInfiniteQuery({
    queryKey: ['members', q, selectedDistrict, activeTab, selectedReferrer],
    queryFn: ({ pageParam = 1 }) => apiFetch(`/members?page=${pageParam}&limit=10&q=${q}&district=${selectedDistrict}`),
    getNextPageParam: (lastPage, allPages) => lastPage.hasMore ? allPages.length + 1 : undefined,
    enabled: !!user,
    initialPageParam: 1,
  });

  const members = data?.pages?.flatMap(page => page.members) || [];
  const totalMembers = data?.pages?.[0]?.total || 0;
  const loading = status === 'pending';

  const allDistricts = [...new Set(members.map(m => m.district).filter(Boolean))];
  const allReferrers = [...new Set(members.map(m => m.referredBy).filter(Boolean))];

  const filtered = members.filter(m => { // This filtering is now client-side on the loaded data
    const matchesSearch = [m.name, m.district, m.voterId, m.phone, m.referredBy].join(' ').toLowerCase().includes(q.toLowerCase());
    if (!matchesSearch) return false;

    if (selectedDistrict !== 'all' && m.district !== selectedDistrict) return false;

    if (activeTab === 'referrals') {
      if (!m.referredBy) return false;
      if (selectedReferrer !== 'all' && m.referredBy !== selectedReferrer) return false;
    }
    return true;
  });

  const handleDeleteMember = async (member) => {
    const confirmed = window.confirm(`உறுப்பினர் "${member.name}"-ஐ நீக்கவா?`);
    if (!confirmed) return;

    try {
      setDeletingId(member.id);
      await apiFetch(`/members/${member.id}`, { method: 'DELETE' });
      push('உறுப்பினர் நீக்கப்பட்டது.', 'success');
      await queryClient.invalidateQueries({ queryKey: ['members'] });
    } catch (err) {
      console.error('Failed to delete member', err);
      push('உறுப்பினரை நீக்க முடியவில்லை. தயவுசெய்து மீண்டும் முயற்சிக்கவும்.', 'error');
    } finally {
      setDeletingId(null);
    }
  };

  const handleSendCredentials = async (member) => {
    setCredentialModalData({ memberId: member.id, email: member.email || '' });
    setShowCredentialModal(true);
  };

  const handleSendCredentialsConfirm = async () => {
    if (!credentialModalData.email) {
      push('மின்னஞ்சல் முகவரி கொடுக்க வேண்டும்.', 'error');
      return;
    }

    try {
      setSendingCredentialsId(credentialModalData.memberId);

      // Get member details to get password
      const member = members.find(m => m.id === credentialModalData.memberId);
      if (!member) {
        push('உறுப்பினர் தகவல் கிடைக்கவில்லை.', 'error');
        return;
      }

      // Fetch member password from backend (stored in members table)
      const memberDataResponse = await apiFetch(`/members/${credentialModalData.memberId}`);
      const memberData = memberDataResponse.data || memberDataResponse;
      const password = memberData?.password || member?.password || 'N/A';

      const response = await apiFetch(`/members/${credentialModalData.memberId}/send-credentials`, {
        method: 'POST',
        body: {
          email: credentialModalData.email,
          phone: member.phone,
          password: password
        }
      });

      if (response.success) {
        push('நற்சான்றிதழ் மின்னஞ்சல் அனுப்பப்பட்டது.', 'success');
      } else {
        push(response.error || 'நற்சான்றிதழ் அனுப்ப முடியவில்லை.', 'error');
      }

      setShowCredentialModal(false);
      setCredentialModalData({ memberId: null, email: '' });
    } catch (err) {
      console.error('Failed to send credentials', err);
      push('நற்சான்றிதழ் அனுப்ப முடியவில்லை. தயவுசெய்து மீண்டும் முயற்சிக்கவும்.', 'error');
    } finally {
      setSendingCredentialsId(null);
    }
  };

  const handleLoadMore = () => {
    if (isFetchingNextPage || !hasNextPage) return;
    fetchNextPage();
  };

  const handleDownloadCSV = () => {
    const headers = ["ID", "பெயர்", "மாவட்டம்", "வாக்காளர் ID", "தொலைபேசி", "பரிந்துரைத்தவர்"];
    const rows = filtered.map(m => [m.id, m.name, m.district, m.voterId, m.phone, m.referredBy || 'N/A']);
    let csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "members_list.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDownloadPDF = () => {
    // Use CSV format instead - more reliable than PDF for Tamil text
    const headers = ["ID", "பெயர்", "மாவட்டம்", "வாக்காளர் ID", "தொலைபேசி", "பரிந்துரைத்தவர்"];
    const rows = filtered.map(m => [m.id, m.name, m.district, m.voterId, m.phone, m.referredBy || 'N/A']);
    let csvContent = "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "members_list.csv");
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const handleDownloadXLSX = () => {
    const headers = ["ID", "பெயர்", "மாவட்டம்", "வாக்காளர் ID", "தொலைபேசி", "பரிந்துரைத்தவர்"];
    const data = filtered.map(m => ({
      ID: m.id, "பெயர்": m.name, "மாவட்டம்": m.district, "வாக்காளர் ID": m.voterId, "தொலைபேசி": m.phone, "பரிந்துரைத்தவர்": m.referredBy || 'N/A'
    }));
    const worksheet = XLSX.utils.json_to_sheet(data, { header: headers });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Members");
    XLSX.utils.sheet_add_aoa(worksheet, [headers], { origin: "A1" }); // Re-write headers to ensure order
    XLSX.writeFile(workbook, "members_list.xlsx");
  };

  return (
    <Shell>
      <Card className="p-3 mb-4 flex flex-col md:flex-row items-center justify-between gap-2 bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800/30">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 rounded-lg">
            <ClipboardList className="w-5 h-5 text-amber-700" />
          </div>
          <div>
            <div className="font-semibold text-amber-900">புதிய உறுப்பினர் அனுமதிகள்</div>
            <div className="text-xs text-amber-700">நிலுவையில் உள்ள கோரிக்கைகளை மதிப்பாய்வு செய்யவும்</div>
          </div>
        </div>
        <button onClick={() => navigate('/approvals')} className="w-full md:w-auto px-4 py-2 bg-amber-500 text-white rounded-xl text-sm font-semibold hover:bg-amber-600 transition-colors">காண்க</button>
      </Card>
      <div className="border-b border-gray-200 dark:border-gray-700 mb-4">
        <div className="flex -mb-px">
          <button onClick={() => setActiveTab('all')} className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === 'all' ? 'border-green-600 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>அனைத்து உறுப்பினர்கள்</button>
          <button onClick={() => setActiveTab('referrals')} className={`px-4 py-2 text-sm font-medium border-b-2 ${activeTab === 'referrals' ? 'border-green-600 text-green-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>என் பரிந்துரைகள்</button>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row items-center gap-2 mb-4">
        <div className="flex items-center bg-white rounded-xl border border-gray-200 px-3 py-2 w-full flex-grow">
          <Search className="w-4 h-4 text-gray-400" /><input value={q} onChange={e => setQ(e.target.value)} placeholder="தேடல்..." className="outline-none px-2 w-full text-sm" />
        </div>
        <div className="w-full sm:w-auto">
          <SelectInput value={selectedDistrict} onChange={e => setSelectedDistrict(e.target.value)} className="w-full"><option value="all">அனைத்து மாவட்டங்கள்</option>{allDistricts.map(d => <option key={d} value={d}>{d}</option>)}</SelectInput>
        </div>
        {activeTab === 'referrals' && allReferrers.length > 0 && (
          <SelectInput value={selectedReferrer} onChange={e => setSelectedReferrer(e.target.value)} className="w-full sm:w-auto"><option value="all">அனைத்து பரிந்துரையாளர்கள்</option>{allReferrers.map(r => <option key={r} value={r}>{r}</option>)}</SelectInput>
        )}
        <button onClick={() => navigate('/members/new')} className="flex items-center justify-center gap-2 px-3 py-2.5 bg-green-600 text-white rounded-xl text-sm whitespace-nowrap w-full sm:w-auto hover:bg-green-700 transition-colors">
          <UserPlus className="w-4 h-4" />
          <span className="sm:inline">புதிய உறுப்பினர்</span>
        </button>
      </div>
      <div className="mt-4 text-sm text-gray-600">
        காண்பிக்கப்படுகிறது: <b>{filtered.length}</b> / <b>{totalMembers}</b> உறுப்பினர்கள்
      </div>
      <div className="my-4 flex flex-wrap items-center gap-2">
        <button onClick={handleDownloadCSV} className="px-3 py-1.5 bg-gray-600 text-white rounded-lg text-xs">CSV</button>
        <button onClick={handleDownloadPDF} className="px-3 py-1.5 bg-red-600 text-white rounded-lg text-xs">PDF</button>
        <button onClick={handleDownloadXLSX} className="px-3 py-1.5 bg-red-700 text-white rounded-lg text-xs">XLSX</button>
      </div>
      <div className="mt-4 space-y-3">
        {loading ? (
          Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-20" />)
        ) : filtered.length > 0 ? (
          filtered.map((m) => (
            <Card key={m.id} className="p-3 flex items-start gap-3">
              <img
                src={m.photo || `https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(m.name)}`}
                alt={m.name}
                className="w-12 h-12 rounded-xl border object-cover"
                onError={(e) => { e.target.src = `https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(m.name)}`; }}
              />
              <div className="flex-1 min-w-0 text-gray-800 dark:text-gray-200">
                <div className="font-medium truncate">{m.name} • <span className="text-gray-600 dark:text-gray-400">{m.district}</span></div>
                <div className="text-xs text-gray-500 truncate">Voter ID: {m.voterId} • {m.phone}{m.referredBy && ` • பரிந்துரை: ${m.referredBy}`}</div>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  {/* <button onClick={() => { setEditingMember(m); setIsEditModalOpen(true); }} className="px-3 py-1 text-xs rounded-lg bg-blue-600 text-white flex items-center gap-1 hover:bg-blue-700 transition-colors"><Edit3 className="w-3 h-3" /> திருத்தவும்</button> */}
                  <button
                    onClick={() => handleSendCredentials(m)}
                    disabled={sendingCredentialsId === m.id}
                    className="px-3 py-1 text-xs rounded-lg bg-purple-600 text-white flex items-center gap-1 hover:bg-purple-700 disabled:opacity-50 transition-colors"
                  >
                    {sendingCredentialsId === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Mail className="w-3 h-3" />}
                    {sendingCredentialsId === m.id ? 'அனுப்புகிறது...' : 'நற்சான்றிதழ்'}
                  </button>
                  <button
                    onClick={() => handleDeleteMember(m)}
                    disabled={deletingId === m.id}
                    className="px-3 py-1 text-xs rounded-lg bg-red-600 text-white flex items-center gap-1 disabled:opacity-50"
                  >
                    {deletingId === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <XCircle className="w-3 h-3" />}
                    {deletingId === m.id ? 'நீக்குகிறது...' : 'நீக்கவும்'}
                  </button>

                  <a
                    href={`https://join.tvkparty.org/idcard?newMemberId=${m.id}&from=creation`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-3 py-1 text-xs rounded-lg bg-blue-700 text-white flex items-center gap-1 hover:bg-blue-800 transition-colors"
                  >
                    <Download className="w-3 h-3" /> ஐடி கார்டு (Direct)
                  </a>

                </div>
              </div>
            </Card>
          ))
        ) : <EmptyState icon={UsersIcon} title="உறுப்பினர்கள் இல்லை" message="புதிய உறுப்பினர்களைச் சேர்க்கத் தொடங்குங்கள்." />}
      </div>
      {hasNextPage && !loading && (
        <div className="mt-6 text-center">
          <button onClick={handleLoadMore} disabled={isFetchingNextPage} className="px-6 py-2 bg-white border border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50">
            {isFetchingNextPage ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'மேலும் ஏற்றவும்'}
          </button>
        </div>
      )}

      <AnimatePresence>
        {isEditModalOpen && (
          <EditMemberModal
            member={editingMember}
            isOpen={isEditModalOpen}
            onClose={() => { setIsEditModalOpen(false); setEditingMember(null); }}
            onSave={(updatedMember) => {
              // Optionally refetch to get latest data
              // queryClient.invalidateQueries({ queryKey: ['members'] });
            }}
          />
        )}
      </AnimatePresence>

      {/* Send Credentials Modal */}
      <AnimatePresence>
        {showCredentialModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowCredentialModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6"
            >
              <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-4">நற்சான்றிதழ் அனுப்பவும்</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">உறுப்பினருக்கு மின்னஞ்சல் முகவரி கொடுங்கள்:</p>

              <input
                type="email"
                value={credentialModalData.email}
                onChange={(e) => setCredentialModalData({ ...credentialModalData, email: e.target.value })}
                placeholder="மின்னஞ்சல் முகவரி"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg mb-4 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              />

              <div className="flex gap-3">
                <button
                  onClick={() => setShowCredentialModal(false)}
                  className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  ரद்து செய்யவும்
                </button>
                <button
                  onClick={handleSendCredentialsConfirm}
                  disabled={sendingCredentialsId === credentialModalData.memberId}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
                >
                  {sendingCredentialsId === credentialModalData.memberId ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      அனுப்புகிறது...
                    </>
                  ) : (
                    <>
                      <Mail className="w-4 h-4" />
                      அனுப்பவும்
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </Shell>
  );
}

// Import our new components
import { StepIndicator, FormCard, InputGroup, Button } from './components/StepIndicator.jsx';
import './components/RegisterForm.css';

function MemberCreatePage() {
  const { push } = useToasts();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [animationDirection, setAnimationDirection] = useState('forward');
  const [form, setForm] = useState({
    photo: null, voterId: "", name: "", fatherOrHusbandName: "", phone: "",
    gender: "ஆண்", dob: "", bloodGroup: "", email: "", state: "தமிழ்நாடு",
    district: "", assembly: "", city: "", address1: "", address2: "", id: "",
    pincode: "", education: "", profession: "", aadhaar: "", contribution: "உறுப்பினராக இருத்தல்",
  });

  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoInfo, setPhotoInfo] = useState(null);

  const [imgSrc, setImgSrc] = useState('');
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [isCropping, setIsCropping] = useState(false);

  const [verification, setVerification] = useState({
    voterId: { verifying: false, verified: false, error: null },
    phone: { verifying: false, verified: false, otpSent: false, otp: "", error: null }
  });
  const [otpCountdown, setOtpCountdown] = useState(0);
  const [validationErrors, setValidationErrors] = useState({
    phone: null,
    email: null,
    pincode: null
  });
  const [otpToken, setOtpToken] = useState(null);

  // Voter ID OCR state
  const [voterIdFront, setVoterIdFront] = useState(null);
  const [voterIdBack, setVoterIdBack] = useState(null);
  const [voterIdFrontPreview, setVoterIdFrontPreview] = useState(null);
  const [voterIdBackPreview, setVoterIdBackPreview] = useState(null);
  const [extractingData, setExtractingData] = useState(false);
  const [extractedData, setExtractedData] = useState(null);


  useEffect(() => {
    if (otpCountdown > 0) {
      const timer = setTimeout(() => setOtpCountdown(otpCountdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpCountdown]);

  useEffect(() => {
    // Reset photo preview/info when form resets
    if (!form.photo) {
      setPhotoPreview(null);
      setPhotoInfo(null);
    }
  }, [form.photo]);

  // Removed voterIdDetails and isFetchingVoterId logic. User must enter details manually.

  const setField = (k) => (e) => {
    let value = e.target ? (e.target.type === 'file' ? e.target.files?.[0] || null : e.target.value) : e;

    // Restrict to numbers only for phone and pincode
    if ((k === 'phone' || k === 'pincode') && value && !/^\d*$/.test(value)) {
      return; // Ignore non-numeric input
    }

    if (k === 'photo' && value) {
      // Capture selected file details for display
      setPhotoInfo({
        name: value.name || 'untitled',
        type: value.type || 'image/*',
        size: value.size || 0
      });

      // Simple preview without crop modal
      const reader = new FileReader();
      reader.onload = (ev) => setPhotoPreview(ev.target?.result || null);
      reader.readAsDataURL(value);

      setForm((f) => ({ ...f, photo: value }));
      setIsCropping(false);
      return;
    } else if (k === 'photo' && !value) {
      setPhotoInfo(null);
      setPhotoPreview(null);
    } else {
      setForm((f) => ({ ...f, [k]: value }));

      // Real-time validation
      if (k === 'phone') {
        if (value && !/^[0-9]{10}$/.test(value)) {
          setValidationErrors(err => ({ ...err, phone: 'சரியான 10 இலக்க தொலைபேசி எண்ணை உள்ளிடவும்' }));
        } else {
          setValidationErrors(err => ({ ...err, phone: null }));
        }
      } else if (k === 'email') {
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          setValidationErrors(err => ({ ...err, email: 'சரியான மின்னஞ்சல் முகவரியை உள்ளிடவும்' }));
        } else {
          setValidationErrors(err => ({ ...err, email: null }));
        }
      } else if (k === 'pincode') {
        if (value && !/^[0-9]{6}$/.test(value)) {
          setValidationErrors(err => ({ ...err, pincode: 'சரியான 6 இலக்க அஞ்சல் குறியீடு தேவை' }));
        } else {
          setValidationErrors(err => ({ ...err, pincode: null }));
        }
      }
    }
  }; function onImageLoad(e) {
    const { width, height } = e.currentTarget;
    setCrop(centerCrop(makeAspectCrop({ unit: '%', width: 90 }, 1, width, height), width, height));
  }

  const handleVoterIdChange = (e) => {
    const value = e.target.value;
    setField('voterId')(e);
    if (/^[A-Z]{3}[0-9]{7}$/i.test(value)) {
      setVerificationState('voterId', 'verified', true);
      setVerificationState('voterId', 'error', null);
    } else {
      setVerificationState('voterId', 'verified', false);
      setVerificationState('voterId', 'error', 'சரியான வாக்காளர் அடையாள அட்டை எண்ணை உள்ளிடவும்');
    }
  };

  const setVerificationField = (key, value) => {
    setVerification(v => ({ ...v, phone: { ...v.phone, [key]: value } }));
  };

  const setVerificationState = (key, subkey, value) => {
    setVerification(v => ({ ...v, [key]: { ...v[key], [subkey]: value } }));
  };

  const nextStep = () => setStep(s => Math.min(5, s + 1));
  const prevStep = () => setStep(s => Math.max(1, s - 1));

  // Handle Voter ID front image selection
  const handleVoterIdFrontChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setVoterIdFront(file);
      const reader = new FileReader();
      reader.onload = (ev) => setVoterIdFrontPreview(ev.target?.result);
      reader.readAsDataURL(file);
    }
  };

  // Handle Voter ID back image selection
  const handleVoterIdBackChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setVoterIdBack(file);
      const reader = new FileReader();
      reader.onload = (ev) => setVoterIdBackPreview(ev.target?.result);
      reader.readAsDataURL(file);
    }
  };

  // Extract data from Voter ID images using Gemini AI
  const handleExtractVoterIdData = async () => {
    if (!voterIdFront) {
      return push('வாக்காளர் அடையாள அட்டை முன்பக்க படம் தேவை', 'error');
    }

    setExtractingData(true);
    try {
      const formData = new FormData();
      formData.append('front', voterIdFront);
      if (voterIdBack) {
        formData.append('back', voterIdBack);
      }

      const result = await apiFetch('/ocr/extract-voterid', {
        method: 'POST',
        body: formData
      });
      console.log('OCR Result:', result);

      if (result.success && result.data) {
        setExtractedData(result.data);

        // Prefill form fields with extracted data
        // Normalize state and district to match dropdown options (case-insensitive)
        const matchedState = findMatchingState(result.data.state);
        const matchedDistrict = findMatchingDistrict(result.data.district, matchedState);

        setForm(f => ({
          ...f,
          name: result.data.name || f.name,
          fatherOrHusbandName: result.data.fatherName || f.fatherOrHusbandName,
          dob: result.data.dob || f.dob,
          gender: result.data.gender || f.gender,
          voterId: result.data.voterId || result.data.aadhaarNumber || f.voterId,
          address1: result.data.address || f.address1,
          district: matchedDistrict || result.data.district || f.district,
          state: matchedState || result.data.state || f.state,
          pincode: result.data.pincode || f.pincode,
          assembly: result.data.constituency || result.data.assembly || f.assembly,
          voterIdFront: result.frontUrl,
          voterIdBack: result.backUrl
        }));

        push('வாக்காளர் அட்டை தகவல்கள் வெற்றிகரமாக பெறப்பட்டன!', 'success');
        nextStep(); // Move to next step after extraction
      } else {
        push(result.error || 'தகவல் பெற இயலவில்லை. மீண்டும் முயற்சிக்கவும்.', 'error');
      }
    } catch (error) {
      console.error('OCR Error:', error);
      push('தகவல் பெறுவதில் பிழை ஏற்பட்டது', 'error');
    } finally {
      setExtractingData(false);
    }
  };

  // Handle Step 2 (Photo + Voter ID) Next button
  const handleStep2PhotoNext = () => {
    if (!form.photo) {
      return push('புகைப்படம் தேவை', 'error');
    }
    if (!voterIdFront) {
      return push('வாக்காளர் அடையாள அட்டை முன்பக்க படம் தேவை', 'error');
    }
    handleExtractVoterIdData();
  };

  const handleStep2Next = () => {

    if (!/^[A-Z]{3}[0-9]{7}$/i.test(form.voterId)) {
      return push('சரியான வாக்காளர் அடையாள அட்டை எண்ணை உள்ளிடவும் (எ.கா: ABC1234567)', 'error');
    }
    // Skip verification check
    // if (!verification.voterId.verified) { return push('வாக்காளர் அடையாள எண்ணைச் சரிபார்க்கவும்', 'error'); } 
    nextStep();
  };

  const handleSendOtp = async () => {
    if (!/^[0-9]{10}$/.test(form.phone)) return push('சரியான 10 இலக்க தொலைபேசி எண் தேவை', 'error');
    setVerificationState('phone', 'verifying', true);
    setVerificationState('phone', 'error', null);
    try {
      // Check if phone already exists
      const checkRes = await apiFetch('/members/check-phone', { method: 'POST', body: { phone: form.phone } });
      if (checkRes.exists) {
        throw new Error('இந்த தொலைபேசி எண் ஏற்கனவே பதிவு செய்யப்பட்டுள்ளது');
      }

      // Send OTP via backend
      const res = await apiFetch('/otp/send', { method: 'POST', body: { phone: form.phone } });
      if (res.success) {
        setOtpToken(res.token);
        setVerificationState('phone', 'otpSent', true);
        setOtpCountdown(60);
        push('OTP அனுப்பப்பட்டது. உங்கள் தொலைபேசியை சரிபார்க்கவும்.', 'success');
      } else {
        throw new Error(res.error || 'OTP அனுப்ப முடியவில்லை');
      }
    } catch (err) {
      console.error('❌ OTP send error:', err);
      let errorMessage = err.message || 'OTP அனுப்ப முடியவில்லை';
      setVerificationState('phone', 'error', errorMessage);
      push(errorMessage, 'error');
    } finally {
      setVerificationState('phone', 'verifying', false);
    }
  };

  const handleResendOtp = async () => {
    setVerificationState('phone', 'otpSent', false);
    setVerificationState('phone', 'otp', '');
    setOtpToken(null);
    await handleSendOtp();
  };

  const handleVerifyOtp = async () => {
    if (!/^[0-9]{6}$/.test(verification.phone.otp)) return push('சரியான 6 இலக்க OTP தேவை', 'error');
    setVerificationState('phone', 'verifying', true);
    setVerificationState('phone', 'error', null);
    try {
      if (!otpToken) {
        throw new Error('OTP session காணவில்லை. மீண்டும் OTP அனுப்பவும்.');
      }

      console.log('🔍 Verifying OTP via backend...');

      // Verify OTP via backend API
      const res = await apiFetch('/otp/verify', {
        method: 'POST',
        body: { otp: verification.phone.otp, token: otpToken }
      });

      if (res.verified) {
        console.log('✅ OTP verified successfully');
        setVerificationState('phone', 'verified', true);
        const tempId = `TVK${Math.floor(10000000000 + Math.random() * 90000000000)}`;
        setForm(f => ({ ...f, id: tempId }));
        push('தொலைபேசி சரிபார்க்கப்பட்டது ✓', 'success');
      } else {
        throw new Error('தவறான OTP குறியீடு');
      }
    } catch (err) {
      console.error('❌ OTP verification error:', err);
      let errorMessage = err.message || 'தவறான OTP';
      setVerificationState('phone', 'error', errorMessage);
      push(errorMessage, 'error');
    } finally {
      setVerificationState('phone', 'verifying', false);
    }
  };

  const handleStep3Next = () => {
    if (!form.name.trim()) return push('பெயர் தேவை', 'error');
    if (!form.fatherOrHusbandName.trim()) return push('தந்தை/கணவர் பெயர் தேவை', 'error');
    if (!verification.phone.verified) return push('தொடர்பு எண்ணைச் சரிபார்க்கவும்', 'error');
    if (!form.dob) return push('பிறந்த தேதி தேவை', 'error');
    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      return push('சரியான மின்னஞ்சல் முகவரியை உள்ளிடவும்', 'error');
    }
    if (!form.district.trim()) return push('மாவட்டம் தேவை', 'error');
    if (!form.assembly.trim()) return push('தொகுதி தேவை', 'error');
    if (!form.address1.trim()) return push('முகவரி தேவை', 'error');
    if (!/^[0-9]{6}$/.test(form.pincode)) return push('சரியான 6 இலக்க அஞ்சல் குறியீடு தேவை', 'error');
    nextStep();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.aadhaar && !/^[0-9]{12}$/.test(form.aadhaar)) {
      return push('சரியான 12 இலக்க ஆதார் எண்ணை உள்ளிடவும்', 'error');
    }
    setLoading(true);
    try {
      const body = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (v !== null && v !== undefined) {
          if (k === 'photo' && v instanceof Blob) {
            // Rename photo file with phone number and extension
            const extension = v.type ? `.${v.type.split('/')[1]}` : '.jpg';
            const renamedFile = new File([v], `${form.phone}${extension}`, { type: v.type });
            body.append(k, renamedFile);
          } else {
            body.append(k, v);
          }
        }
      });
      const responseData = await apiFetch('/members', { method: 'POST', body });
      console.log('MEMBER CREATE RESPONSE:', responseData);
      push('உறுப்பினர் வெற்றிகரமாக சேர்க்கப்பட்டார்!', 'success');
      navigate(`/registration-success?newMemberId=${responseData.id}&from=creation`);
    } catch (err) {
      console.error('MEMBER CREATE ERROR:', err);
      if (err && err.response) {
        // Try to parse backend error details if available
        err.response.json?.().then(data => {
          console.error('API ERROR RESPONSE:', data);
          push('சேர்க்க முடியவில்லை: ' + (data?.message || err.message), 'error');
        }).catch(() => {
          push('சேர்க்க முடியவில்லை: ' + err.message, 'error');
        });
      } else {
        push('சேர்க்க முடியவில்லை: ' + err.message, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const bloodGroups = ["O+", "O-", "A+", "A-", "B+", "B-", "AB+", "AB-"];

  return (
    <Shell>
      <div className="floating-progress">
        <div className="flex items-center gap-4">
          {[1, 2, 3, 4, 5].map((s) => (
            <div
              key={s}
              className={`progress-step ${s === step ? 'active' : s < step ? 'completed' : ''}`}
            >
              {s < step && (
                <CheckIcon className="w-4 h-4 absolute inset-0 m-auto text-white" />
              )}
              {s === step && (
                <div className="absolute inset-0 m-auto w-1.5 h-1.5 bg-white rounded-full" />
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="gradient-border mb-4">
        <Card className="p-4">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-xl font-bold mb-2 bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent"
          >
            உறுப்பினர் சேர்க்கை
          </motion.h1>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="flex items-center gap-3 bg-blue-50 dark:bg-blue-900/20 p-3 rounded-xl"
          >
            <div className="p-2 bg-blue-100 rounded-lg">
              <ShieldCheck className="w-5 h-5 text-blue-700" />
            </div>
            <div>
              <div className="font-semibold text-blue-900">உங்கள் தரவு பாதுகாப்பானது</div>
              <p className="text-xs text-blue-700">உங்கள் தகவல்கள் எங்களுடன் பாதுகாப்பாக உள்ளன. எங்கள் <Link to="/privacy-policy" className="underline">தனியுரிமைக் கொள்கை</Link> மற்றும் <Link to="/terms" className="underline">விதிமுறைகளைப்</Link> படிக்கவும்.</p>
            </div>
          </motion.div>
        </Card>
      </div>

      <Progress step={step} total={5} />
      <form onSubmit={handleSubmit}>
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ x: animationDirection === 'forward' ? 20 : -20, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: animationDirection === 'forward' ? -20 : 20, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {step === 1 && (
              <div className="form-card">
                <motion.h2
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-xl font-bold mb-6 text-gray-800"
                >
                  தொலைபேசி எண் சரிபார்ப்பு
                </motion.h2>
                <AnimatePresence>
                  {form.id && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className="success-animation"
                    >
                      <div className="p-4 mb-6 bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl border border-green-200 dark:border-green-800/30 relative overflow-hidden">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-green-100 dark:bg-green-800/50 rounded-lg">
                            <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400" />
                          </div>
                          <div>
                            <div className="font-medium text-green-800 dark:text-green-200">தற்காலிக உறுப்பினர் எண்</div>
                            <div className="text-lg font-mono text-green-700 dark:text-green-300">{form.id}</div>
                          </div>
                        </div>
                        <div className="absolute top-0 -left-full h-full w-1/2 bg-gradient-to-r from-transparent via-white/50 to-transparent transform -skew-x-12 animate-[glare_4s_ease-in-out_infinite]"></div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-sm"><span className="text-gray-700">தொடர்பு எண்<span className="text-red-500"> *</span></span></label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <input
                          value={form.phone}
                          onChange={setField('phone')}
                          placeholder="9999999999"
                          disabled={verification.phone.otpSent}
                          className={`mt-1 w-full px-3 py-2 rounded-xl border ${validationErrors.phone ? 'border-red-500 focus:ring-red-500' : 'border-gray-300 focus:ring-red-600'} focus:outline-none focus:ring-2 disabled:bg-gray-100`}
                          maxLength={10}
                          type="tel"
                        />
                        {validationErrors.phone && <p className="text-red-600 text-xs mt-1">{validationErrors.phone}</p>}
                      </div>
                      {!verification.phone.verified && !verification.phone.otpSent && <button type="button" onClick={handleSendOtp} disabled={verification.phone.verifying || verification.phone.otpSent || otpCountdown > 0 || validationErrors.phone || !form.phone} className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm whitespace-nowrap disabled:bg-gray-400 h-10">{verification.phone.verifying && !verification.phone.otpSent ? <Loader2 className="w-4 h-4 animate-spin" /> : otpCountdown > 0 ? `${otpCountdown}s` : 'OTP அனுப்பு'}</button>}
                      {verification.phone.otpSent && !verification.phone.verified && (
                        <button
                          type="button"
                          onClick={() => {
                            setVerification(prev => ({
                              ...prev,
                              phone: {
                                ...prev.phone,
                                otpSent: false,
                                otp: '',
                                error: null
                              }
                            }));
                            setOtpCountdown(0);
                          }}
                          className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-xl text-sm whitespace-nowrap h-10 flex items-center gap-1"
                        >
                          <Edit3 className="w-4 h-4" /> திருத்து
                        </button>
                      )}
                      {verification.phone.verified && <ShieldCheck className="w-6 h-6 text-red-600" />}
                    </div>
                  </div>
                  {verification.phone.otpSent && !verification.phone.verified && <div className="sm:col-span-2">
                    <label className="block text-sm"><span className="text-gray-700">OTP<span className="text-red-500"> *</span></span></label>
                    <div className="flex items-start sm:items-end gap-2 flex-col sm:flex-row">
                      <div className="flex-grow">
                        <OtpInput value={verification.phone.otp} onChange={otp => setVerificationField('otp', otp)} />
                      </div>
                      <div className="flex gap-2 w-full sm:w-auto">
                        <button type="button" onClick={handleVerifyOtp} disabled={verification.phone.verifying || verification.phone.otp.length !== 6} className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm whitespace-nowrap disabled:bg-gray-400 h-10 flex-1 sm:flex-none">{verification.phone.verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : 'சரிபார்'}</button>
                        {otpCountdown === 0 && <button type="button" onClick={handleResendOtp} disabled={verification.phone.verifying} className="px-4 py-2 bg-orange-500 text-white rounded-xl text-sm whitespace-nowrap disabled:bg-gray-400 h-10 flex items-center gap-1"><RotateCw className="w-4 h-4" /> மீண்டும்</button>}
                      </div>
                    </div>
                    {otpCountdown > 0 && <p className="text-xs text-gray-500 mt-2">மீண்டும் அனுப்ப {otpCountdown} வினாடிகள் காத்திருக்கவும்</p>}
                  </div>}
                  {verification.phone.error && <div className="text-red-600 text-sm sm:col-span-2 flex items-center gap-1"><AlertCircle className="w-4 h-4" /> {verification.phone.error}</div>}
                </div>
                <div className="flex items-center gap-2 pt-6"></div>
                <button type="button" onClick={nextStep} disabled={!verification.phone.verified} className="px-6 py-2 bg-green-600 text-white rounded-xl text-sm disabled:bg-gray-400 hover:bg-green-700 transition-colors">அடுத்து</button>
              </div>
            )}

            {step === 2 && <Section title="புகைப்படம்">
              <AnimatePresence>
                {false && isCropping && (
                  <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
                  >
                    {/* Cropping modal disabled per request */}
                  </motion.div>
                )}
              </AnimatePresence>
              <AnimatePresence>
                {form.id && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                    <Card className="p-2 mb-4 text-center bg-gray-50 dark:bg-gray-700/50 relative overflow-hidden"><div className="text-xs text-gray-600 dark:text-gray-300">தற்காலிக உறுப்பினர் எண்: <strong className="font-mono">{form.id}</strong></div><div className="absolute top-0 -left-full h-full w-1/2 bg-gradient-to-r from-transparent via-white/50 to-transparent transform -skew-x-12 animate-[glare_4s_ease-in-out_infinite]"></div></Card>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Profile Photo Section */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-4">
                <div className="flex flex-col items-center">
                  <h4 className="font-semibold text-sm mb-3 text-gray-800 dark:text-gray-100">புகைப்படம் <span className="text-red-500">*</span></h4>
                  <label className="cursor-pointer group">
                    <div className="w-36 h-36 rounded-full relative border-4 border-white dark:border-gray-700 shadow-lg bg-gray-200 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
                      {photoPreview ? (
                        <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
                      ) : form.photo ? (
                        <img src={URL.createObjectURL(form.photo)} alt="Preview" className="w-full h-full object-cover" />
                      ) : (
                        <UserIcon className="w-16 h-16 text-gray-400 dark:text-gray-500" />
                      )}
                      <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <UploadCloud className="w-8 h-8 text-white" />
                      </div>
                    </div>
                    <input type="file" accept="image/*" className="hidden" onChange={setField('photo')} />
                  </label>
                  {photoInfo && (
                    <div className="mt-2 text-xs text-gray-600 dark:text-gray-300 text-center">
                      <div>{photoInfo.name}</div>
                      <div>{(photoInfo.size / 1024).toFixed(1)} KB</div>
                    </div>
                  )}
                </div>

                {/* Voter ID Upload Section */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-sm text-center text-gray-800 dark:text-gray-100">வாக்காளர் அடையாள அட்டை படங்கள் <span className="text-red-500">*</span></h4>

                  {/* Voter ID Front */}
                  <div className="flex flex-col items-center">
                    <label className="cursor-pointer group w-full">
                      <div className="relative h-28 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50 flex flex-col items-center justify-center overflow-hidden hover:border-blue-500 transition-colors">
                        {voterIdFrontPreview ? (
                          <img src={voterIdFrontPreview} alt="Voter ID Front" className="w-full h-full object-contain" />
                        ) : (
                          <>
                            <UploadCloud className="w-8 h-8 text-gray-400 mb-1" />
                            <span className="text-xs text-gray-500">முன்பக்கம் (Front)</span>
                          </>
                        )}
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="text-white text-sm">மாற்று</span>
                        </div>
                      </div>
                      <input type="file" accept="image/*" className="hidden" onChange={handleVoterIdFrontChange} />
                    </label>
                    {voterIdFront && <div className="text-xs text-green-600 mt-1 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> பதிவேற்றப்பட்டது</div>}
                  </div>

                  {/* Voter ID Back */}
                  <div className="flex flex-col items-center">
                    <label className="cursor-pointer group w-full">
                      <div className="relative h-28 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700/50 flex flex-col items-center justify-center overflow-hidden hover:border-blue-500 transition-colors">
                        {voterIdBackPreview ? (
                          <img src={voterIdBackPreview} alt="Voter ID Back" className="w-full h-full object-contain" />
                        ) : (
                          <>
                            <UploadCloud className="w-8 h-8 text-gray-400 mb-1" />
                            <span className="text-xs text-gray-500">பின்பக்கம் (Back)</span>
                          </>
                        )}
                        <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <span className="text-white text-sm">மாற்று</span>
                        </div>
                      </div>
                      <input type="file" accept="image/*" className="hidden" onChange={handleVoterIdBackChange} />
                    </label>
                    {voterIdBack && <div className="text-xs text-green-600 mt-1 flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> பதிவேற்றப்பட்டது</div>}
                  </div>

                  <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                    வாக்காளர் அட்டை படங்களிலிருந்து தகவல்கள் தானாக பெறப்படும்
                  </div>
                </div>
              </div>

              {/* Guidelines */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-4 mt-4">
                <Card className="p-3 bg-gray-50 dark:bg-gray-700/50">
                  <h4 className="font-semibold text-sm mb-2 text-gray-800 dark:text-gray-100">புகைப்படத்திற்கான வழிமுறைகள்</h4>
                  <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-300">
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3 h-3 text-green-500 mt-0.5 shrink-0" /><span>தெளிவான, பாஸ்போர்ட் அளவு புகைப்படம்</span></li>
                    <li className="flex items-start gap-2"><XCircle className="w-3 h-3 text-red-500 mt-0.5 shrink-0" /><span>செல்ஃபிகள், தொப்பிகள் தவிர்க்கவும்</span></li>
                  </ul>
                </Card>
                <Card className="p-3 bg-gray-50 dark:bg-gray-700/50">
                  <h4 className="font-semibold text-sm mb-2 text-gray-800 dark:text-gray-100">வாக்காளர் அட்டைக்கான வழிமுறைகள்</h4>
                  <ul className="space-y-1 text-xs text-gray-600 dark:text-gray-300">
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3 h-3 text-green-500 mt-0.5 shrink-0" /><span>தெளிவான, படிக்கக்கூடிய படம்</span></li>
                    <li className="flex items-start gap-2"><CheckCircle2 className="w-3 h-3 text-green-500 mt-0.5 shrink-0" /><span>முழு அட்டையும் தெரிய வேண்டும்</span></li>
                  </ul>
                </Card>
              </div>

              {/* Navigation Buttons */}
              <div className="flex items-center justify-center gap-3 mt-6">
                <button type="button" onClick={prevStep} className="px-4 py-2 bg-gray-200 dark:bg-gray-600 rounded-xl text-sm">முந்தைய</button>
                <button
                  type="button"
                  onClick={handleStep2PhotoNext}
                  disabled={extractingData}
                  className="px-6 py-2 bg-green-600 text-white rounded-xl text-sm hover:bg-green-700 transition-colors disabled:bg-gray-400 flex items-center gap-2"
                >
                  {extractingData ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      தகவல்களை பெறுகிறது...
                    </>
                  ) : (
                    'அடுத்து'
                  )}
                </button>
              </div>
            </Section>}

            {step === 3 && <Section title="வாக்காளர் அடையாள அட்டை">
              <AnimatePresence>
                {form.id && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                    <Card className="p-2 mb-4 text-center bg-gray-50 dark:bg-gray-700/50 relative overflow-hidden"><div className="text-xs text-gray-600 dark:text-gray-300">தற்காலிக உறுப்பினர் எண்: <strong className="font-mono">{form.id}</strong></div><div className="absolute top-0 -left-full h-full w-1/2 bg-gradient-to-r from-transparent via-white/50 to-transparent transform -skew-x-12 animate-[glare_4s_ease-in-out_infinite]"></div></Card>
                  </motion.div>
                )}
              </AnimatePresence>
              <div className="space-y-4 max-w-md mx-auto">
                <label className="block text-sm text-center text-gray-700 dark:text-gray-300">
                  <span>வாக்காளர் அடையாள அட்டை எண்<span className="text-red-500"> *</span></span>
                  <div className="mt-2"><VoterIdInput value={form.voterId} onChange={v => setField('voterId')({ target: { value: v } })} /></div>
                </label>
                {/* Removed voterIdDetails and isFetchingVoterId UI. User must enter details manually. */}
                {verification.voterId.verified && <div className="text-red-600 text-sm flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> வாக்காளர் அடையாள அட்டை சரிபார்க்கப்பட்டது.</div>}
                {verification.voterId.error && <div className="text-red-600 text-sm flex items-center gap-1"><AlertCircle className="w-4 h-4" /> {verification.voterId.error}</div>}
                <div className="flex items-center gap-2 pt-4">
                  <button type="button" onClick={prevStep} className="px-4 py-2 bg-gray-200 rounded-xl text-sm">முந்தைய</button>
                  <button type="button" onClick={handleStep2Next} className="px-6 py-2 bg-green-600 text-white rounded-xl text-sm disabled:bg-gray-400 hover:bg-green-700 transition-colors">அடுத்து</button>
                </div>
              </div>
            </Section>}

            {step === 4 && <Section title="தனிப்பட்ட & தொடர்பு விவரங்கள்">
              <AnimatePresence>
                {form.id && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                    <Card className="p-2 mb-4 text-center bg-gray-50 dark:bg-gray-700/50 relative overflow-hidden"><div className="text-xs text-gray-600 dark:text-gray-300">தற்காலிக உறுப்பினர் எண்: <strong className="font-mono">{form.id}</strong></div><div className="absolute top-0 -left-full h-full w-1/2 bg-gradient-to-r from-transparent via-white/50 to-transparent transform -skew-x-12 animate-[glare_4s_ease-in-out_infinite]"></div></Card>
                  </motion.div>
                )}
              </AnimatePresence>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextInput label="பெயர்" required value={form.name} onChange={setField('name')} />
                <TextInput label="தந்தை/கணவர் பெயர்" required value={form.fatherOrHusbandName} onChange={setField('fatherOrHusbandName')} />
                <SelectInput label="பாலினம்" required value={form.gender} onChange={setField('gender')}><option>ஆண்</option><option>பெண்</option><option>திருநங்கையர்</option></SelectInput>
                <div className="sm:col-span-2">
                  <label className="block text-sm text-gray-700 dark:text-gray-300">
                    <span>பிறந்த தேதி<span className="text-red-500"> *</span></span>
                  </label>
                  <div className="grid grid-cols-3 gap-2 mt-1">
                    <SelectInput aria-label="Day" value={form.dob && !isNaN(new Date(form.dob)) ? new Date(form.dob).getDate() : ''} onChange={e => setField('dob')({ target: { value: `${form.dob ? form.dob.split('-')[0] : new Date().getFullYear()}-${form.dob ? form.dob.split('-')[1] : '01'}-${String(e.target.value).padStart(2, '0')}` } })}>
                      <option value="">நாள்</option>
                      {Array.from({ length: 31 }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                    </SelectInput>
                    <SelectInput aria-label="Month" value={form.dob && !isNaN(new Date(form.dob)) ? new Date(form.dob).getMonth() + 1 : ''} onChange={e => setField('dob')({ target: { value: `${form.dob ? form.dob.split('-')[0] : new Date().getFullYear()}-${String(e.target.value).padStart(2, '0')}-${form.dob ? form.dob.split('-')[2] : '01'}` } })}>
                      <option value="">மாதம்</option>
                      {['ஜனவரி', 'பிப்ரவரி', 'மார்ச்', 'ஏப்ரல்', 'மே', 'ஜூன்', 'ஜூலை', 'ஆகஸ்ட்', 'செப்டம்பர்', 'அக்டோபர்', 'நவம்பர்', 'டிசம்பர்'].map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                    </SelectInput>
                    <SelectInput aria-label="Year" value={form.dob && !isNaN(new Date(form.dob)) ? new Date(form.dob).getFullYear() : ''} onChange={e => setField('dob')({ target: { value: `${e.target.value}-${form.dob ? form.dob.split('-')[1] : '01'}-${form.dob ? form.dob.split('-')[2] : '01'}` } })}>
                      <option value="">வருடம்</option>
                      {Array.from({ length: 100 }, (_, i) => new Date().getFullYear() - 18 - i).map(y => <option key={y} value={y}>{y}</option>)}
                    </SelectInput>
                  </div>
                  {form.dob && (() => {
                    const birthDate = new Date(form.dob);
                    if (isNaN(birthDate.getTime())) return null;
                    let age = new Date().getFullYear() - birthDate.getFullYear();
                    const m = new Date().getMonth() - birthDate.getMonth();
                    if (m < 0 || (m === 0 && new Date().getDate() < birthDate.getDate())) {
                      age--;
                    }
                    return <div className="text-xs text-gray-600 dark:text-gray-400 mt-1.5">வயது: {age}</div>;
                  })()}
                </div>
                <SelectInput label="குருதி வகை" required value={form.bloodGroup} onChange={setField('bloodGroup')}><option value="">தேர்ந்தெடுக்கவும்</option>{bloodGroups.map(bg => <option key={bg} value={bg}>{bg}</option>)}</SelectInput>
                <div className="relative">
                  <TextInput label="மின்னஞ்சல்" type="email" value={form.email} onChange={setField('email')} className={validationErrors.email ? 'border-red-500' : ''} />
                  {validationErrors.email && <p className="text-red-600 text-xs mt-1">{validationErrors.email}</p>}
                  {form.email && !validationErrors.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email) && (
                    <div className="absolute inset-y-0 right-0 top-5 flex items-center pr-3 pointer-events-none">
                      <ShieldCheck className="w-5 h-5 text-green-500" />
                    </div>
                  )}
                </div>
                <SelectInput
                  label="மாநிலம்"
                  required
                  value={form.state}
                  onChange={(e) => { setField('state')(e); setField('district')({ target: { value: '' } }); }}
                >
                  <option value="">மாநிலத்தைத் தேர்ந்தெடுக்கவும்</option>
                  {allStatesData.states.map(s => <option key={s.state} value={s.state}>{s.state}</option>)}
                </SelectInput>
                <SelectInput label="மாவட்டம்" required value={form.district} onChange={setField('district')} disabled={!form.state}>
                  <option value="">மாவட்டத்தைத் தேர்ந்தெடுக்கவும்</option>
                  {(allStatesData.states.find(s => s.state === form.state)?.districts || []).map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </SelectInput>
                <TextInput label="தொகுதி" required value={form.assembly} onChange={setField('assembly')} />
                <TextInput label="நகரம்" value={form.city} onChange={setField('city')} />
                <TextInput label="முகவரி" required value={form.address1} onChange={setField('address1')} className="sm:col-span-2" />
                <div>
                  <TextInput
                    label="அஞ்சல் குறியீடு"
                    required
                    value={form.pincode}
                    onChange={setField('pincode')}
                    placeholder="600001"
                    maxLength={6}
                    type="tel"
                    className={validationErrors.pincode ? 'border-red-500' : ''}
                  />
                  {validationErrors.pincode && <p className="text-red-600 text-xs mt-1">{validationErrors.pincode}</p>}
                </div>
              </div>
              <div className="flex items-center gap-2 pt-6">
                <button type="button" onClick={prevStep} className="px-4 py-2 bg-gray-200 rounded-xl text-sm">முந்தைய</button>
                <button type="button" onClick={handleStep3Next} className="px-6 py-2 bg-green-600 text-white rounded-xl text-sm hover:bg-green-700 transition-colors">அடுத்து</button>
              </div>
            </Section>}

            {step === 5 && <Section title="கூடுதல் விவரங்கள்">
              <AnimatePresence>
                {form.id && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                    <Card className="p-2 mb-4 text-center bg-gray-50 dark:bg-gray-700/50 relative overflow-hidden"><div className="text-xs text-gray-600 dark:text-gray-300">தற்காலிக உறுப்பினர் எண்: <strong className="font-mono">{form.id}</strong></div><div className="absolute top-0 -left-full h-full w-1/2 bg-gradient-to-r from-transparent via-white/50 to-transparent transform -skew-x-12 animate-[glare_4s_ease-in-out_infinite]"></div></Card>
                  </motion.div>
                )}
              </AnimatePresence>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <TextInput label="கல்வி தகுதி" value={form.education} onChange={setField('education')} />
                <TextInput label="தொழில்" value={form.profession} onChange={setField('profession')} />
                <div className="sm:col-span-2">
                  <label className="block text-sm text-center text-gray-700 dark:text-gray-300">
                    <span>ஆதார் எண்</span>
                    <div className="mt-2"><AadhaarInput value={form.aadhaar} onChange={v => setField('aadhaar')({ target: { value: v } })} /></div>
                  </label>
                </div>
                <SelectInput label="உங்களின் பங்களிப்பு" value={form.contribution} onChange={setField('contribution')}>
                  <option>களப்பணி</option><option>சமூகவலைத்தளம்</option><option>வடிவைமைப்பு</option><option>படத்தொகுப்பு</option><option>உறுப்பினராக இருத்தல்</option>
                </SelectInput>
              </div>
              {/* <div className="flex items-center gap-2 pt-6">
                <button type="button" onClick={prevStep} className="px-4 py-2 bg-gray-200 rounded-xl text-sm">முந்தைய</button>
                <button type="button" onClick={nextStep} className="px-6 py-2 bg-green-600 text-white rounded-xl text-sm hover:bg-green-700 transition-colors">அடுத்து</button>
              </div> */}
            </Section>}

            {step === 5 && <Section title="உறுதிப்படுத்தல்">
              <AnimatePresence>
                {form.id && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}>
                    <Card className="p-2 mb-4 text-center bg-gray-50 dark:bg-gray-700/50 relative overflow-hidden"><div className="text-xs text-gray-600 dark:text-gray-300">தற்காலிக உறுப்பினர் எண்: <strong className="font-mono">{form.id}</strong></div><div className="absolute top-0 -left-full h-full w-1/2 bg-gradient-to-r from-transparent via-white/50 to-transparent transform -skew-x-12 animate-[glare_4s_ease-in-out_infinite]"></div></Card>
                  </motion.div>
                )}
              </AnimatePresence>
              <Card className="p-4 space-y-3">
                <h4 className="text-lg font-semibold border-b pb-2 mb-3">உங்கள் விவரங்களைச் சரிபார்க்கவும்</h4>
                {form.photo && (
                  <div className="flex justify-center mb-4">
                    <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-blue-500">
                      <img src={URL.createObjectURL(form.photo)} alt="Profile" className="w-full h-full object-cover" />
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  <div className="text-gray-500">பெயர்:</div><div className="font-medium">{form.name || '-'}</div>
                  <div className="text-gray-500">தந்தை/கணவர் பெயர்:</div><div className="font-medium">{form.fatherOrHusbandName || '-'}</div>
                  <div className="text-gray-500">தொலைபேசி:</div><div className="font-medium">{form.phone || '-'}</div>
                  <div className="text-gray-500">பாலினம்:</div><div className="font-medium">{form.gender || '-'}</div>
                  <div className="text-gray-500">பிறந்த தேதி:</div><div className="font-medium">{form.dob || '-'}</div>
                  <div className="text-gray-500">குருதி வகை:</div><div className="font-medium">{form.bloodGroup || '-'}</div>
                  <div className="text-gray-500">வாக்காளர் ID:</div><div className="font-medium">{form.voterId || '-'}</div>
                  <div className="text-gray-500">மின்னஞ்சல்:</div><div className="font-medium">{form.email || '-'}</div>
                  <div className="text-gray-500">மாநிலம்:</div><div className="font-medium">{form.state || '-'}</div>
                  <div className="text-gray-500">மாவட்டம்:</div><div className="font-medium">{form.district || '-'}</div>
                  <div className="text-gray-500">தொகுதி:</div><div className="font-medium">{form.assembly || '-'}</div>
                  <div className="text-gray-500">நகரம்:</div><div className="font-medium">{form.city || '-'}</div>
                  <div className="text-gray-500">முகவரி:</div><div className="font-medium">{form.address1 || '-'}</div>
                  <div className="text-gray-500">அஞ்சல் குறியீடு:</div><div className="font-medium">{form.pincode || '-'}</div>
                  <div className="text-gray-500">கல்வி:</div><div className="font-medium">{form.education || '-'}</div>
                  <div className="text-gray-500">தொழில்:</div><div className="font-medium">{form.profession || '-'}</div>
                  <div className="text-gray-500">ஆதார்:</div><div className="font-medium">{form.aadhaar || '-'}</div>
                  <div className="text-gray-500">பங்களிப்பு:</div><div className="font-medium">{form.contribution || '-'}</div>
                </div>
                <p className="text-xs text-gray-500 pt-2">மேலே உள்ள விவரங்கள் சரியானவை என்பதை உறுதிப்படுத்தவும். சமர்ப்பித்த பிறகு மாற்றங்கள் செய்வது கடினம்.</p>
              </Card>
              <div className="flex items-center gap-2 pt-6">
                <button type="button" onClick={prevStep} className="px-4 py-2 bg-gray-200 rounded-xl text-sm">முந்தைய</button>
                <button type="submit" disabled={loading} className="px-6 py-2 bg-green-600 text-white rounded-xl text-sm hover:bg-green-700 transition-colors">{loading ? 'சமர்ப்பிக்கிறது...' : 'சமர்ப்பி'}</button>
              </div>
            </Section>}
          </motion.div>
        </AnimatePresence>
      </form>
    </Shell >
  );
}

function ViewUserModal({ user, onClose }) {
  if (!user) return null;

  const fields = [
    { label: "Name", value: user.name },
    { label: "Father/Husband Name", value: user.fatherOrHusbandName },
    { label: "Phone", value: user.phone },
    { label: "Email", value: user.email },
    { label: "Voter ID", value: user.voterId },
    { label: "Aadhaar", value: user.aadhaar },
    { label: "Gender", value: user.gender },
    { label: "Date of Birth", value: user.dob },
    { label: "Blood Group", value: user.bloodGroup },
    { label: "Address", value: `${user.address1}, ${user.city}, ${user.district}, ${user.state} - ${user.pincode}` },
    { label: "Assembly", value: user.assembly },
    { label: "Education", value: user.education },
    { label: "Profession", value: user.profession },
    { label: "Contribution", value: user.contribution },
  ];

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 10 }} transition={{ duration: 0.2, ease: "easeOut" }} className="relative z-10 w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <Card className="relative p-5 max-h-[90vh] overflow-y-auto">
          <button onClick={onClose} className="absolute top-3 right-3 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700">
            <X className="w-5 h-5 text-gray-500" />
          </button>
          <div className="flex items-center gap-4 mb-4">
            <img src={normalizeMinioUrl(user.photo) || `https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(user.name)}`} alt={user.name} className="w-24 h-24 rounded-full object-cover border-2 border-white shadow-md" />
            <div>
              <h3 className="text-xl font-bold">{user.name}</h3>
              <p className="text-sm text-gray-500">{user.district}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
            {fields.map(field => (
              <React.Fragment key={field.label}>
                <div className="font-semibold text-gray-600 dark:text-gray-400">{field.label}:</div>
                <div className="text-gray-800 dark:text-gray-200">{field.value || '-'}</div>
              </React.Fragment>
            ))}
          </div>
        </Card>
      </motion.div>
    </motion.div>
  );
}

function ApprovalsPage() {
  const { user, role } = useAuth();
  const { push } = useToasts();
  const canApprove = RBAC[role]?.canApprove;
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    setLoading(true);
    apiFetch('/approvals')
      .then(setList)
      .catch(err => push('Failed to load approvals: ' + err.message, 'error'))
      .finally(() => setLoading(false));
  }, [push]);

  const approve = async (idx) => { const item = list[idx]; try { await apiFetch(`/approvals/${item.id}/approve`, { method: 'POST' }); setList(prev => prev.filter((_, i) => i !== idx)); push('Approved', 'success'); } catch (e) { push('Approve failed: ' + e.message, 'error'); } };
  const reject = async (idx) => { const item = list[idx]; try { await apiFetch(`/approvals/${item.id}/reject`, { method: 'POST' }); setList(prev => prev.filter((_, i) => i !== idx)); push('Rejected', 'warn'); } catch (e) { push('Reject failed: ' + e.message, 'error'); } };
  return (
    <Shell>
      {!canApprove && (<Card className="p-3 mb-3 border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20"><div className="text-sm text-amber-800 dark:text-amber-100">இந்தப் பக்கத்தை அணுக <b>அனுமதி</b> தேவை. உங்கள் role: <b>{role}</b></div></Card>)}
      <Section title="உறுப்பினர் அனுமதிகள்">
        <div className="space-y-3">
          {loading && Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24" />)}
          {!loading && list.length > 0 && list.map((m, idx) => (
            <Card key={m.id} className="p-3 flex flex-col sm:flex-row items-start gap-3">
              <img src={m.photo || `https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(m.name)}`} alt={m.name} className="w-12 h-12 rounded-xl border object-cover" />
              <div className="flex-1 min-w-0">
                <div className="font-medium truncate text-gray-800 dark:text-gray-100">{m.name} • {m.district}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">Voter ID: {m.voterId} • Phone: {m.phone}</div>
                {canApprove && (
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <button onClick={() => approve(idx)} className="px-3 py-1.5 text-xs rounded-lg bg-green-600 text-white flex items-center gap-1"><CheckCircle2 className="w-4 h-4" /> அனுமதி</button>
                    <button onClick={() => reject(idx)} className="px-3 py-1.5 text-xs rounded-lg bg-red-600 text-white flex items-center gap-1"><XCircle className="w-4 h-4" /> நிராகரி</button>
                    <button onClick={() => setSelectedUser(m)} className="px-3 py-1.5 text-xs rounded-lg bg-blue-600 text-white flex items-center gap-1"><Eye className="w-4 h-4" /> View</button>
                    <button onClick={() => push('Edit feature coming soon!', 'info')} className="px-3 py-1.5 text-xs rounded-lg bg-yellow-500 text-white flex items-center gap-1"><Edit3 className="w-4 h-4" /> Edit</button>
                  </div>
                )}
              </div>
            </Card>
          ))}
          {!loading && list.length === 0 && (<EmptyState icon={FilePlus} title="அனுமதிக்க கோரிக்கைகள் இல்லை" message="புதிய உறுப்பினர் சேர்க்கைக்கான கோரிக்கைகள் இங்கே தோன்றும்." />)}
        </div>
      </Section>
      <AnimatePresence>
        {selectedUser && <ViewUserModal user={selectedUser} onClose={() => setSelectedUser(null)} />}
      </AnimatePresence>
    </Shell>
  );
}

function MessagesPage() {
  const { role } = useAuth();
  const { push } = useToasts();
  const [notes, setNotes] = useState([]);
  const [selectedNote, setSelectedNote] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const canCreate = ['admin', 'state'].includes(role);

  useEffect(() => {
    setLoading(true);
    apiFetch('/messages')
      .then(response => {
        // Handle paginated response: { data: [...], pagination: {...} }
        const messages = response?.data || response || [];
        setNotes(Array.isArray(messages) ? messages : []);
      })
      .catch(err => push('Failed to load messages: ' + err.message, 'error'))
      .finally(() => setLoading(false));
  }, [push]);

  const handleShare = (note) => {
    if (!note) return;
    const shareText = `Message: ${note.title}\n\n${note.content}`;
    const shareUrl = window.location.href;

    if (navigator.share) {
      navigator.share({
        title: note.title,
        text: shareText,
        url: shareUrl,
      }).catch((error) => console.log('Error sharing', error));
    } else {
      navigator.clipboard.writeText(shareText)
        .then(() => push('Message details copied!', 'success'))
        .catch(() => push('Failed to copy details.', 'error'));
    }
  };

  const filteredNotes = notes.filter(n =>
    n.title.toLowerCase().includes(q.toLowerCase()) ||
    n.content.toLowerCase().includes(q.toLowerCase())
  );

  return (
    <Shell>
      <Section title="அறிவிப்புகள்">
        <div className="flex flex-col sm:flex-row items-center gap-2 mb-4">
          <div className="flex items-center bg-white dark:bg-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 px-3 py-2 w-full flex-grow">
            <Search className="w-4 h-4 text-gray-400" />
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="அறிவிப்புகளில் தேடவும்..." className="outline-none px-2 w-full text-sm" />
          </div>
          {canCreate && (
            <button onClick={() => setIsCreateModalOpen(true)} className="w-full sm:w-auto px-4 py-2.5 bg-green-600 text-white rounded-xl text-sm whitespace-nowrap">+ புதிய அறிவிப்பு</button>
          )}
        </div>
        <div className="space-y-3">
          {loading ? (
            Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16" />)
          ) : filteredNotes.length > 0 ? (
            filteredNotes.map((n) => (
              <NotificationCard key={n.id} title={n.title} time={n.time} onClick={() => setSelectedNote(n)} />
            ))
          ) : (
            <EmptyState icon={FileText} title={q ? 'தேடல் முடிவுகள் இல்லை' : 'அறிவிப்புகள் இல்லை'} message={q ? 'வேறு தேடல் சொல்லை முயற்சிக்கவும்.' : 'புதிய அறிவிப்புகள் விரைவில் சேர்க்கப்படும்.'} />
          )}
        </div>
      </Section>

      <AnimatePresence>
        {selectedNote && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={() => setSelectedNote(null)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 10 }}
              transition={{ duration: 0.2, ease: "easeOut" }}
              className="relative z-10 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <Card className="relative p-5 dark:text-white">
                <button onClick={() => setSelectedNote(null)} className="absolute top-3 right-3 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
                <h3 className="text-lg font-semibold mb-2 ">{selectedNote.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{selectedNote.time}</p>
                <div className="max-h-[60vh] overflow-y-auto pr-2 text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{selectedNote.content}</div>
                <div className="flex justify-end mt-4">
                  <button onClick={() => handleShare(selectedNote)} className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm flex items-center gap-2">
                    <Share2 className="w-4 h-4" />
                    <span>Share</span>
                  </button>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isCreateModalOpen && (
          <CreateAnnouncementModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            onSuccess={(newNote) => {
              setNotes(prev => [newNote, ...prev]);
              setIsCreateModalOpen(false);
            }}
          />
        )}
      </AnimatePresence>
    </Shell>
  );
}


function CreateAnnouncementModal({ isOpen, onClose, onSuccess }) {
  const { push } = useToasts();
  const [form, setForm] = useState({ title: '', content: '', date: new Date().toISOString().split('T')[0], district: '', assembly: '', photos: Array(4).fill(null) });
  const [loading, setLoading] = useState(false);

  const setField = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handlePhotoChange = (index, e) => {
    const file = e.target.files?.[0];
    if (file) {
      const newPhotos = [...form.photos];
      newPhotos[index] = file;
      setForm(f => ({ ...f, photos: newPhotos }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      return push('தலைப்பு மற்றும் உள்ளடக்கம் தேவை.', 'error');
    }
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (key === 'photos') {
          value.forEach(p => { if (p) formData.append('photos', p) });
        } else if (value) {
          formData.append(key, value);
        }
      });

      const newAnnouncement = await apiFetch('/messages', { method: 'POST', body: formData });
      push('அறிவிப்பு வெற்றிகரமாக உருவாக்கப்பட்டது!', 'success');
      onSuccess(newAnnouncement);
    } catch (err) {
      push('அறிவிப்பை உருவாக்க முடியவில்லை: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 10 }} transition={{ duration: 0.2, ease: "easeOut" }} className="relative z-10 w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <Card className="p-5 max-h-[90vh] overflow-y-auto">
          <h3 className="text-lg font-semibold mb-4">புதிய அறிவிப்பு உருவாக்கு</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <TextInput label="தலைப்பு" required value={form.title} onChange={setField('title')} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput label="தேதி" type="date" required value={form.date} onChange={setField('date')} />
              <TextInput label="மாவட்டம்" required value={form.district} onChange={setField('district')} />
            </div>
            <TextInput label="சட்டமன்றத் தொகுதி" required value={form.assembly} onChange={setField('assembly')} />
            <label className="block text-sm">
              <span className="text-gray-700">உள்ளடக்கம்<span className="text-red-500"> *</span></span>
              <textarea value={form.content} onChange={setField('content')} rows="6" className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-600"></textarea>
            </label>
            <div>
              <span className="text-sm text-gray-700">புகைப்படங்கள் (4 வரை)</span>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3">
                {form.photos.map((photo, index) => (
                  <label key={index} className="cursor-pointer aspect-square bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-green-500 hover:text-green-600 transition-colors">
                    {photo ? (
                      <img src={URL.createObjectURL(photo)} alt={`Preview ${index + 1}`} className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <div className="text-center">
                        <UploadCloud className="w-8 h-8 mx-auto" />
                        <span className="text-xs mt-1 block">புகைப்படம் {index + 1}</span>
                      </div>
                    )}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoChange(index, e)} />
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end items-center gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded-xl text-sm">ரத்து</button>
              <button type="submit" disabled={loading} className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm disabled:bg-green-400">{loading ? 'சமர்ப்பிக்கிறது...' : 'சமர்ப்பி'}</button>
            </div>
          </form>
        </Card>
      </motion.div>
    </motion.div>
  );
}

function MotionBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden bg-gray-50 dark:bg-gray-900 -z-10">
      <AnimatePresence>
        <motion.div
          key="ball-1"
          initial={{ y: "100%", x: "0%", scale: 0, opacity: 0 }}
          animate={{ y: "-20%", x: "20%", scale: 1, opacity: 0.1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 20, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
          className="absolute bottom-0 left-0 w-96 h-96 bg-green-300 rounded-full filter blur-3xl"
        />
        <motion.div
          key="ball-2"
          initial={{ y: "0%", x: "100%", scale: 0, opacity: 0 }}
          animate={{ y: "50%", x: "70%", scale: 1.2, opacity: 0.1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 25, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
          className="absolute top-0 right-0 w-80 h-80 bg-blue-300 rounded-full filter blur-3xl"
        />
        <motion.div
          key="ball-3"
          initial={{ y: "100%", x: "100%", scale: 0, opacity: 0 }}
          animate={{ y: "20%", x: "10%", scale: 0.8, opacity: 0.15 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 30, repeat: Infinity, repeatType: "reverse", ease: "easeInOut" }}
          className="absolute bottom-0 right-0 w-72 h-72 bg-yellow-300 rounded-full filter blur-3xl"
        />
      </AnimatePresence>
    </div>
  );
}

function IdCardPreview({ user, isFlipped, frontRef, backRef, cardDesign = 'thalaivar' }) {
  const profileUrl = `https://join.tvkparty.org/member/${user.id}`;
  const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=80x80&data=${encodeURIComponent(profileUrl)}`;

  const CardFront = () => (
    <Card className={`w-full h-full overflow-hidden flex flex-col relative ${cardDesign}-card`}>
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src={idCardFront}
          alt="Card Background"
          className="w-full h-full object-cover opacity-90"
        />
      </div>

      {/* Content overlay - Uses percentage-based positioning for responsiveness */}
      <div className="relative z-10 flex flex-col flex-grow w-full h-full">
        {/* Body - Main content container */}
        <div className="absolute inset-0 flex">
          {/* Left side: User Details - positioned with percentages */}
          <div className="absolute flex flex-col gap-[2%]" style={{ top: '32%', left: '28%', width: '42%' }}>
            {/* Name */}
            <div>
              <div className="text-[1.8vw] sm:text-[8px] md:text-[10px] text-gray-600 font-medium leading-none">பெயர்</div>
              <div className="font-bold text-[3.5vw] sm:text-sm md:text-lg leading-tight">{user.name}</div>
            </div>

            {/* ID */}
            <div>
              <div className="text-[1.8vw] sm:text-[8px] md:text-[10px] text-gray-600 font-medium leading-none">உறுப்பினர் எண்</div>
              <div className="text-[3vw] sm:text-xs md:text-base font-semibold leading-tight">{user.id}</div>
            </div>

            {/* District */}
            <div>
              <div className="text-[1.8vw] sm:text-[8px] md:text-[10px] text-gray-600 font-medium leading-none">மாவட்டம்</div>
              <div className="text-[3vw] sm:text-xs md:text-base font-semibold leading-tight">{user.district}</div>
            </div>

            {/* Assembly */}
            <div>
              <div className="text-[1.8vw] sm:text-[8px] md:text-[10px] text-gray-600 font-medium leading-none">சட்டமன்றத் தொகுதி</div>
              <div className="text-[3vw] sm:text-xs md:text-base font-semibold leading-tight">{user.assembly}</div>
            </div>
          </div>

          {/* Issue Date - Positioned at bottom left */}
          <div className="absolute" style={{ bottom: '8%', left: '4%' }}>
            <div className="text-[1.8vw] sm:text-[8px] md:text-[10px] text-gray-600 font-medium">வழங்கிய நாள்</div>
            <div className="text-[3vw] sm:text-xs md:text-base font-semibold">{new Date().toLocaleDateString('ta-IN')}</div>
          </div>

          {/* Right side: User Photo and QR */}
          <div className="absolute flex flex-col items-center" style={{ top: '28%', right: '4%', gap: '4%' }}>
            {/* User Photo */}
            <div className="relative" style={{ width: '16%', minWidth: '50px' }}>
              <img
                src={normalizeMinioUrl(user.photo) || `https://api.dicebear.com/8.x/initials/svg?seed=${encodeURIComponent(user.name)}`}
                alt="Profile"
                className="w-full aspect-[5/7] border-2 border-white shadow-lg object-cover bg-gray-100 rounded-lg"
              />
              <div className="absolute -top-1 -left-1 bg-green-600 text-white text-[1.8vw] sm:text-[8px] md:text-[10px] font-bold px-1 py-0.5 rounded">
                {user.bloodGroup || 'O+'}
              </div>
            </div>

            {/* QR Code with Logo Overlay */}
            <div className="relative" style={{ width: '13%', minWidth: '40px', aspectRatio: '1' }}>
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=64x64&data=${encodeURIComponent(`https://join.tvkparty.org/member/${user.id}`)}&bgcolor=FFFFFF`}
                alt="Member QR"
                className="w-full h-full"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <img
                  src={partyLogo}
                  alt="Party Logo"
                  className="w-[40%] h-[40%] opacity-90"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );

  const CardBack = () => (
    <Card className={`relative w-full h-full overflow-hidden flex flex-col ${cardDesign}-card`}>
      {/* Background Image */}
      <div className="absolute inset-0 w-full h-full">
        <img
          src={idCardBack}
          alt="Card Background"
          className="w-full h-full object-cover opacity-90"
        />
      </div>

      {/* Content overlay with percentage-based positioning */}
      <div className="relative z-10 w-full h-full">
        {/* Left Column - Details */}
        <div className="absolute flex flex-col gap-[3%]" style={{ top: '18%', left: '4%', width: '55%' }}>
          {/* Date of Birth with Age */}
          <div>
            <div className="text-[1.6vw] sm:text-[7px] md:text-[8px] text-gray-600 font-medium leading-none">பிறந்த தேதி</div>
            <div className="text-[2.4vw] sm:text-[10px] md:text-xs font-semibold leading-tight" style={{ fontFamily: 'Latha, sans-serif' }}>
              {new Date(user.dob || '1990-01-15').toLocaleDateString('ta-IN')}
              <span className="text-[1.8vw] sm:text-[8px] md:text-[10px] text-gray-600 ml-1">
                (வயது: {new Date().getFullYear() - new Date(user.dob || '1990-01-15').getFullYear()})
              </span>
            </div>
          </div>

          {/* Blood Group */}
          <div>
            <div className="text-[1.6vw] sm:text-[7px] md:text-[8px] text-gray-600 font-medium leading-none">குருதி வகை</div>
            <div className="text-[2.4vw] sm:text-[10px] md:text-xs font-semibold leading-tight" style={{ fontFamily: 'Latha, sans-serif' }}>{user.bloodGroup || 'தெரியவில்லை'}</div>
          </div>

          {/* Address */}
          <div>
            <div className="text-[1.6vw] sm:text-[7px] md:text-[8px] text-gray-600 font-medium leading-none">முகவரி</div>
            <div className="text-[2.4vw] sm:text-[10px] md:text-xs font-semibold leading-tight" style={{ fontFamily: 'Latha, sans-serif' }}>
              {user.address1 || '123, அண்ணா சாலை, தேனாம்பேட்டை'}
              <br />
              {user.city || 'சென்னை'}, {user.pincode || '600018'}
            </div>
          </div>
        </div>
      </div>
    </Card>
  );

  return (
    <div className="w-full max-w-[507px] aspect-[1013/638] mx-auto px-2 sm:px-0" style={{ perspective: '1000px' }}>
      <motion.div
        className="relative w-full h-full"
        style={{ transformStyle: 'preserve-3d' }}
        animate={{ rotateY: isFlipped ? 180 : 0 }}
        transition={{ duration: 0.6 }}
      >
        <div ref={frontRef} className="absolute w-full h-full" style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden' }}>
          <CardFront />
        </div>
        <div ref={backRef} className="absolute w-full h-full" style={{ backfaceVisibility: 'hidden', WebkitBackfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}>
          <CardBack />
        </div>
      </motion.div>
    </div>
  );
}

function IdCardModal({ user, isOpen, onClose }) {
  const [isFlipped, setIsFlipped] = useState(false);
  const frontRef = React.useRef(null);
  const backRef = React.useRef(null);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="relative w-full max-w-lg"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={onClose} className="absolute -top-3 -right-3 z-10 bg-white rounded-full p-1.5 shadow-lg"><X className="w-5 h-5 text-gray-700" /></button>
            <IdCardPreview user={user} isFlipped={isFlipped} frontRef={frontRef} backRef={backRef} />
            <div className="mt-4 flex justify-center">
              <button onClick={() => setIsFlipped(f => !f)} className="px-4 py-2 bg-white text-black rounded-xl text-sm flex items-center gap-2 shadow-lg">
                <RotateCw className="w-4 h-4" />
                <span>{isFlipped ? 'முன்பக்கம் காட்டு' : 'பின்பக்கம் காட்டு'}</span>
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function CreateNewsModal({ isOpen, onClose, onSuccess }) {
  const { push } = useToasts();
  const [form, setForm] = useState({ title: '', content: '', date: new Date().toISOString().split('T')[0], district: '', assembly: '', photos: Array(4).fill(null) });
  const [loading, setLoading] = useState(false);

  const setField = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handlePhotoChange = (index, e) => {
    const file = e.target.files?.[0];
    if (file) {
      const newPhotos = [...form.photos];
      newPhotos[index] = file;
      setForm(f => ({ ...f, photos: newPhotos }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim()) {
      return push('தலைப்பு மற்றும் உள்ளடக்கம் தேவை.', 'error');
    }
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (key === 'photos') {
          value.forEach(p => { if (p) formData.append('photos', p) });
        } else {
          formData.append(key, value);
        }
      });

      const newNews = await apiFetch('/news', { method: 'POST', body: formData });
      push('செய்தி வெற்றிகரமாக உருவாக்கப்பட்டது!', 'success');
      onSuccess(newNews);
    } catch (err) {
      push('செய்தியை உருவாக்க முடியவில்லை: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 10 }} transition={{ duration: 0.2, ease: "easeOut" }} className="relative z-10 w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <Card className="p-5 max-h-[90vh] overflow-y-auto">
          <h3 className="text-lg font-semibold mb-4">புதிய செய்தி உருவாக்கு</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <TextInput label="தலைப்பு" required value={form.title} onChange={setField('title')} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput label="தேதி" type="date" required value={form.date} onChange={setField('date')} />
              <TextInput label="மாவட்டம்" required value={form.district} onChange={setField('district')} />
            </div>
            <TextInput label="சட்டமன்றத் தொகுதி" required value={form.assembly} onChange={setField('assembly')} />
            <label className="block text-sm">
              <span className="text-gray-700">உள்ளடக்கம்<span className="text-red-500"> *</span></span>
              <textarea value={form.content} onChange={setField('content')} rows="5" className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-600"></textarea>
            </label>
            <div>
              <span className="text-sm text-gray-700">புகைப்படங்கள் (4 வரை)</span>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3">
                {form.photos.map((photo, index) => (
                  <label key={index} className="cursor-pointer aspect-square bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-green-500 hover:text-green-600 transition-colors">
                    {photo ? (
                      <img src={URL.createObjectURL(photo)} alt={`Preview ${index + 1}`} className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <div className="text-center">
                        <UploadCloud className="w-8 h-8 mx-auto" />
                        <span className="text-xs mt-1 block">புகைப்படம் {index + 1}</span>
                      </div>
                    )}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoChange(index, e)} />
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end items-center gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded-xl text-sm">ரத்து</button>
              <button type="submit" disabled={loading} className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm disabled:bg-green-400">{loading ? 'சமர்ப்பிக்கிறது...' : 'சமர்ப்பி'}</button>
            </div>
          </form>
        </Card>
      </motion.div>
    </motion.div>
  );
}

function CreateEventModal({ isOpen, onClose, onSuccess }) {
  const { push } = useToasts();
  const [form, setForm] = useState({ title: '', content: '', when: new Date().toISOString().split('T')[0], where: '', assembly: '', photos: Array(4).fill(null) });
  const [loading, setLoading] = useState(false);

  const setField = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

  const handlePhotoChange = (index, e) => {
    const file = e.target.files?.[0];
    if (file) {
      const newPhotos = [...form.photos];
      newPhotos[index] = file;
      setForm(f => ({ ...f, photos: newPhotos }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.content.trim() || !form.when || !form.where) {
      return push('அனைத்து புலங்களும் தேவை.', 'error');
    }
    setLoading(true);
    try {
      const formData = new FormData();
      Object.entries(form).forEach(([key, value]) => {
        if (key === 'photos') {
          value.forEach(p => { if (p) formData.append('photos', p) });
        } else if (value) {
          formData.append(key, value);
        }
      });

      const newEvent = await apiFetch('/events', { method: 'POST', body: formData });
      push('நிகழ்வு வெற்றிகரமாக உருவாக்கப்பட்டது!', 'success');
      onSuccess(newEvent);
    } catch (err) {
      push('நிகழ்வை உருவாக்க முடியவில்லை: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 10 }} transition={{ duration: 0.2, ease: "easeOut" }} className="relative z-10 w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
        <Card className="p-5 max-h-[90vh] overflow-y-auto">
          <h3 className="text-lg font-semibold mb-4">புதிய நிகழ்வு உருவாக்கு</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <TextInput label="நிகழ்வின் தலைப்பு" required value={form.title} onChange={setField('title')} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <TextInput label="தேதி" type="date" required value={form.when} onChange={setField('when')} />
              <TextInput label="இடம்" required value={form.where} onChange={setField('where')} />
            </div>
            <TextInput label="சட்டமன்றத் தொகுதி" required value={form.assembly} onChange={setField('assembly')} />
            <label className="block text-sm">
              <span className="text-gray-700">விவரம்<span className="text-red-500"> *</span></span>
              <textarea value={form.content} onChange={setField('content')} rows="5" className="mt-1 w-full px-3 py-2 rounded-xl border border-gray-300 focus:outline-none focus:ring-2 focus:ring-green-600"></textarea>
            </label>
            <div>
              <span className="text-sm text-gray-700">புகைப்படங்கள் (4 வரை)</span>
              <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-3">
                {form.photos.map((photo, index) => (
                  <label key={index} className="cursor-pointer aspect-square bg-gray-100 rounded-xl border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-green-500 hover:text-green-600 transition-colors">
                    {photo ? (
                      <img src={URL.createObjectURL(photo)} alt={`Preview ${index + 1}`} className="w-full h-full object-cover rounded-lg" />
                    ) : (
                      <div className="text-center">
                        <UploadCloud className="w-8 h-8 mx-auto" />
                        <span className="text-xs mt-1 block">புகைப்படம் {index + 1}</span>
                      </div>
                    )}
                    <input type="file" accept="image/*" className="hidden" onChange={(e) => handlePhotoChange(index, e)} />
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end items-center gap-2 pt-2">
              <button type="button" onClick={onClose} className="px-4 py-2 bg-gray-200 rounded-xl text-sm">ரத்து</button>
              <button type="submit" disabled={loading} className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm disabled:bg-green-400">{loading ? 'சமர்ப்பிக்கிறது...' : 'சமர்ப்பி'}</button>
            </div>
          </form>
        </Card>
      </motion.div>
    </motion.div>
  );
}

function RegistrationSuccessPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const queryParams = new URLSearchParams(location.search);
  const [countdown, setCountdown] = useState(15);
  const [isDownloading, setIsDownloading] = useState(false);
  const newMemberId = queryParams.get('newMemberId');
  const shareText = `நான் தமிழக வாழ்வுரிமைக் கட்சியில் உறுப்பினராக இணைந்துள்ளேன்! என் உறுப்பினர் எண்: ${newMemberId}. நீங்களும் இணையுங்கள்! #TVK #Velmurugan`;
  const shareUrl = `https://join.tvkparty.org/join`;

  // Get base URL from current window location
  const baseUrl = window.location.origin;

  // Refetch user data after registration so topbar shows correct photo
  React.useEffect(() => {
    const timer = setInterval(() => {
      setCountdown(prevCountdown => prevCountdown - 1);
    }, 1000);

    const redirectTimer = setTimeout(() => {
      window.location.href = '/';
    }, 15000);

    return () => {
      clearInterval(timer);
      clearTimeout(redirectTimer);
    };
  }, []);

  const handleViewIdCard = () => {
    window.location.href = `/idcard?newMemberId=${newMemberId}&from=creation`;
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  return (
    <Shell>
      <div className="flex items-center justify-center p-4 font-anek">
        <Card className="w-full max-w-md p-6 text-center">
          <motion.div initial={{ scale: 0.5, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: 'spring', stiffness: 260, damping: 20 }}>
            <BadgeCheck className="w-16 h-16 text-green-600 mx-auto" />
          </motion.div>
          <h2 className="text-2xl font-bold mt-4">பதிவு வெற்றி!</h2>
          <p className="text-gray-600 mt-2">தமிழக வாழ்வுரிமைக் கட்சியில் இணைந்ததற்கு நன்றி. உங்கள் உறுப்பினர் ஐடி: <strong>{newMemberId}</strong></p>
          <div className="mt-6 flex flex-col sm:flex-row justify-center gap-3">
            <button
              onClick={handleViewIdCard}
              className="px-6 py-2 bg-green-600 text-white rounded-xl text-sm font-semibold hover:bg-green-700 transition-colors"
            >
              அடையாள அட்டையைப் பார்க்க
            </button>
            <a
              href={`${baseUrl}/idcard?newMemberId=${newMemberId}&from=creation`}
              target="_blank"
              rel="noopener noreferrer"
              className="px-6 py-2 bg-blue-600 text-white rounded-xl text-sm font-semibold flex items-center justify-center hover:bg-blue-700 transition-colors"
            >
              <Download className="w-4 h-4 mr-2" />
              புதிய தாவலில் திற
            </a>
            <button
              onClick={handleGoHome}
              className="px-6 py-2 bg-gray-200 text-gray-800 rounded-xl text-sm font-semibold hover:bg-gray-300 transition-colors"
            >
              முகப்புக்குச் செல்
            </button>
          </div>
          <div className="mt-6 pt-4 border-t">
            <p className="text-sm font-semibold text-gray-700">நண்பர்களுடன் பகிரவும்</p>
            <div className="flex justify-center gap-3 mt-3">
              <a href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"><Twitter className="w-5 h-5 text-[#1DA1F2]" /></a>
              <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`} target="_blank" rel="noopener noreferrer" className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"><Facebook className="w-5 h-5 text-[#1877F2]" /></a>
              <a href={`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`} target="_blank" rel="noopener noreferrer" className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"><MessageCircle className="w-5 h-5 text-[#25D366]" /></a>
              <button onClick={() => navigator.share ? navigator.share({ title: 'TVK உறுப்பினர்', text: shareText, url: shareUrl }) : navigator.clipboard.writeText(shareText + ' ' + shareUrl)} className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"><Share2 className="w-5 h-5 text-gray-600" /></button>
            </div>
          </div>
          <div className="mt-4 text-xs text-gray-500">
            முகப்புப் பக்கத்திற்குத் தானாகவே {countdown} வினாடிகளில் திருப்பி விடப்படுவீர்கள்...
          </div>
        </Card>
      </div>
    </Shell>
  );
} // This was missing

function IdCardPage() {

  const { push } = useToasts();
  const { user: authUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = typeof useParams === 'function' ? useParams() : {};
  const [isFlipped, setIsFlipped] = useState(false);
  const [cardDesign, setCardDesign] = useState('thalaivar');
  const frontRef = React.useRef(null);
  const backRef = React.useRef(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const queryParams = new URLSearchParams(location.search);
  const newMemberId = queryParams.get('newMemberId');
  const fromCreation = queryParams.get('from') === 'creation';
  const routeId = params && params.id;

  useEffect(() => {
    setLoading(true);
    setError("");
    const fetchUser = async () => {
      try {
        let endpoint = '/me';
        if (routeId) {
          endpoint = `/members/${routeId}`;
        } else if (newMemberId) {
          endpoint = `/members/${newMemberId}`;
        } else if (authUser && authUser.id) {
          endpoint = `/members/${authUser.id}`;
        }
        const userData = await apiFetch(endpoint);
        if (!userData || !userData.data) {
          setError('உறுப்பினர் தகவல் கிடைக்கவில்லை.');
          setUser(null);
        } else {
          setUser(userData.data);
        }
      } catch (e) {
        // If superadmin, show hardcoded superadmin data
        if (authUser && (authUser.role === 'superadmin' || authUser.id === 'TVKADMIN001')) {
          setUser({
            id: 'TVKADMIN001',
            name: 'Super Admin',
            district: 'தலைமையகம்',
            assembly: 'தமிழ்நாடு',
            role: 'superadmin',
            photo: '',
            badge: 'தங்கம்',
            dob: '1980-01-01',
            bloodGroup: 'O+',
            address1: '123, தலைமை அலுவலகம்',
            city: 'சென்னை',
            pincode: '600018',
          });
          setError("");
        } else {
          setError('உறுப்பினர் தகவலை ஏற்ற முடியவில்லை: ' + (e.message || 'Unknown error'));
          setUser(null);
        }
        console.error('IDCard fetch error:', e);
      } finally {
        setLoading(false);
      }
    };
    if (routeId || newMemberId || (authUser && authUser.id)) {
      fetchUser();
    } else {
      setError('ID இல்லை.');
      setLoading(false);
    }
  }, [routeId, newMemberId, authUser]);

  if (loading) {
    return <div className="flex items-center justify-center h-96 text-lg">ஏற்றுகிறது...</div>;
  }
  if (error) {
    return <div className="flex items-center justify-center h-96 text-red-600 text-lg">{error}</div>;
  }
  if (!user) {
    return <div className="flex items-center justify-center h-96 text-gray-600 text-lg">உறுப்பினர் இல்லை.</div>;
  }

  // ...existing code for rendering the ID card...

  const handleShare = async () => {
    if (!frontRef.current || !backRef.current || !user) {
      push('அடையாள அட்டை ஏற்றப்படவில்லை, மீண்டும் முயற்சிக்கவும்.', 'error');
      return;
    }
    push('அடையாள அட்டை PDF உருவாக்கப்படுகிறது...', 'info');

    const frontCardElement = frontRef.current;
    const backCardElement = backRef.current;


    // Remove all web font links before export to avoid font errors
    document.querySelectorAll('link[rel="stylesheet"]').forEach(link => {
      if (link.href && link.href.includes('fonts.googleapis.com')) {
        link.parentNode.removeChild(link);
      }
    });

    // Set fallback font-family to system fonts only
    const cardEls = [frontCardElement?.querySelector('.thalaivar-card'), backCardElement?.querySelector('.thalaivar-card')];
    cardEls.forEach(el => {
      if (el) el.style.fontFamily = "Arial, sans-serif";
    });

    // Remove border radius for generation
    cardEls.forEach(el => el?.classList.remove('rounded-2xl'));

    try {
      // Capture front card normally
      const frontCanvas = await html2canvas(frontRef.current, { scale: 2, useCORS: true });

      // For back card: clone it to a temporary container outside the 3D transform context
      // This prevents the mirrored appearance caused by rotateY(180deg)
      const backCardContent = backRef.current.cloneNode(true);
      const tempContainer = document.createElement('div');
      tempContainer.style.cssText = 'position: fixed; left: -9999px; top: 0; width: 507px; height: 319px; z-index: -1000;';
      // Remove the rotateY transform from the clone
      backCardContent.style.transform = 'none';
      backCardContent.style.position = 'relative';
      backCardContent.style.width = '100%';
      backCardContent.style.height = '100%';
      tempContainer.appendChild(backCardContent);
      document.body.appendChild(tempContainer);

      // Wait for the clone to be rendered
      await new Promise(resolve => setTimeout(resolve, 100));

      // Capture the cloned back card
      const backCanvas = await html2canvas(backCardContent, { scale: 2, useCORS: true });

      // Remove the temporary container
      document.body.removeChild(tempContainer);

      const frontUrl = frontCanvas.toDataURL('image/png');
      const backUrl = backCanvas.toDataURL('image/png');

      // Create PDF using pdf-lib
      const pdfDoc = await PDFDocument.create();

      // Add front page
      const page1 = pdfDoc.addPage([152.25, 95.8]);
      const frontImage = await pdfDoc.embedPng(frontUrl);
      page1.drawImage(frontImage, {
        x: 0,
        y: 0,
        width: 152.25,
        height: 95.8
      });

      // Add back page
      const page2 = pdfDoc.addPage([152.25, 95.8]);
      const backImage = await pdfDoc.embedPng(backUrl);
      page2.drawImage(backImage, {
        x: 0,
        y: 0,
        width: 152.25,
        height: 95.8
      });

      // Generate PDF bytes and download
      const pdfBytes = await pdfDoc.save();
      const pdfBlob = new Blob([pdfBytes], { type: 'application/pdf' });
      const pdfLink = document.createElement('a');
      pdfLink.href = URL.createObjectURL(pdfBlob);
      pdfLink.download = `${user.id}-id-card.pdf`;
      document.body.appendChild(pdfLink);
      pdfLink.click();
      document.body.removeChild(pdfLink);
      URL.revokeObjectURL(pdfLink.href);

      push('அடையாள அட்டை PDF பதிவிறக்கம் செய்யப்பட்டது.', 'success');
    } catch (e) {
      console.error('PDF generation failed:', e);
      push('PDF உருவாக்கம் தோல்வியுற்றது: ' + (e.message || 'Unknown error'), 'error');
    } finally {
      // Restore border radius
      frontCardElement?.querySelector('.thalaivar-card')?.classList.add('rounded-2xl');
      backCardElement?.querySelector('.thalaivar-card')?.classList.add('rounded-2xl');

      if (backCardElement && backCardElement.style.transform === 'none') {
        backCardElement.style.transform = 'rotateY(180deg)';
      }
    }
  };

  const handleShareAsImage = async () => {
    if (!frontRef.current || !user) {
      push('அடையாள அட்டை ஏற்றப்படவில்லை, மீண்டும் முயற்சிக்கவும்.', 'error');
      return;
    }

    if (!navigator.share) {
      push('உங்கள் உலாவியில் இந்தப் பகிர்வு அம்சம் ஆதரிக்கப்படவில்லை.', 'warn');
      return;
    }

    push('படம் உருவாக்கப்படுகிறது...', 'info');

    try {
      const imageBlob = await toBlob(frontRef.current, { quality: 0.95, pixelRatio: 2 });
      const imageFile = new File([imageBlob], `${user.id}-id-card.png`, { type: 'image/png' });

      await navigator.share({
        title: 'TVK உறுப்பினர் அடையாள அட்டை',
        text: `நான் தமிழக வாழ்வுரிமைக் கட்சியில் உறுப்பினராக இணைந்துள்ளேன்! என் உறுப்பினர் எண்: ${user.id}.`,
        files: [imageFile],
      });
      push('அடையாள அட்டை பகிரப்பட்டது!', 'success');
    } catch (e) {
      // Avoid showing an error if the user cancels the share dialog
      if (e.name !== 'AbortError') {
        push('படத்தைப் பகிர முடியவில்லை: ' + e.message, 'error');
      }
    }
  };

  // Simple container for public access (from registration)
  const PublicContainer = ({ children }) => (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 font-anek">
      <header className="sticky top-0 z-40 bg-gradient-to-b from-green-800 to-green-900 border-b border-white/20">
        <div className="relative z-10 flex items-center justify-center h-20 px-4 bg-black/10">
          <img src={partyLogo} alt="Party Logo" className="h-16 w-auto drop-shadow-[0_8px_12px_rgba(250,204,21,0.6)]" />
        </div>
      </header>
      <main className="overflow-y-auto pb-8">
        <div className="max-w-5xl mx-auto px-4 pt-4">{children}</div>
      </main>
    </div>
  );

  // Determine which wrapper to use
  const Wrapper = fromCreation ? PublicContainer : Shell;

  if (loading) {
    return (
      <Wrapper>
        <div className="flex justify-center items-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-green-600" />
          <span className="ml-2 text-gray-600">ஏற்றுகிறது...</span>
        </div>
      </Wrapper>
    );
  }

  if (error) {
    return (
      <Wrapper>
        <div className="flex flex-col items-center justify-center h-64 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
          <p className="text-red-600 text-lg">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
          >
            மீண்டும் முயற்சி
          </button>
        </div>
      </Wrapper>
    );
  }

  if (!user) {
    return (
      <Wrapper>
        <EmptyState icon={IdCardIcon} title="பயனர் இல்லை" message="அடையாள அட்டைக்கான பயனர் தரவைக் கண்டுபிடிக்க முடியவில்லை." />
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="relative text-white rounded-2xl p-6 mb-6 overflow-hidden shadow-lg">
        <div className="absolute inset-0 bg-gradient-to-r from-green-600 to-green-700"></div>
        <img src={wavylines} alt="background" className="absolute inset-0 w-full h-full object-cover opacity-25" />
        <div className="absolute top-0 -left-full h-full w-1/2 bg-gradient-to-r from-transparent via-white/30 to-transparent transform -skew-x-12 animate-[glare_5s_ease-in-out_infinite]"></div>
        <div className="absolute top-6 right-6 z-20 sm:left-1/2 sm:-translate-x-1/2 sm:right-auto">
          <div className="relative px-4 py-2 bg-white/25 rounded-full border border-white/30 backdrop-blur-sm">
            <div className="text-xs font-bold uppercase tracking-wider">{user?.badge || 'தங்கம்'}</div>
            <div className="text-[9px] opacity-80 -mt-0.5">பயனர் பேட்ஜ்</div>
          </div>
        </div>
        <div className="relative z-10">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div>
              <div className="text-sm text-green-100">உறுப்பினர் பெயர்</div>
              <div className="text-2xl font-bold">{user.name}</div>
            </div>
            <div className="text-left sm:text-right mt-4 sm:mt-0">
              <div className="text-sm text-green-100">உறுப்பினர் எண்</div>
              <div className="text-lg font-mono bg-black/20 px-3 py-1 rounded-lg inline-block">{user.id}</div>
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-white/20 flex flex-col sm:flex-row items-center justify-center gap-3">
            <div className="flex gap-3 w-full sm:w-auto">
              <button onClick={handleShare} className="flex-1 px-4 py-2 bg-gradient-to-r from-red-600 to-red-500 border-none text-white rounded-xl text-sm flex items-center justify-center gap-2 hover:from-red-500 hover:to-red-400 transition-all shadow-lg">
                <Download className="w-4 h-4" /><span>PDF பதிவிறக்கம்</span>
              </button>
              <button onClick={handleShareAsImage} className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-500 border-none text-white rounded-xl text-sm flex items-center justify-center gap-2 hover:from-blue-500 hover:to-blue-400 transition-all shadow-lg">
                <Share2 className="w-4 h-4" /><span>படமாகப் பகிர்</span>
              </button>
            </div>
            {fromCreation && (
              <button onClick={() => window.location.href = '/members/new'} className="w-full sm:w-auto px-4 py-2 bg-white/20 backdrop-blur-sm border border-white/30 text-white rounded-xl text-sm flex items-center justify-center gap-2 hover:bg-white/30 transition-colors">
                <Home className="w-4 h-4" />
                <span>புதிய பதிவு</span>
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="mt-6">
        <IdCardPreview user={user} isFlipped={isFlipped} frontRef={frontRef} backRef={backRef} cardDesign={cardDesign} />
      </div>

      <div className="mt-4 flex justify-center">
        <button onClick={() => setIsFlipped(f => !f)} className="px-4 py-2 bg-white text-black rounded-xl text-sm flex items-center gap-2 shadow-lg border">
          <RotateCw className="w-4 h-4" />
          <span>{isFlipped ? 'முன்பக்கம் காட்டு' : 'பின்பக்கம் காட்டு'}</span>
        </button>
      </div>

      <div className="mt-6 mx-auto max-w-lg">
        <div className="bg-green-50/30 backdrop-blur-md border border-green-200/30 rounded-xl p-4 flex items-center gap-3">
          <div className="p-2 bg-green-100/50 rounded-lg">
            <ShieldCheck className="w-5 h-5 text-green-600" />
          </div>
          <div>
            <div className="font-semibold text-green-800 text-xs">உங்கள் தரவு பாதுகாப்பானது</div>
            <div className="text-xs text-green-700/80 mt-0.5">உங்கள் அடையாள அட்டை தரவுகள் பாதுகாப்பாக சேமிக்கப்படுகின்றன</div>
          </div>
        </div>
      </div>
    </Wrapper>
  );
}

function IdEditPage() {
  const { push } = useToasts();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: 'கணேஷ் குமார்', district: 'சென்னை', assembly: 'சைதாப்பேட்டை' });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const onSubmit = async (e) => {
    e.preventDefault();
    try { setLoading(true); await apiFetch('/idcard', { method: 'PUT', body: form, retry: 2 }); push('ID Card புதுப்பிக்கப்பட்டது', 'success'); navigate('/idcard'); }
    catch (err) { push('புதுப்பிக்க முடியவில்லை: ' + err.message, 'error'); } finally { setLoading(false); }
  }
  return (
    <Shell>
      <Section title="ID மாற்றம் (Edit)">
        <form onSubmit={onSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextInput label="பெயர்" required value={form.name} onChange={set('name')} />
          <TextInput label="மாவட்டம்" required value={form.district} onChange={set('district')} />
          <TextInput label="தொகுதி" required value={form.assembly} onChange={set('assembly')} />
          <div className="sm:col-span-2 flex items-center gap-2">
            <button disabled={loading} className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm">{loading ? 'சேமிக்கிறது...' : 'சேமிக்கவும்'}</button>
            <button type="button" onClick={() => navigate(-1)} className="px-4 py-2 bg-gray-200 rounded-xl text-sm">ரத்து</button>
          </div>
        </form>
      </Section>
    </Shell>
  );
}

function EventsPage() {
  const { push } = useToasts();
  const { role } = useAuth();
  const [items, setItems] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [q, setQ] = useState("");
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiFetch('/events')
      .then(response => {
        // Handle paginated response: { data: [...], pagination: {...} }
        const events = response?.data || response || [];
        setItems(Array.isArray(events) ? events : []);
      })
      .catch(err => push('Failed to load events: ' + err.message, 'error'))
      .finally(() => setLoading(false));
  }, [push]);

  const handleShare = (event) => {
    if (!event) return;
    const shareText = `Event: ${event.title}\nWhen: ${event.when}\nWhere: ${event.where}\n\n${event.content}`;
    const shareUrl = window.location.href;

    if (navigator.share) {
      navigator.share({
        title: event.title,
        text: shareText,
        url: shareUrl,
      }).catch((error) => console.log('Error sharing', error));
    } else {
      navigator.clipboard.writeText(shareText)
        .then(() => push('Event details copied!', 'success'))
        .catch(() => push('Failed to copy details.', 'error'));
    }
  };

  const canAddEvents = ['admin', 'state', 'district'].includes(role);

  const filteredEvents = items.filter(e =>
    e.title.toLowerCase().includes(q.toLowerCase()) ||
    e.where.toLowerCase().includes(q.toLowerCase())
  );
  return (
    <Shell>
      <div className="flex flex-col sm:flex-row items-center gap-2 mb-4">
        <div className="flex items-center bg-white rounded-xl border border-gray-200 px-3 py-2 w-full flex-grow">
          <Search className="w-4 h-4 text-gray-400" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="நிகழ்வுகளில் தேடவும்..." className="outline-none px-2 w-full text-sm" />
        </div>
        {canAddEvents && (
          <button onClick={() => setIsEventModalOpen(true)} className="w-full sm:w-auto px-4 py-2.5 bg-green-600 text-white rounded-xl text-sm whitespace-nowrap">+ புதிய நிகழ்வு</button>
        )}
      </div>
      <div className="space-y-3">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-16" />)
        ) : filteredEvents.length > 0 ? (
          filteredEvents.map((e) => (
            <Card key={e.id} onClick={() => setSelectedEvent(e)} className="p-3 flex items-center justify-between cursor-pointer active:bg-gray-50 transition-colors">
              <div className="dark:text-white">
                <div className="text-sm font-semibold">{e.title}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{e.when} • {e.where}</div>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-400" />
            </Card>
          ))
        ) : <EmptyState icon={CalendarDays} title={q ? 'தேடல் முடிவுகள் இல்லை' : 'நிகழ்வுகள் இல்லை'} message={q ? 'வேறு தேடல் சொல்லை முயற்சிக்கவும்.' : 'புதிய நிகழ்வுகள் விரைவில் சேர்க்கப்படும்.'} />}
      </div>

      <AnimatePresence>
        {selectedEvent && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={() => setSelectedEvent(null)}>
            <motion.div initial={{ scale: 0.95, opacity: 0, y: 10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.95, opacity: 0, y: 10 }} transition={{ duration: 0.2, ease: "easeOut" }} className="relative z-10 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <Card className="relative p-5 dark:text-white">
                <button onClick={() => setSelectedEvent(null)} className="absolute top-3 right-3 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
                <h3 className="text-lg font-semibold mb-2 ">{selectedEvent.title}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{selectedEvent.when} • {selectedEvent.where}</p>
                <div className="max-h-[60vh] overflow-y-auto pr-2 text-sm text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">{selectedEvent.content}</div>
                <div className="flex justify-end mt-4">
                  <button onClick={() => handleShare(selectedEvent)} className="px-4 py-2 bg-blue-600 text-white rounded-xl text-sm flex items-center gap-2">
                    <Share2 className="w-4 h-4" />
                    <span>Share</span>
                  </button>
                </div>
              </Card>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isEventModalOpen && (
          <CreateEventModal
            isOpen={isEventModalOpen}
            onClose={() => setIsEventModalOpen(false)}
            onSuccess={(newEvent) => {
              setItems(prev => [newEvent, ...prev]);
              setIsEventModalOpen(false);
            }}
          />
        )}
      </AnimatePresence>
    </Shell>
  );
}


function HelpPage() {
  const [openFAQ, setOpenFAQ] = useState(0); // State to manage which FAQ is open, default first one
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const faqs = [
    {
      q: "புதிய உறுப்பினரைச் சேர்ப்பது எப்படி?",
      a: "பக்கப்பட்டியில் உள்ள \"புதிய உறுப்பினர்\" அல்லது முகப்புப் பக்கத்தில் உள்ள \"உறுப்பினர் சேர்\" பொத்தானை அழுத்தவும். பின்னர், ஐந்து படிநிலை செயல்முறையைப் பின்பற்றி தேவையான விவரங்களை (தொலைபேசி எண், புகைப்படம், வாக்காளர் அடையாள அட்டை, தனிப்பட்ட விவரங்கள்) நிரப்பவும்."
    },
    {
      q: "எனது அடையாள அட்டையை பதிவிறக்கம் செய்வது எப்படி?",
      a: "\"ID அட்டை\" பக்கத்திற்குச் செல்லவும். உங்கள் அட்டையைப் பார்க்கலாம். \"PDF ஆக பதிவிறக்கு\" பொத்தானைப் பயன்படுத்தி PDF வடிவிலும், \"படமாகப் பகிர்\" பொத்தானைப் பயன்படுத்தி படமாகவும் பதிவிறக்கம் செய்யலாம்."
    },
    {
      q: "கடவுச்சொல்லை மறந்துவிட்டேன். என்ன செய்வது?",
      a: "உள்நுழைவுத் திரையில், \"கடவுச்சொல்லை மறந்துவிட்டேன்\" என்ற இணைப்பு இருக்கும். அதை அழுத்தி, உங்கள் பதிவு செய்யப்பட்ட மொபைல் எண் மூலம் கடவுச்சொல்லை மீட்டமைப்பதற்கான வழிமுறைகளைப் பின்பற்றவும். உங்களுக்கு ஒரு OTP அனுப்பப்படும், அதை உள்ளிட்டு புதிய கடவுச்சொல்லை அமைக்கலாம்."
    },
    {
      q: "எனது சுயவிவரத் தகவலை எப்படிப் புதுப்பிப்பது?",
      a: "\"சுயவிவரம்\" பக்கத்திற்குச் செல்லவும். உங்கள் பெயர், தொலைபேசி, மின்னஞ்சல் மற்றும் சுயவிவரப் புகைப்படத்தை மாற்றலாம். மாற்றங்களைச் செய்த பிறகு, \"மாற்றங்களைச் சேமிக்கவும்\" பொத்தானை அழுத்த மறக்காதீர்கள்."
    },
    {
      q: "எனது பரிந்துரை இணைப்பை (Referral Link) எப்படிப் பெறுவது?",
      a: "\"சுயவிவரம்\" பக்கத்தில், \"பரிந்துரை இணைப்பு\" என்ற பகுதிக்குச் செல்லவும். அங்குள்ள \"இணைப்பை உருவாக்கு\" பொத்தானை அழுத்தினால், உங்களுக்கான தனிப்பட்ட இணைப்பு உருவாக்கப்படும். அதை நகலெடுத்து நண்பர்களுடன் பகிரலாம்."
    },
    {
      q: "கட்சியில் உள்ள பதவிகள் என்னென்ன?",
      a: "கட்சியில் நிர்வாகி (Admin), மாநில (State), மற்றும் மாவட்ட (District) அளவிலான பதவிகள் உள்ளன. ஒவ்வொரு பதவிக்கும் வெவ்வேறு பொறுப்புகளும், அணுகல் நிலைகளும் உள்ளன."
    },
    {
      q: "நான் கட்சிக்கு எப்படி பங்களிக்க முடியும்?",
      a: "நீங்கள் களப்பணி, சமூக ஊடகப் பிரச்சாரம், வடிவமைப்பு, மற்றும் பல வழிகளில் பங்களிக்கலாம். உங்கள் விருப்பத்தை \"சுயவிவரம்\" பக்கத்தில் குறிப்பிடலாம்."
    },
    {
      q: "கட்சியின் சமீபத்திய செய்திகளை எங்கே அறிவது?",
      a: "\"அறிவிப்புகள்\" மற்றும் \"நிகழ்வுகள்\" பக்கங்களில் கட்சியின் சமீபத்திய செய்திகள், அறிவிப்புகள் மற்றும் நிகழ்வுகளைக் காணலாம்."
    },
    {
      q: "எனது தரவு பாதுகாப்பாக உள்ளதா?",
      a: "ஆம், உங்கள் தரவு அனைத்தும் பாதுகாப்பாக வைக்கப்பட்டுள்ளது. நாங்கள் உங்கள் தனியுரிமையை மதிக்கிறோம். மேலும் விவரங்களுக்கு எங்கள் தனியுரிமைக் கொள்கையைப் படிக்கவும்."
    },
    {
      q: "செயலியில் ஏதேனும் சிக்கல் ஏற்பட்டால் என்ன செய்வது?",
      a: "உங்களுக்கு ஏதேனும் தொழில்நுட்பச் சிக்கல் ஏற்பட்டால், இந்தப் பக்கத்தில் உள்ள \"நேரலை உரையாடல்\" மூலம் உடனடியாகத் தெரிவிக்கலாம் அல்லது itwing@tvkparty.org என்ற மின்னஞ்சலுக்கு உங்கள் சிக்கலை விவரித்து அனுப்பலாம்."
    },
  ];

  const toggleFAQ = (index) => setOpenFAQ(openFAQ === index ? null : index);

  const filteredFaqs = faqs.filter(faq =>
    faq.q.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.a.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Shell>
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}>
        <Card className="p-4 sm:p-6 text-center bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-800 dark:to-gray-900 mb-6">
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-gradient-to-br from-blue-100 to-purple-200 dark:from-blue-800 dark:to-purple-900 rounded-xl inline-block">
              <LifeBuoy className="w-8 h-8 text-blue-700 dark:text-blue-300" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">உதவி மையம்</h1>
          <p className="max-w-2xl mx-auto mt-2 text-gray-600 dark:text-gray-300">
            உங்களுக்கு ஏதேனும் கேள்விகள் உள்ளதா? நாங்கள் உதவ இங்கே இருக்கிறோம்.
          </p>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <a href="mailto:itwing@tvkparty.org" className="group block">
            <Card className="p-6 text-center h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border-blue-200 dark:border-blue-800 hover:border-blue-400 dark:hover:border-blue-600">
              <Mail className="w-10 h-10 mx-auto text-blue-500 transition-transform duration-300 group-hover:scale-110" />
              <h3 className="font-semibold mt-4 text-lg">மின்னஞ்சல்</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">கேள்விகளுக்கு, எங்களைத் தொடர்பு கொள்ளவும்.</p>
              <div className="text-base font-semibold text-blue-600 dark:text-blue-400 mt-3">itwing@tvkparty.org</div>
            </Card>
          </a>
          <a href="tel:7699977778" className="group block">
            <Card className="p-6 text-center h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border-green-200 dark:border-green-800 hover:border-green-400 dark:hover:border-green-600">
              <Phone className="w-10 h-10 mx-auto text-green-500 transition-transform duration-300 group-hover:scale-110" />
              <h3 className="font-semibold mt-4 text-lg">அலைப்பேசி</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">உடனடி உதவிக்கு அழைக்கவும்.</p>
              <div className="text-base font-semibold text-green-600 dark:text-green-400 mt-3">76999 77778</div>
            </Card>
          </a>
          <div onClick={() => setIsChatOpen(true)} className="group cursor-pointer">
            <Card className="p-6 text-center h-full transition-all duration-300 hover:shadow-xl hover:-translate-y-1 border-purple-200 dark:border-purple-800 hover:border-purple-400 dark:hover:border-purple-600">
              <MessageSquareText className="w-10 h-10 mx-auto text-purple-500 transition-transform duration-300 group-hover:scale-110" />
              <h3 className="font-semibold mt-4 text-lg">நேரலை உரையாடல்</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">எங்கள் பிரதிநிதியுடன் உரையாடுங்கள்.</p>
              <div className="mt-3 px-6 py-2 inline-block bg-purple-500 text-white rounded-lg font-semibold group-hover:bg-purple-600 transition-colors shadow-md">
                தொடங்கு
              </div>
            </Card>
          </div>
        </div>

        <h2 className="text-2xl font-bold text-center mb-6 text-gray-800 dark:text-gray-100">அடிக்கடி கேட்கப்படும் கேள்விகள்</h2>

        <div className="mb-4 relative">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="கேள்விகளைத் தேடுங்கள்..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-600"
          />
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        </div>

        <div className="space-y-3">
          {filteredFaqs.length > 0 ? (
            filteredFaqs.map((faq, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm transition-all duration-300 overflow-hidden">
                <button
                  className="flex justify-between items-center w-full text-left p-4"
                  onClick={() => toggleFAQ(index)}
                >
                  <span className="font-semibold text-gray-800 dark:text-gray-100 flex-1 pr-2">{faq.q}</span>
                  <div className={`p-1 rounded-full transition-transform duration-300 ${openFAQ === index ? 'rotate-180 bg-green-100 dark:bg-green-900' : 'bg-gray-100 dark:bg-gray-700'}`}>
                    <ChevronDown className={`w-5 h-5 transition-colors ${openFAQ === index ? 'text-green-600 dark:text-green-300' : 'text-gray-500'}`} />
                  </div>
                </button>
                <AnimatePresence>
                  {openFAQ === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="px-4 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700/50">
                        <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed">{faq.a}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))
          ) : (
            <Card className="p-6 text-center border-dashed">
              <Search className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100">முடிவுகள் இல்லை</h3>
              <p className="mt-1 text-sm text-gray-500">உங்கள் தேடலுக்குப் பொருந்தக்கூடிய கேள்விகள் எதுவும் இல்லை.</p>
            </Card>
          )}
        </div>
      </motion.div>
      <AnimatePresence>
        {isChatOpen && <LiveChatWindow onClose={() => setIsChatOpen(false)} />}
      </AnimatePresence>
    </Shell>
  );
}


function TextPageModal({ title, content, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
        className="relative w-full max-w-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <Card className="flex flex-col h-[80vh]">
          <div className="p-4 border-b flex justify-between items-center">
            <h3 className="text-lg font-bold text-gray-800">{title}</h3>
            <button onClick={onClose} className="p-1 rounded-full hover:bg-gray-200"><X className="w-5 h-5 text-gray-700" /></button>
          </div>
          <div className="p-5 overflow-y-auto text-sm text-gray-700 leading-relaxed bg-gray-50/50">
            <pre className="whitespace-pre-wrap font-sans">{content}</pre>
          </div>
          <div className="p-3 border-t bg-gray-50">
            <Card className="p-2 bg-blue-50 border-blue-100 text-blue-800 text-xs text-center">பதிப்பு 1.0 | கடைசியாகப் புதுப்பிக்கப்பட்டது: 25/07/2024</Card>
          </div>
          <div className="p-3 border-t bg-gray-100 flex justify-end rounded-b-2xl">
            <button onClick={onClose} className="px-4 py-2 bg-gray-200 rounded-xl text-sm">மூடு</button>
          </div>
        </Card>
      </motion.div>
    </motion.div>
  );
}

const chatQuestions = [
  { key: 'name', question: "வணக்கம்! உங்கள் பெயர் என்ன?" },
  { key: 'userId', question: "உங்கள் உறுப்பினர் எண் என்ன?" },
  { key: 'district', question: "நீங்கள் எந்த மாவட்டம்?" },
  { key: 'assembly', question: "உங்கள் சட்டமன்றத் தொகுதி?" },
  { key: 'message', question: "உங்கள் செய்தி அல்லது கேள்வியை உள்ளிடவும்." }
];

function LiveChatWindow({ onClose }) {
  const [messages, setMessages] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userInput, setUserInput] = useState('');
  const [formData, setFormData] = useState({});
  const [isComplete, setIsComplete] = useState(false);
  const [isBotTyping, setIsBotTyping] = useState(true);
  const messagesEndRef = React.useRef(null);
  const { push } = useToasts();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    if (currentQuestionIndex < chatQuestions.length) {
      const timer = setTimeout(() => {
        setIsBotTyping(false);
        setMessages(prev => [...prev, { id: Date.now(), text: chatQuestions[currentQuestionIndex].question, sender: 'bot' }]);
      }, 500);
      return () => clearTimeout(timer);
    } else if (!isComplete) {
      (async () => {
        setIsComplete(true);
        try {
          setIsBotTyping(true);
          // In a real app, you'd send the formData to your backend here.
          // await apiFetch('/report-message', { method: 'POST', body: formData });
          await sleep(1500); // Mock network delay
          const summary = `நன்றி! உங்கள் செய்தி பெறப்பட்டது. உங்களை ஒரு ஒருங்கிணைப்பாளருடன் இணைக்கிறோம்.`;
          setMessages(prev => [...prev, { id: Date.now(), text: summary, sender: 'bot' }]);
        } catch (err) {
          const errorMessage = `மன்னிக்கவும், உங்கள் செய்தியைச் சமர்ப்பிப்பதில் பிழை ஏற்பட்டது. பின்னர் முயற்சிக்கவும்.`;
          setMessages(prev => [...prev, { id: Date.now(), text: errorMessage, sender: 'bot' }]);
          push('செய்தியைச் சமர்ப்பிக்க முடியவில்லை: ' + err.message, 'error');
        }
        setIsBotTyping(false);
      })();
    }
  }, [currentQuestionIndex, isComplete, formData, push]);

  useEffect(scrollToBottom, [messages, isBotTyping]);

  const handleSend = (e) => {
    e.preventDefault();
    if (userInput.trim() === '' || isBotTyping) return;

    const currentQuestion = chatQuestions[currentQuestionIndex];
    const userMessage = { id: Date.now(), text: userInput, sender: 'user' };

    setMessages(m => [...m, userMessage]);
    setFormData(prev => ({ ...prev, [currentQuestion.key]: userInput }));
    setUserInput('');
    setIsBotTyping(true);
    setCurrentQuestionIndex(prev => prev + 1);
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-end justify-center sm:justify-end bg-black/50 backdrop-blur-sm p-4 pb-20 sm:pb-4" onClick={onClose}>
      <motion.div
        initial={{ y: "100vh" }}
        animate={{ y: 0 }}
        exit={{ y: "100vh" }}
        transition={{ type: "spring", stiffness: 400, damping: 40 }}
        className="relative z-10 w-full max-w-md h-[70vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <Card className="flex-grow flex flex-col overflow-hidden">
          <div className="p-3 bg-green-600 text-white flex items-center justify-between rounded-t-2xl">
            <div>
              <h3 className="font-semibold">நேரலை உதவி</h3>
              <p className="text-xs opacity-90">ஒருங்கிணைப்பாளருடன் உரையாடுங்கள்</p>
            </div>
            <button onClick={onClose} className="p-1 rounded-full hover:bg-white/20"><X className="w-5 h-5" /></button>
          </div>
          <div className="flex-grow p-4 overflow-y-auto space-y-4 bg-gray-100">
            {messages.map(msg => (
              <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] p-3 rounded-2xl text-sm ${msg.sender === 'user' ? 'bg-blue-500 text-white rounded-br-none' : 'bg-white text-gray-800 rounded-bl-none'}`}>
                  {msg.text}
                </div>
              </div>
            ))}
            {isBotTyping && (
              <div className="flex justify-start">
                <div className="max-w-[80%] p-3 rounded-2xl text-sm bg-white text-gray-800 rounded-bl-none">
                  <div className="flex items-center gap-1.5"><div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce [animation-delay:-0.3s]"></div><div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce [animation-delay:-0.15s]"></div><div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce"></div></div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          {!isComplete && (
            <form onSubmit={handleSend} className="p-3 border-t bg-white flex items-center gap-2">
              <input value={userInput} onChange={e => setUserInput(e.target.value)} placeholder="உங்கள் பதிலை உள்ளிடவும்..." className="flex-grow bg-gray-100 border-gray-200 border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500" />
              <button type="submit" className="p-2.5 bg-red-600 text-white rounded-full hover:bg-red-700 disabled:bg-gray-400" disabled={!userInput.trim() || isBotTyping}><Send className="w-5 h-5" /></button>
            </form>
          )}
        </Card>
      </motion.div>
    </motion.div>
  );
}

function PrivacyPolicyPage() {
  const navigate = useNavigate();
  const content = `
உங்கள் தனியுரிமை எங்களுக்கு மிகவும் முக்கியம். தமிழக வாழ்வுரிமைக் கட்சியின் இந்தச் செயலி, உங்கள் தனிப்பட்ட தகவல்களை எவ்வாறு சேகரிக்கிறது, பயன்படுத்துகிறது, மற்றும் பாதுகாக்கிறது என்பதை இந்தத் தனியுரிமைக் கொள்கை விளக்குகிறது.

1. நாங்கள் சேகரிக்கும் தகவல்கள்:
  * தனிப்பட்ட அடையாளத் தகவல்கள்: பெயர், தந்தை/கணவர் பெயர், பிறந்த தேதி, பாலினம்.
  * தொடர்புத் தகவல்கள்: தொலைபேசி எண், மின்னஞ்சல் முகவரி, வீட்டு முகவரி.
  * அரசு அடையாளத் தகவல்கள்: வாக்காளர் அடையாள அட்டை எண், ஆதார் எண் (விருப்பப்பட்டால்).
  * பிற தகவல்கள்: புகைப்படம், கல்வித் தகுதி, தொழில், குருதி வகை.

2. தகவல்களைப் பயன்படுத்தும் விதம்:
  * உறுப்பினர் பதிவேட்டை நிர்வகிக்கவும், கட்சி உறுப்பினர்களை அடையாளம் காணவும்.
  * கட்சி தொடர்பான அறிவிப்புகள், நிகழ்வுகள், மற்றும் செய்திகளை உங்களுக்கு அனுப்ப.
  * கட்சியின் உள் அமைப்பு மற்றும் நிர்வாக மேம்பாட்டிற்காக.
  * உங்கள் பகுதியில் கட்சியின் செயல்பாடுகளை ஒருங்கிணைக்க.

3. தகவல்களின் பாதுகாப்பு:
  உங்கள் தனிப்பட்ட தகவல்களின் பாதுகாப்பை நாங்கள் உறுதி செய்கிறோம். அங்கீகரிக்கப்படாத அணுகல், மாற்றம் அல்லது அழிவிலிருந்து உங்கள் தரவைப் பாதுகாக்க, நாங்கள் உரிய தொழில்நுட்ப மற்றும் நிர்வாகப் பாதுகாப்பு நடவடிக்கைகளை மேற்கொள்கிறோம்.

4. தகவல்களைப் பகிர்தல்:
  சட்டப்படி தேவைப்பட்டாலன்றி, உங்கள் அனுமதியின்றி உங்கள் தனிப்பட்ட தகவல்களை எந்த மூன்றாம் தரப்பினருடனும் நாங்கள் பகிர மாட்டோம். கட்சியின் உள் நிர்வாகத் தேவைகளுக்கு மட்டுமே உங்கள் தகவல்கள் பயன்படுத்தப்படும்.

5. கொள்கை மாற்றங்கள்:
  இந்தத் தனியுரிமைக் கொள்கையில் மாற்றங்கள் செய்யப்படும்போது, இந்தச் செயலி மூலம் உங்களுக்குத் தெரிவிக்கப்படும்.

மேலும் கேள்விகளுக்கு, itwing@tvkparty.org என்ற மின்னஞ்சலில் எங்களைத் தொடர்பு கொள்ளவும்.
  `;
  return <TextPageModal title="தனியுரிமைக் கொள்கை" content={content} onClose={() => navigate(-1)} />;
}

function TermsPage() {
  const navigate = useNavigate();
  const content = `
தமிழக வாழ்வுரிமைக் கட்சியின் உறுப்பினர் செயலிக்கு வரவேற்கிறோம். இந்தச் செயலியைப் பயன்படுத்துவதன் மூலம், நீங்கள் பின்வரும் விதிமுறைகள் மற்றும் நிபந்தனைகளை ஏற்றுக்கொள்கிறீர்கள்:

1. உறுப்பினர் தகுதி:
  * நீங்கள் வழங்கும் அனைத்துத் தகவல்களும் உண்மையானவை மற்றும் சரியானவை என்பதை உறுதிப்படுத்த வேண்டும்.
  * கட்சியின் கொள்கைகள் மற்றும் நோக்கங்களை ஏற்றுக்கொள்பவராக இருக்க வேண்டும்.

2. செயலியின் பயன்பாடு:
  * இந்தச் செயலி, கட்சியின் தகவல் தொடர்பு மற்றும் நிர்வாக நோக்கங்களுக்காக மட்டுமே பயன்படுத்தப்பட வேண்டும்.
  * கட்சியின் நற்பெயருக்குக் களங்கம் விளைவிக்கும் வகையிலோ, சட்டவிரோத செயல்களுக்கோ இந்தச் செயலியைப் பயன்படுத்தக் கூடாது.

3. நடத்தை விதிகள்:
  * பிற உறுப்பினர்கள் அல்லது தலைவர்களைப் பற்றி அவதூறாகப் பேசுவதோ, தவறான தகவல்களைப் பரப்புவதோ கூடாது.
  * கட்சியின் கொள்கைகளுக்கு முரணான எந்தவொரு செயலிலும் ஈடுபடக் கூடாது.

4. உறுப்பினர் தகுதி நீக்கம்:
  * மேற்கண்ட விதிமுறைகளை மீறுவது, கட்சியின் கட்டுப்பாட்டுக் குழுவின் முடிவின்படி, உங்கள் உறுப்பினர் தகுதியை இழக்கச் செய்யலாம்.
  * இந்தச் செயலியைத் தவறாகப் பயன்படுத்துவது உங்கள் கணக்கை முடக்க வழிவகுக்கும்.

5. பொறுப்புத் துறப்பு:
  இந்தச் செயலியில் உள்ள தகவல்கள் அவ்வப்போது மாற்றங்களுக்கு உட்படலாம். கட்சியின் அதிகாரப்பூர்வ அறிவிப்புகளே இறுதியானதாகக் கருதப்படும்.

இந்த விதிமுறைகள் தொடர்பான கேள்விகளுக்கு, itwing@tvkparty.org என்ற மின்னஞ்சலில் எங்களைத் தொடர்பு கொள்ளவும்.
  `;
  return <TextPageModal title="விதிமுறைகள் மற்றும் நிபந்தனைகள்" content={content} onClose={() => navigate(-1)} />;
}

function ProfilePage() {
  const { role, logout } = useAuth();
  const { push } = useToasts();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const photoInputRef = React.useRef(null);
  const [referralLink, setReferralLink] = useState(null);
  const [qrCodeUrl, setQrCodeUrl] = useState(null);
  const [passwords, setPasswords] = useState({ current: '', new: '', confirm: '' });
  const [is2FAModalOpen, setIs2FAModalOpen] = useState(false);
  const { user: authUser, refetch: refetchUser } = useAuth();
  const [user, setUser] = useState(authUser);

  // Password strength logic
  function getPasswordStrength(pw) {
    let score = 0;
    const guidelines = [
      { label: 'குறைந்தது 8 எழுத்துகள்', test: pw.length >= 8 },
      { label: 'ஒரு பெரிய எழுத்து', test: /[A-Z]/.test(pw) },
      { label: 'ஒரு சிறிய எழுத்து', test: /[a-z]/.test(pw) },
      { label: 'ஒரு எண்', test: /[0-9]/.test(pw) },
      { label: 'ஒரு சிறப்பு எழுத்து (!@#$%^&*)', test: /[!@#$%^&*]/.test(pw) },
    ];
    guidelines.forEach(g => { if (g.test) score++; });
    return { score, guidelines };
  }

  const passwordStrength = getPasswordStrength(passwords.new);
  const passwordsMatch = passwords.new && passwords.confirm && passwords.new === passwords.confirm;

  // Session timer: 30 minutes per session. When it reaches 0, auto-logout.
  const SESSION_DURATION = 30 * 60; // seconds
  const [sessionSeconds, setSessionSeconds] = useState(SESSION_DURATION);
  const sessionIntervalRef = React.useRef(null);

  const formatTime = (secs) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  useEffect(() => {
    // start countdown when component mounts
    sessionIntervalRef.current = setInterval(() => {
      setSessionSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(sessionIntervalRef.current);
          push('சேவையின் காலாவதி. உள்நுழைவிலிருந்து வெளியேறப்படுகிறது.', 'info');
          logout();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (sessionIntervalRef.current) clearInterval(sessionIntervalRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setPasswordField = (key) => (e) => {
    setPasswords(p => ({ ...p, [key]: e.target.value }));
  };

  const handlePasswordChange = async () => {
    if (!passwords.current || !passwords.new || !passwords.confirm) {
      return push('அனைத்து கடவுச்சொல் புலங்களையும் நிரப்பவும்', 'error');
    }
    if (passwords.new !== passwords.confirm) {
      return push('புதிய கடவுச்சொற்கள் பொருந்தவில்லை', 'error');
    }
    if (passwordStrength.score < 4) {
      return push('கடவுச்சொல் போதுமான வலிமையுடன் இல்லை', 'error');
    }
    setLoading(true);
    try {
      await apiFetch('/me/password', {
        method: 'PUT',
        body: {
          currentPassword: passwords.current,
          newPassword: passwords.new
        }
      });
      push('கடவுச்சொல் வெற்றிகரமாக மாற்றப்பட்டது', 'success');
      setPasswords({ current: '', new: '', confirm: '' });
    } catch (err) {
      push(`கடவுச்சொல்லை மாற்ற முடியவில்லை: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authUser) {
      setUser(authUser);
      setLoading(false);
    }
  }, [authUser, setLoading]);

  const handleFieldChange = (key) => (e) => {
    setUser(u => ({ ...u, [key]: e.target.value }));
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setUser(u => ({ ...u, photo: file }));
      setPhotoPreview(URL.createObjectURL(file));
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('name', user.name);
      formData.append('phone', user.phone);
      formData.append('email', user.email);
      // Only append photo if it's a new file, not a URL string
      if (user.photo && typeof user.photo !== 'string') {
        formData.append('photo', user.photo);
      }
      await apiFetch('/me', { method: 'PATCH', body: formData });
      push('சுயவிவரம் வெற்றிகரமாக புதுப்பிக்கப்பட்டது', 'success');
      if (typeof refetchUser === 'function') {
        const result = await refetchUser();
        if (result && result.data) {
          setUser(result.data);
        }
      } else {
        console.warn('refetchUser is not a function. Make sure AuthProvider is used.');
      }
    } catch (err) {
      push('சுயவிவரத்தை புதுப்பிக்க முடியவில்லை: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const generateReferralLink = () => {
    // In a real app, this ID would be fetched from the backend or be the user's unique ID.
    const referralId = `ref_${user.phone}_${Math.random().toString(36).substring(2, 10)}`;
    const link = `https://join.tvkparty.org/join?ref=${referralId}`;
    setReferralLink(link);
    setQrCodeUrl(`https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=${encodeURIComponent(link)}`);
    push('பரிந்துரை இணைப்பு உருவாக்கப்பட்டது!', 'success');
  };

  const copyToClipboard = () => {
    if (!referralLink || !user) return;
    const referralMessage = `வணக்கம், நான் ${user.name}, உங்கள் மாவட்டத்தில் தகவல் தொழில்நுட்பப் பிரிவில் பணியாற்றி வருகிறேன், நீங்கள் கட்சியில் இணைய விரும்பினால் கீழே இணைத்துள்ள இணைப்பின் மூலம் இணைந்து கொள்ளலாம். மேலும் ஏதேனும் சந்தேகம் இருந்தால் என்னை அழைக்காலம்.

அலைப்பேசி எண் : ${user.phone || 'N/A'}
மின்னஞ்சல் முகவரி : ${user.email || 'N/A'}
உறவாய் இணைய!
${referralLink}`;
    navigator.clipboard.writeText(referralMessage);
    push('பரிந்துரை செய்தி நகலெடுக்கப்பட்டது!', 'success');
  };

  const handle2FASuccess = () => {
    // Refetch user data to get the latest mfaEnabled status
    refetchUser(); // This will trigger the useEffect to update local user state
  };
  return (
    <Shell>
      {/* Session timer card at top of profile page */}
      <Section>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="relative overflow-hidden bg-gradient-to-br from-green-50 to-blue-50 dark:from-gray-800 dark:to-gray-800 p-4 mb-4">
            <div className="absolute inset-0 bg-grid-gray-900/5 [mask-image:linear-gradient(0deg,transparent,black)]" />
            <div className="relative flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">அமர்வு நேரம்</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">உங்கள் அமர்வு மேலிருப்பது</p>
              </div>
              <div className="text-right">
                <motion.div
                  className="text-2xl font-mono font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent"
                  animate={{ scale: [1, 1.03, 1] }}
                  transition={{ duration: 1, repeat: Infinity, repeatDelay: 1 }}
                >
                  {formatTime(sessionSeconds)}
                </motion.div>
                <div className="text-xs text-gray-500 dark:text-gray-400">சேவை காலம் முடிவு அன்று தானாக வெளியேறப்படுகிறது</div>
              </div>
            </div>
          </Card>
        </motion.div>
      </Section>
      <Section>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Card className="relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-green-400/20 to-blue-400/20 dark:from-green-900/20 dark:to-blue-900/20" />
            <div className="relative p-6 space-y-6">
              <div className="flex flex-col items-center gap-4">
                <motion.div
                  className="relative group"
                  whileHover={{ scale: 1.05 }}
                  transition={{ type: "spring", stiffness: 300, damping: 20 }}
                >
                  <div className="relative w-32 h-32">
                    <div className="absolute inset-0 bg-gradient-to-br from-green-400 to-blue-500 rounded-full animate-spin-slow opacity-75 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute inset-1 bg-white dark:bg-gray-800 rounded-full" />
                    <img
                      src={photoPreview || user?.photo || partyLogo}
                      alt="Profile"
                      className="absolute inset-2 w-28 h-28 rounded-full object-cover"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => photoInputRef.current?.click()}
                    className="absolute bottom-0 right-0 p-2 rounded-full bg-gradient-to-br from-green-500 to-blue-500 text-white shadow-lg transform group-hover:scale-110 transition-transform"
                  >
                    <Edit3 className="w-5 h-5" />
                  </button>
                  <input type="file" ref={photoInputRef} className="hidden" accept="image/*" onChange={handlePhotoChange} />
                </motion.div>
                <div className="text-center space-y-1">
                  <motion.h2
                    className="text-2xl font-bold bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.2 }}
                  >
                    {user?.name}
                  </motion.h2>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="inline-flex items-center px-3 py-1 rounded-full bg-gradient-to-r from-green-100 to-blue-100 dark:from-green-900/50 dark:to-blue-900/50"
                  >
                    <BadgeCheck className="w-4 h-4 mr-1 text-green-600 dark:text-green-400" />
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200 capitalize">{role}</span>
                  </motion.div>
                </div>
              </div>

              <div className="space-y-4">
                <TextInput label="முழு பெயர்" required value={user.name} onChange={handleFieldChange('name')} />
                <TextInput label="தொலைபேசி எண்" required value={user.phone} onChange={handleFieldChange('phone')} />
                <TextInput label="மின்னஞ்சல்" type="email" value={user.email} onChange={handleFieldChange('email')} />
              </div>
              <div className="flex justify-end">
                <button type="button" onClick={handleProfileUpdate} disabled={loading} className="px-6 py-2 bg-blue-600 text-white rounded-xl text-sm disabled:bg-blue-400">
                  {loading ? 'சேமிக்கிறது...' : 'சுயவிவரத்தை சேமி'}
                </button>
              </div>
              <div className="space-y-4 pt-6 border-t">
                <p className="text-sm text-gray-600">கடவுச்சொல்லை மாற்ற, கீழே உள்ள புலங்களை நிரப்பவும். இல்லையெனில், காலியாக விடவும்.</p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <TextInput label="தற்போதைய கடவுச்சொல்" type="password" value={passwords.current} onChange={setPasswordField('current')} autoComplete="current-password" name="current-password" />
                  </div>
                  <div>
                    <TextInput label="புதிய கடவுச்சொல்" type="password" value={passwords.new} onChange={setPasswordField('new')} autoComplete="new-password" name="new-password" />
                    {/* Password strength and guidelines */}
                    {passwords.new && (
                      <div className="mt-1">
                        <div className="h-2 w-full rounded bg-gray-200 overflow-hidden mb-1">
                          <div className={`h-2 rounded transition-all duration-300 ${passwordStrength.score <= 2 ? 'bg-red-500 w-1/4' : passwordStrength.score === 3 ? 'bg-yellow-400 w-2/4' : passwordStrength.score === 4 ? 'bg-blue-400 w-3/4' : 'bg-green-500 w-full'}`}></div>
                        </div>
                        <ul className="text-xs space-y-0.5">
                          {passwordStrength.guidelines.map((g, i) => (
                            <li key={i} className={`flex items-center gap-1 ${g.test ? 'text-green-600' : 'text-gray-400'}`}>
                              {g.test ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {g.label}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div>
                    <TextInput label="புதிய கடவுச்சொல்லை உறுதிப்படுத்தவும்" type="password" value={passwords.confirm} onChange={setPasswordField('confirm')} autoComplete="new-password" name="confirm-password" />
                    {/* Password match indicator */}
                    {passwords.confirm && (
                      <div className="mt-1 text-xs flex items-center gap-1">
                        {passwordsMatch ? (
                          <><CheckCircle2 className="w-4 h-4 text-green-600" /> <span className="text-green-600">கடவுச்சொற்கள் பொருந்துகின்றன</span></>
                        ) : (
                          <><XCircle className="w-4 h-4 text-red-500" /> <span className="text-red-500">கடவுச்சொற்கள் பொருந்தவில்லை</span></>
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex justify-end pt-2">
                  <button type="button" onClick={handlePasswordChange} disabled={loading} className="px-6 py-2 bg-green-600 text-white rounded-xl text-sm disabled:bg-green-400">
                    {loading ? 'சேமிக்கிறது...' : 'கடவுச்சொல்லை மாற்று'}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <button type="button" onClick={logout} className="px-6 py-2.5 bg-gray-200 text-gray-800 rounded-xl text-sm w-full">
                  வெளியேறு
                </button>
                <button type="button" onClick={() => navigate('/help')} className="px-6 py-2.5 bg-blue-100 text-blue-800 rounded-xl text-sm w-full flex items-center justify-center gap-2">
                  <HelpCircle className="w-4 h-4" /> உதவி
                </button>
              </div>
            </div>
          </Card>
        </motion.div>
      </Section>
      <Section title="பரிந்துரை இணைப்பு">
        <div className="grid grid-cols-2 gap-4">
          <StatCard icon={Users} label="மொத்த பரிந்துரைகள்" value="12" />
          <StatCard icon={Clock} label="நிலுவையில் உள்ளவை" value="3" />
        </div>
        <Card className="p-4 mt-4">
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-grow w-full">
              {!referralLink && (
                <button onClick={generateReferralLink} className="w-full md:w-auto px-4 py-2 bg-blue-600 text-white rounded-xl text-sm">இணைப்பை உருவாக்கு</button>
              )}
              {referralLink && (
                <div>
                  <label className="text-sm font-medium text-gray-700">உங்கள் பரிந்துரை இணைப்பு (நகலெடுக்க கிளிக் செய்யவும்)</label>
                  <div onClick={copyToClipboard} className="mt-1 p-2 bg-gray-100 rounded-lg text-sm text-gray-800 cursor-pointer active:bg-gray-200 truncate">{referralLink}</div>
                </div>
              )}
            </div>
            {qrCodeUrl && (
              <div className="flex-shrink-0">
                <img src={qrCodeUrl} alt="Referral QR Code" className="w-24 h-24 rounded-lg border p-1" />
              </div>
            )}
          </div>
        </Card>
      </Section>
      <AnimatePresence>
        {is2FAModalOpen && (
          <Enable2FAModal
            isOpen={is2FAModalOpen}
            onClose={() => setIs2FAModalOpen(false)}
            onSuccess={handle2FASuccess}
            mfaEnabled={user.mfaEnabled}
          />
        )}
      </AnimatePresence>
    </Shell>
  );
}

function Enable2FAModal({ isOpen, onClose, onSuccess, mfaEnabled }) {
  const { push } = useToasts();
  const [step, setStep] = useState(mfaEnabled ? 'disable' : 'setup'); // 'setup', 'verify', 'disable'
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && !mfaEnabled && step !== 'setup' && step !== 'verify') {
      const setup = async () => {
        setLoading(true);
        setError('');
        try {
          const { secret: aSecret, otpauthUrl: dataUrl } = await generate2FASecret();
          setQrCode(dataUrl);
          setSecret(aSecret);
          setStep('setup');
        } catch (err) {
          setError('QR குறியீட்டைப் பெறுவதில் தோல்வி. மீண்டும் முயற்சிக்கவும்.');
          push('2FA அமைவு தோல்வி: ' + err.message, 'error');
        } finally {
          setLoading(false);
        }
      };
      setup();
    } else if (isOpen && mfaEnabled && step !== 'disable') {
      setStep('disable');
    }
  }, [isOpen, mfaEnabled, push, step]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await apiFetch('/auth/verify-2fa-setup', { method: 'POST', body: { otp } });
      push('2FA வெற்றிகரமாக இயக்கப்பட்டது!', 'success');
      onSuccess();
      onClose();
    } catch (err) {
      setError('தவறான OTP. மீண்டும் முயற்சிக்கவும்.');
      push('OTP சரிபார்ப்பு தோல்வி: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDisable = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await apiFetch('/auth/disable-2fa', { method: 'POST', body: { password } });
      push('2FA வெற்றிகரமாக முடக்கப்பட்டது!', 'success');
      onSuccess();
      onClose();
    } catch (err) {
      setError('தவறான கடவுச்சொல் அல்லது பிழை ஏற்பட்டது.');
      push('2FA முடக்கம் தோல்வி: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const renderContent = () => {
    if (loading && step !== 'disable') return <div className="flex justify-center items-center h-48"><Loader2 className="w-8 h-8 animate-spin" /></div>;
    if (error && step !== 'disable') return <div className="text-center text-red-500 p-4">{error}</div>;

    switch (step) {
      case 'setup':
        return <div className="text-center"><h4 className="font-semibold mb-2">Authenticator செயலியில் ஸ்கேன் செய்யவும்</h4><p className="text-xs text-gray-500 mb-4">Google Authenticator அல்லது Authy போன்ற செயலியைப் பயன்படுத்தவும்.</p>{qrCode && <img src={qrCode} alt="2FA QR Code" className="mx-auto border p-2 rounded-lg bg-white" />}<p className="text-xs text-gray-500 mt-4">அல்லது இந்த ரகசிய விசையைப் பயன்படுத்தவும்:</p><p className="font-mono bg-gray-100 p-2 rounded-md text-sm my-2 cursor-pointer" onClick={() => { navigator.clipboard.writeText(secret); push('ரகசிய விசை நகலெடுக்கப்பட்டது!', 'success'); }}>{secret}</p><button onClick={() => setStep('verify')} className="mt-4 w-full px-4 py-2 bg-green-600 text-white rounded-xl text-sm">அடுத்து</button></div>;
      case 'verify':
        return <form onSubmit={handleVerify} className="space-y-4"><h4 className="font-semibold text-center">சரிபார்ப்புக் குறியீட்டை உள்ளிடவும்</h4><p className="text-xs text-gray-500 text-center -mt-2 mb-4">உங்கள் authenticator செயலியிலிருந்து 6-இலக்கக் குறியீட்டை உள்ளிடவும்.</p><OtpInput value={otp} onChange={setOtp} /><button type="submit" disabled={loading || otp.length !== 6} className="w-full px-4 py-2 bg-green-600 text-white rounded-xl text-sm disabled:bg-gray-400">{loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'சரிபார்த்து இயக்கு'}</button>{error && <p className="text-red-500 text-sm text-center">{error}</p>}</form>;
      case 'disable':
        return <form onSubmit={handleDisable} className="space-y-4"><h4 className="font-semibold text-center">2FA-ஐ முடக்கவும்</h4><p className="text-xs text-gray-500 text-center -mt-2 mb-4">தொடர, உங்கள் கணக்கின் கடவுச்சொல்லை உள்ளிடவும்.</p><LoginTextInput label="கடவுச்சொல்" type="password" value={password} onChange={e => setPassword(e.target.value)} required /><button type="submit" disabled={loading} className="w-full px-4 py-2 bg-red-600 text-white rounded-xl text-sm disabled:bg-gray-400">{loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : 'உறுதிசெய்து முடக்கு'}</button>{error && <p className="text-red-500 text-sm text-center">{error}</p>}</form>;
      default: return null;
    }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onClose}>
      <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }} className="relative z-10 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <Card className="p-6"><button onClick={onClose} className="absolute top-3 right-3 p-1 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"><X className="w-5 h-5 text-gray-500" /></button>{renderContent()}</Card>
      </motion.div>
    </motion.div>
  );
}

function AdminPage() {
  const { role } = useAuth();
  const { push } = useToasts();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(null);
  const [newRole, setNewRole] = useState('member');
  const canAccess = RBAC[role]?.canAccessAdmin;

  useEffect(() => {
    if (!canAccess) { setLoading(false); return; }
    setLoading(true);
    apiFetch('/admin/users?limit=50').then(setUsers).catch(err => push('Failed to load users: ' + err.message, 'error')).finally(() => setLoading(false));
  }, [canAccess, push]);

  const openModal = (u) => { setCurrent(u); setNewRole(u.role); setOpen(true); };
  const closeModal = () => { setOpen(false); setCurrent(null); };
  async function applyRole() {
    if (!current) return; try { await apiFetch(`/admin/users/${encodeURIComponent(current.id)}/role`, { method: 'PATCH', body: { role: newRole }, retry: 2 }); setUsers(prev => prev.map(u => u.id === current.id ? { ...u, role: newRole } : u)); closeModal(); push('Role updated', 'success'); } catch (err) { push('Role மாற்ற முடியவில்லை: ' + err.message, 'error'); }
  }
  if (!canAccess) return (<Shell><Card className="p-4 text-sm text-red-800 dark:text-red-200 bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800/30">Admin Panel-ஐ அணுக <b>admin role</b> தேவை. உங்கள் role: <b>{role}</b></Card></Shell>);
  return (
    <Shell>
      <Section title="Admin Panel">
        <div className="flex flex-col sm:flex-row items-center gap-2 mb-4">
          <div className="flex-grow" />
          {canAccess && (
            <button onClick={() => navigate('/members/new')} className="w-full sm:w-auto px-4 py-2.5 bg-green-600 text-white rounded-xl text-sm whitespace-nowrap">+ புதிய பயனர்</button>
          )}
        </div>
        <div className="overflow-x-auto">
          <Card className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700"><tr className="text-left text-gray-500 dark:text-gray-300"><th className="py-2 px-3 font-semibold">பெயர்</th><th className="py-2 px-3 font-semibold">தொலைபேசி</th><th className="py-2 px-3 font-semibold">Role</th><th className="py-2 px-3 font-semibold">Action</th></tr></thead>
              <tbody className="dark:text-gray-200">
                {loading ? (
                  Array.from({ length: 5 }).map((_, i) => <tr key={i}><td colSpan="4"><Skeleton className="h-10 my-1.5" /></td></tr>)
                ) : (
                  users.map(u => (
                    <tr key={u.id} className="border-t dark:border-gray-700">
                      <td className="py-2 px-3">{u.name}</td>
                      <td className="py-2 px-3">{u.phone}</td>
                      <td className="py-2 px-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.role === 'admin' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-800'}`}>{u.role}</span></td>
                      <td className="py-2 px-3"><button onClick={() => openModal(u)} className="px-3 py-1.5 bg-gray-800 text-white rounded-lg text-xs hover:bg-black">Change Role</button></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            {!loading && users.length === 0 && (
              <div className="text-center py-8 text-gray-500">No users found.</div>
            )}
          </Card>
        </div>
      </Section>
      <AnimatePresence>
        {open && current && (
          <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/40" onClick={closeModal} />
            <Card key={current?.id} className="relative z-10 w-[92%] max-w-sm p-4">
              <div className="text-base font-semibold mb-2">Change Role</div>
              <div className="text-sm text-gray-600 mb-3">User: {current?.name}</div>
              <SelectInput label="புதிய Role" value={newRole} onChange={(e) => setNewRole(e.target.value)}>
                <option value="member">member</option>
                <option value="district">district</option>
                <option value="state">state</option>
                <option value="admin">admin</option>
              </SelectInput>
              <div className="mt-4 flex items-center gap-2">
                <button onClick={applyRole} className="px-4 py-2 bg-green-600 text-white rounded-xl text-sm">சேமி</button>
                <button onClick={closeModal} className="px-4 py-2 bg-gray-200 rounded-xl text-sm">ரத்து</button>
              </div>
            </Card>
          </div>
        )}
      </AnimatePresence>
    </Shell>
  );
}

function SettingsPage() {
  const { logout, user } = useAuth();
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);

  const [is2FAModalOpen, setIs2FAModalOpen] = useState(false);
  // Force Tamil language and remove language switcher
  useEffect(() => {
    if (i18n.language !== 'ta') {
      i18n.changeLanguage('ta');
    }
  }, [i18n]);

  const handle2FASuccess = () => {
    // In a real app, you'd refetch user data here to update the UI
    // For now, we can just close the modal.
  };

  const settingsItems = [
    {
      title: t('settings_profile_title'),
      description: t('settings_profile_desc'),
      icon: UserIcon,
      onClick: () => navigate('/profile'),
      gradient: 'from-blue-400 to-blue-600',
      className: 'hover:shadow-blue-200 dark:hover:shadow-blue-900/20',
    },
    {
      title: t('settings_appearance_title'),
      description: t('settings_appearance_desc', { theme: theme === 'dark' ? t('dark_theme') : t('light_theme') }),
      icon: theme === 'dark' ? Moon : Sun,
      onClick: toggleTheme,
      gradient: 'from-purple-400 to-purple-600',
      className: 'hover:shadow-purple-200 dark:hover:shadow-purple-900/20',
    },
    {
      title: t('settings_security_title'),
      description: user?.mfaEnabled ? '2FA இயக்கப்பட்டது. உங்கள் கணக்கைப் பாதுகாக்கவும்.' : '2FA முடக்கப்பட்டது. உங்கள் கணக்கைப் பாதுகாக்கவும்.',
      icon: ShieldCheck,
      onClick: () => setIs2FAModalOpen(true),
      gradient: 'from-green-400 to-green-600',
      className: 'hover:shadow-green-200 dark:hover:shadow-green-900/20',

    },
    {
      title: t('settings_notifications_title'),
      description: t('settings_notifications_desc', { status: notificationsEnabled ? t('notifications_enabled') : t('notifications_disabled') }),
      icon: notificationsEnabled ? Bell : BellOff,
      onClick: () => setNotificationsEnabled(n => !n),
      gradient: notificationsEnabled ? 'from-amber-400 to-amber-600' : 'from-gray-400 to-gray-600',
      className: notificationsEnabled ? 'hover:shadow-amber-200 dark:hover:shadow-amber-900/20' : 'hover:shadow-gray-200 dark:hover:shadow-gray-900/20',
    },
    {
      title: t('settings_logout_title'),
      description: t('settings_logout_desc'),
      icon: LogOut,
      onClick: logout,
      gradient: 'from-red-400 to-red-600',
      className: 'hover:shadow-red-200 dark:hover:shadow-red-900/20',
    },
  ];

  return (
    <Shell>
      <Section className="h-full overflow-auto pb-safe-area-inset-bottom">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          className="max-w-3xl mx-auto min-h-full"
        >
          <div className="sticky top-0 z-10 mb-6 sm:mb-8 text-center px-4 pt-2 pb-4 bg-gradient-to-b from-gray-50 dark:from-gray-900 to-transparent">
            <h1 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-green-500 to-green-700 bg-clip-text text-transparent">{t('settings')}</h1>
            <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">உங்கள் கணக்கு மற்றும் பயன்பாட்டு விருப்பங்களை நிர்வகிக்கவும்</div>
          </div>

          <div className="flex-1 grid gap-3 sm:gap-4 px-3 sm:px-0">
            {settingsItems.map((item, index) => (
              <motion.div
                key={index}
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: index * 0.1 }}
              >
                <Card
                  onClick={item.onClick}
                  className={`group py-3.5 px-3 sm:p-4 flex items-start sm:items-center gap-3 sm:gap-4 cursor-pointer bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm hover:shadow-lg transition-all duration-300 ${item.className || ''}`}
                >
                  <div className={`relative p-2.5 sm:p-3 rounded-xl bg-gradient-to-br ${item.gradient} text-white shadow-lg group-hover:scale-110 transition-transform duration-300 mt-0.5 sm:mt-0`}>
                    <item.icon className="w-5 h-5 sm:w-6 sm:h-6" />
                    <div className="absolute inset-0 rounded-xl bg-white opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
                  </div>
                  <div className="flex-1 min-w-0 pr-2">
                    <h4 className="font-semibold text-gray-800 dark:text-gray-100 text-sm sm:text-base truncate">{item.title}</h4>
                    <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 line-clamp-2 sm:line-clamp-1 leading-tight sm:leading-normal">{item.description}</p>
                  </div>
                  <motion.div
                    className="text-gray-400 flex-shrink-0"
                    initial={false}
                    whileHover={{ x: 5 }}
                    transition={{ type: "spring", stiffness: 400, damping: 25 }}
                  >
                    <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5" />
                  </motion.div>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="mt-auto pt-6 sm:pt-8 text-center pb-20 sm:pb-8">
            <motion.div
              className="inline-block"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.6 }}
            >
              <div className="font-medium text-sm sm:text-base text-gray-600 dark:text-gray-300">
                தமிழக வாழ்வுரிமைக் கட்சி
              </div>
              <div className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                தகவல் தொழில்நுட்பப்பிரிவு
              </div>
              <div className="mt-2 text-[10px] sm:text-xs text-gray-400 dark:text-gray-500">
                Version 1.0.0
              </div>
            </motion.div>
          </div>
        </motion.div>
      </Section>
      <AnimatePresence>
        {is2FAModalOpen && (
          <Enable2FAModal
            isOpen={is2FAModalOpen}
            onClose={() => setIs2FAModalOpen(false)}
            onSuccess={handle2FASuccess}
            mfaEnabled={user?.mfaEnabled}
          />
        )}
      </AnimatePresence>
    </Shell>
  );
}

/** Login Page */
const LoginTextInput = ({ label, required, className, type, ...props }) => {
  const [showPassword, setShowPassword] = useState(type !== 'password');
  const isPassword = type === 'password';
  const Icon = isPassword ? ShieldCheck : Phone;
  const inputId = `login-${props.name}`;

  return (
    <div className={className || ''}>
      <label htmlFor={inputId} className="block text-sm font-semibold text-gray-700 dark:text-gray-300">
        {label}{required && <span className="text-red-400"> *</span>}
      </label>
      <div className="relative mt-1">
        <Icon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" aria-hidden="true" />
        <input
          {...props}
          id={inputId}
          type={isPassword ? (showPassword ? 'text' : 'password') : type}
          className={`input-field w-full pl-11 pr-4 py-3 rounded-xl border-2 border-transparent bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-500 ${props.disabled ? 'bg-gray-200 dark:bg-gray-700' : ''} ${isPassword ? 'pr-10' : ''}`}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword(s => !s)}
            aria-label={showPassword ? "கடவுச்சொல்லை மறை" : "கடவுச்சொல்லைக் காட்டு"}
            className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 rounded-full"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        )}
      </div>
    </div>
  );
};

function LoginPage() {
  const { theme } = useTheme();
  const { login } = useAuth();
  const { push } = useToasts();
  const navigate = useNavigate();
  const [form, setForm] = useState({ phone: '', password: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [viewMode, setViewMode] = useState('login'); // 'login', 'login-2fa', 'forgot-phone', 'forgot-reset'
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    const rememberedPhone = localStorage.getItem('tvk_remember_phone');
    if (rememberedPhone) {
      setForm(f => ({ ...f, phone: rememberedPhone }));
      setRememberMe(true);
    }
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      // Pass rememberMe state to the login function
      const result = await login(form, rememberMe);
      if (result?.mfaRequired) {
        setForm(f => ({ ...f, mfaToken: result.mfaToken }));
        setViewMode('login-2fa');
        push('OTP உங்கள் தொலைபேசிக்கு அனுப்பப்பட்டது', 'success');
      }
    } catch (err) {
      if (err.status === 401 || (err.message && err.message.includes('Unauthorized'))) {
        push('தவறான தொலைபேசி அல்லது கடவுச்சொல்.', 'error'); // Invalid phone or password.
        setError('தவறான தொலைபேசி அல்லது கடவுச்சொல்.');
      } else {
        setError('Login தோல்வி: ' + err.message);
        push('Login தோல்வி: ' + err.message, 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const slogans = [
    "உரிமைகளை மீட்போம்! உலகை ஆள்வோம்!",
    "மாற்றத்தை நோக்கிய முதல் படி, உங்கள் பங்களிப்பு.",
    "இணைவோம், வெல்வோம், தமிழகத்தை வாழ்விப்போம்.",
    "உங்கள் குரல், ನಮ್ಮ கட்சியின் பலம்.",
    "ஒவ்வொரு உறுப்பினரும் ஒரு புதிய நம்பிக்கை.",
    "இளைஞர்களின் శక్తి, கட்சியின் வெற்றி.",
    "நாளை நமதே! இன்றே தொடங்குவோம்!",
    "தமிழர் வாழ்வுரிமை, நமது இலக்கு.",
    "ஒன்றிணைந்த கரங்கள், உயர்ந்த தமிழகம்.",
    "உங்கள் வருகை, ஒரு புதிய தொடக்கம்."
  ];
  const [currentSloganIndex, setCurrentSloganIndex] = useState(0);

  useEffect(() => {
    const sloganInterval = setInterval(() => {
      setCurrentSloganIndex(prevIndex => (prevIndex + 1) % slogans.length);
    }, 8788); // Change slogan every 3 seconds
    return () => clearInterval(sloganInterval);
  }, [slogans.length]);

  const TwoFactorAuthView = () => {
    const { verify2FA } = useAuth(); // Assuming verify2FA is available in useAuth
    const [otp, setOtp] = useState('');
    const [loading2FA, setLoading2FA] = useState(false);

    const handleVerify = async (e) => {
      e.preventDefault();
      if (otp.length !== 6) {
        return push('சரியான 6 இலக்க OTP தேவை', 'error');
      }
      setLoading2FA(true);
      try {
        // Assuming form.mfaToken is set after initial login attempt
        await verify2FA(form.mfaToken, otp);
      } catch (err) {
        push('2FA சரிபார்ப்பு தோல்வி: ' + err.message, 'error');
      } finally {
        setLoading2FA(false);
      }
    };

    return (
      <>
        <div className="flex flex-col items-center mb-6">
          <h2 className="text-2xl font-semibold text-gray-900 dark:text-white">இரு காரணி அங்கீகாரம்</h2>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">உங்கள் தொலைபேசிக்கு அனுப்பப்பட்ட OTP ஐ உள்ளிடவும்.</p>
        </div>
        <form onSubmit={handleVerify} className="space-y-4">
          <div className="space-y-2 text-center">
            <label className="block text-sm text-gray-700 dark:text-gray-300">OTP<span className="text-red-400"> *</span></label>
            <OtpInput length={6} value={otp} onChange={setOtp} />
          </div>
          <motion.button
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={loading2FA || otp.length !== 6}
            className="w-full px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl text-base font-semibold hover:from-green-700 hover:to-green-800 transition-all duration-300 shadow-lg disabled:opacity-50"
          >
            {loading2FA ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'சரிபார்'}
          </motion.button>
          <div className="text-center">
            <button type="button" onClick={() => setViewMode('login')} className="text-sm text-green-600 dark:text-green-400 hover:text-green-500 dark:hover:text-green-300 hover:underline">
              உள்நுழைவுக்குத் திரும்பு
            </button>
          </div>
        </form>
      </>
    );
  };

  const ForgotPasswordPhoneEntry = () => {
    const [phone, setPhone] = useState('');
    const [loading, setLoading] = useState(false);

    const handleRequestOtp = async (e) => {
      e.preventDefault();
      if (!/^[0-9]{10}$/.test(phone)) {
        return push('சரியான 10 இலக்க தொலைபேசி எண் தேவை', 'error');
      }
      setLoading(true);
      try {
        // Mock API call. Replace with actual API call.
        // await apiFetch('/auth/request-password-reset', { method: 'POST', body: { phone } });
        await sleep(1000);
        push('OTP உங்கள் தொலைபேசிக்கு அனுப்பப்பட்டது', 'success');
        setForm(f => ({ ...f, phone })); // Pre-fill phone for the next step
        setViewMode('forgot-reset');
      } catch (err) {
        push('OTP அனுப்ப முடியவில்லை: ' + err.message, 'error');
      } finally {
        setLoading(false);
      }
    };

    return (
      <>
        <div className="flex flex-col items-center mb-6">
          <h2 className="text-2xl font-semibold">கடவுச்சொல்லை மீட்டமை</h2>
          <p className="text-sm text-gray-300 mt-1">உங்கள் தொலைபேசி எண்ணை உள்ளிடவும்</p>
        </div>
        <form onSubmit={handleRequestOtp} className="space-y-4">
          <LoginTextInput label="தொலைபேசி" name="phone" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="9999999999" />
          <button disabled={loading} className="w-full px-4 py-3 bg-green-600 text-white rounded-xl text-base font-semibold hover:bg-green-700 transition-all duration-300 shadow-lg disabled:opacity-50">
            {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'OTP அனுப்பு'}
          </button>
          <div className="text-center">
            <button type="button" onClick={() => setViewMode('login')} className="text-sm text-green-300 hover:text-green-200 hover:underline">
              உள்நுழைவுக்குத் திரும்பு
            </button>
          </div>
        </form>
      </>
    );
  };

  const ForgotPasswordReset = () => {
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const passwordStrength = getPasswordStrength(newPassword);

    const handleResetPassword = async (e) => {
      e.preventDefault();
      if (!otp || !newPassword || !confirmPassword) {
        return push('அனைத்து புலங்களையும் நிரப்பவும்', 'error');
      }
      if (otp.length !== 6) {
        return push('OTP 6 இலக்க எண்ணாக இருக்க வேண்டும்', 'error');
      }
      if (newPassword !== confirmPassword) {
        return push('புதிய கடவுச்சொற்கள் பொருந்தவில்லை', 'error');
      }
      if (passwordStrength.score < 4) {
        return push('கடவுச்சொல் போதுமான வலிமையுடன் இல்லை', 'error');
      }
      setLoading(true);
      try {
        // Mock API call. Replace with actual API call.
        // await apiFetch('/auth/reset-password-with-otp', { method: 'POST', body: { phone: form.phone, otp, newPassword } });
        await sleep(1000);
        push('கடவுச்சொல் வெற்றிகரமாக மாற்றப்பட்டது', 'success');
        setViewMode('login');
      } catch (err) {
        push('கடவுச்சொல்லை மீட்டமைக்க முடியவில்லை: ' + err.message, 'error');
      } finally {
        setLoading(false);
      }
    };

    return (
      <>
        <div className="flex flex-col items-center mb-6">
          <h2 className="text-2xl font-semibold">புதிய கடவுச்சொல்</h2>
          <p className="text-sm text-gray-300 mt-1">உங்கள் தொலைபேசிக்கு அனுப்பப்பட்ட OTP மற்றும் புதிய கடவுச்சொல்லை உள்ளிடவும்.</p>
        </div>
        <form onSubmit={handleResetPassword} className="space-y-4">
          <div className="space-y-2 text-center">
            <label className="block text-sm text-gray-200">OTP<span className="text-red-400"> *</span></label>
            <OtpInput length={6} value={otp} onChange={setOtp} />
          </div>

          <LoginTextInput label="புதிய கடவுச்சொல்" name="newPassword" required value={newPassword} onChange={e => setNewPassword(e.target.value)} type="password" />
          {newPassword && (
            <div className="mt-1">
              <div className="h-2 w-full rounded bg-gray-700 overflow-hidden mb-1">
                <div className={`h-2 rounded transition-all duration-300 ${passwordStrength.score <= 2 ? 'bg-red-500 w-1/4' : passwordStrength.score === 3 ? 'bg-yellow-400 w-2/4' : passwordStrength.score === 4 ? 'bg-blue-400 w-3/4' : 'bg-green-500 w-full'}`}></div>
              </div>
            </div>
          )}
          <LoginTextInput label="புதிய கடவுச்சொல்லை உறுதிப்படுத்தவும்" name="confirmPassword" required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} type="password" />
          <button disabled={loading} className="w-full px-4 py-3 bg-green-600 text-white rounded-xl text-base font-semibold hover:bg-green-700 transition-all duration-300 shadow-lg disabled:opacity-50">
            {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : 'கடவுச்சொல்லை மீட்டமை'}
          </button>
          <div className="text-center">
            <button type="button" onClick={() => setViewMode('login')} className="text-sm text-green-300 hover:text-green-200 hover:underline">
              உள்நுழைவுக்குத் திரும்பு
            </button>
          </div>
        </form>
      </>
    );
  };

  return (
    <motion.main
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="min-h-screen w-full font-anek bg-gray-100 dark:bg-gray-900"
    >
      {/* Background Image */}
      <div
        className="absolute inset-0 w-full h-full bg-cover bg-center opacity-30"
        style={{ backgroundImage: `url(${loginBg})` }}
      ></div>
      {/* Background Overlay */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>

      {/* Animated GIF Overlay */}
      <div className="absolute inset-0 flex items-center justify-center opacity-20">
        <img src={loginGif} alt="" className="w-full h-full object-cover" />
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen w-full p-4">
        <div className="w-full max-w-4xl">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="mb-4"
          >
            <div className="relative text-center p-3 bg-white/10 dark:bg-black/20 backdrop-blur-md rounded-2xl shadow-lg border border-white/20 dark:border-black/30">
              <AnimatePresence mode="wait">
                <motion.p
                  key={currentSloganIndex}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.4, ease: 'easeInOut' }}
                  className="font-semibold text-white text-sm sm:text-base drop-shadow-md"
                >
                  {slogans[currentSloganIndex]}
                </motion.p>
              </AnimatePresence>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            className="w-full grid grid-cols-1 md:grid-cols-2 bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl shadow-2xl border border-gray-200/50 dark:border-gray-700/50 overflow-hidden"
          >
            {/* Left side - Form */}
            <div className="p-6 sm:p-8 md:p-10">
              {viewMode === 'login' && (
                <>
                  <div className="flex flex-col items-center mb-6">
                    <img src={partyLogo} alt="TVK Logo" className="h-20 mb-4 drop-shadow-lg" />
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">உறுப்பினர் உள்நுழைவு</h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">தொடர உங்கள் விவரங்களை உள்ளிடவும்</p>
                  </div>
                  {error && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      className="mb-4 p-3 bg-red-500/10 border border-red-500/30 text-red-700 dark:text-red-300 rounded-lg text-sm text-center font-medium"
                      role="alert"
                      aria-live="assertive"
                    >
                      {error}
                    </motion.div>
                  )}
                  <form onSubmit={onSubmit} className="space-y-4">
                    <LoginTextInput label="தொலைபேசி" name="phone" required value={form.phone} onChange={set('phone')} placeholder="9999999999" autoComplete="tel" />
                    <LoginTextInput label="கடவுச்சொல்" name="password" required value={form.password} onChange={set('password')} type="password" autoComplete="current-password" />
                    <div className="flex items-center justify-between text-xs">
                      <label className="flex items-center gap-2 cursor-pointer text-gray-600 dark:text-gray-400">
                        <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} className="w-4 h-4 rounded text-green-600 bg-gray-200 dark:bg-gray-700 border-gray-300 dark:border-gray-600 focus:ring-green-500" />
                        <span className="font-medium">என்னை நினைவில் கொள்க</span>
                      </label>
                      <button type="button" onClick={() => setViewMode('forgot-phone')} className="font-medium text-green-600 hover:text-green-500 hover:underline focus:outline-none focus:ring-2 focus:ring-green-500 rounded">
                        கடவுச்சொல்லை மறந்துவிட்டேன்?
                      </button>
                    </div>

                    <motion.button
                      whileTap={{ scale: 0.98 }}
                      disabled={loading}
                      className="w-full px-4 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-xl text-base font-semibold hover:from-green-700 hover:to-green-800 transition-all duration-300 shadow-lg disabled:opacity-50"
                    >
                      {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : <span>உள்நுழை</span>}
                    </motion.button>
                    <div className="relative flex items-center my-4">
                      <div className="flex-grow border-t border-gray-300 dark:border-gray-600" role="separator"></div>
                      <span className="flex-shrink mx-4 text-gray-500 dark:text-gray-400 text-xs">அல்லது</span>
                      <div className="flex-grow border-t border-gray-300 dark:border-gray-600" role="separator"></div>
                    </div>
                    <Link to="/members/new" className="w-full block text-center px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl text-base font-semibold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg text-white">
                      எங்களுடன் இணைய
                    </Link>
                  </form>
                </>
              )}
              {viewMode === 'login-2fa' && <TwoFactorAuthView />}
              {viewMode === 'forgot-phone' && <ForgotPasswordPhoneEntry />}
              {viewMode === 'forgot-reset' && <ForgotPasswordReset />}
            </div>

            {/* Right side - Welcome Message */}
            <div className="hidden md:flex flex-col items-center justify-center p-10 bg-gradient-to-br from-green-500 to-green-700 text-white rounded-l-3xl">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.3, duration: 0.5 }}
                className="text-center"
              >
                <img src={tvktext} alt="TVK Text Logo" className="w-48 mx-auto mb-6" />
                <h2 className="text-2xl font-bold mb-2">தமிழக வாழ்வுரிமைக் கட்சி</h2>
                <p className="text-green-100">உரிமைகளை மீட்போம்! உலகை ஆள்வோம்!</p>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
      <div className="absolute bottom-4 text-center text-gray-200 opacity-75 z-10">
        <div className="font-semibold text-sm mb-0.5">தமிழக வாழ்வுரிமைக் கட்சி</div>
        <div className="text-xs">தகவல் தொழில்நுட்பப் பிரிவு</div>
        <div className="text-xs mt-1">Version 1.0.0</div>
      </div>
    </motion.main>
  );
}


/** Route Guard */
function RequireAuth({ children }) {
  const { token, user, userLoading, isUserError } = useAuth();
  const location = useLocation();

  if (userLoading) {
    return (
      <div className="w-full h-screen flex flex-col items-center justify-center bg-gray-50">
        <Loader2 className="w-8 h-8 animate-spin text-green-600" />
        <p className="mt-2 text-sm text-gray-600">Loading user...</p>
      </div>
    );
  }

  if (!token || isUserError) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return children; // The user might see a flicker of the page while user data is loading.
}

/** Lazy Pages */
const LazyHomePage = React.lazy(() => Promise.resolve({ default: HomePage }));
const LazyMembersPage = React.lazy(() => Promise.resolve({ default: MembersPage }));
const LazyMemberCreatePage = React.lazy(() => Promise.resolve({ default: MemberCreatePage }));
const LazyApprovalsPage = React.lazy(() => Promise.resolve({ default: ApprovalsPage }));
const LazyMessagesPage = React.lazy(() => Promise.resolve({ default: MessagesPage }));
const LazyIdCardPage = React.lazy(() => Promise.resolve({ default: IdCardPage }));
const LazyIdEditPage = React.lazy(() => Promise.resolve({ default: IdEditPage }));
const LazyEventsPage = React.lazy(() => Promise.resolve({ default: EventsPage }));
const LazyHelpPage = React.lazy(() => Promise.resolve({ default: HelpPage }));
const LazyAdminPage = React.lazy(() => Promise.resolve({ default: AdminPage }));
const LazySettingsPage = React.lazy(() => Promise.resolve({ default: SettingsPage }));
const LazyProfilePage = React.lazy(() => Promise.resolve({ default: ProfilePage }));
const LazyRegistrationSuccessPage = React.lazy(() => Promise.resolve({ default: RegistrationSuccessPage }));
const NewMemberRegistration = React.lazy(() => Promise.resolve({ default: MemberCreatePage }));
const LazyPrinciplesPage = React.lazy(() => Promise.resolve({ default: PrinciplesPage }));
export default function PartyUserDashboard() {
  const queryClient = new QueryClient();

  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<FullPageLoader />}>
        <ThemeProvider>
          <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <ToastProvider>
              <AuthProvider>
                <Routes>
                  {/* Redirect root to registration for join subdomain */}
                  <Route path="/" element={<Navigate to="/members/new" replace />} />
                  <Route path="/login" element={<LoginPage />} />
                  <Route path="/members/new" element={<NewMemberRegistration />} />
                  <Route path="/registration-success" element={<LazyRegistrationSuccessPage />} />
                  {/* ID Card page is public so new users can download their card */}
                  <Route path="/idcard" element={<LazyIdCardPage />} />
                  <Route path="/*" element={<RequireAuth><AppRoutes /></RequireAuth>} />
                </Routes>
              </AuthProvider>
            </ToastProvider>
          </BrowserRouter>
        </ThemeProvider>
      </Suspense>
    </QueryClientProvider>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<LazyHomePage />} />
      <Route path="/members" element={<LazyMembersPage />} />
      <Route path="/approvals" element={<LazyApprovalsPage />} />
      <Route path="/messages" element={<LazyMessagesPage />} />
      <Route path="/idcard" element={<LazyIdCardPage />} />
      <Route path="/idcard/edit" element={<LazyIdEditPage />} />
      <Route path="/events" element={<LazyEventsPage />} />
      <Route path="/help" element={<LazyHelpPage />} />
      <Route path="/admin" element={<LazyAdminPage />} />
      <Route path="/settings" element={<LazySettingsPage />} />
      <Route path="/profile" element={<LazyProfilePage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/principles" element={<LazyPrinciplesPage />} />
      {/* ... other protected routes ... */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
